import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Grid from "@mui/material/Grid"
import styles from "../../asset/styles/quotation/journeyDetails.module.scss"
import Select from "../select/select"
import InputText from "../inputText/inputText"
import Button from "../button/button"
import IconsColor from "../../util/iconsColor"
import Checkbox from "../checkbox/checkbox"
import { agent } from "../../apiClient/api"
import { useSnackbar } from "../../context/snackbarContext"
import { buildCityDateKey, useQuotation, JourneyOptionsByDateValue } from "../../context/quotationContext"
import {
  AdditionalCostItem,
  CanoRateRow,
  ExcursionOptionItem,
  JourneyDayHotelItem,
  JourneyDayItem,
  JourneyDayMealItem,
  JourneyDayTripExcursionItem,
  JourneyDayTripExcursionTicketItem,
  JourneyDayTripItem,
  QuotationHotelOptionsByDateHotel,
  QuotationHotelOptionsByDateRoomType,
  QuotationHotelOptionsPricingOption,
  QuotationJourneyDetailsPutBody,
  QuotationMealCityPricingGenericItem,
  SeatingPriceOptionItem,
  TripOptionItem,
} from "../../models/axiosModel"
import Dialog from "@mui/material/Dialog"
// import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import IconButton from "@mui/material/IconButton"
import { MdOutlineInfo, MdOutlineTipsAndUpdates } from "react-icons/md"
import { CityNightRow } from "./QuotationBasicDetails"
import HotelRoomSelectorV2 from "./HotelRoomSelectorV2"
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { toTitleCase } from "util/textcase"
import { IGuideGroupPricing } from "models"
import { resolveCanoRatesTable } from "./canoPrivateRatesUtil"
import {
  applyTripIncludedMeals,
  asCruiseLikeTripFields,
  resolveManualMealTogglePrice,
  snapshotTripMealIncludedFields,
  syncMealsAfterTripRemoved,
  tripHasDinnerIncluded,
  trustHydratedMealPrices,
} from "./journeyMealIncludedUtil"
import {
  applyTripsSeatingSnapshotFromApi,
  buildJourneyOptionsDedupeKey,
  buildPersistedSeatingSnapshotFields,
  dayTripsMissingSeatingPriceSnapshot,
  resolveDinnerTransportFromTripSnapshot,
  shouldFetchJourneyOptionsForKey,
  tripKindFromDayTripType,
} from "./journeyDetailsOptionsPerformanceUtil"
import {
  buildSelectedTripFallbackOptions,
  buildTripSelectValue,
  ensureSelectedTripInSelectOptions,
  isPresentSourceCityId,
  mergeJourneyOptionsWithSelected,
} from "./journeyDetailsTripSelectUtil"
import {
  applyHotelDisplaySnapshotFromApi,
  buildHotelOptionsDedupeKey,
  findMatchingHotelOption,
  getIncludedMealBadgesFromHotel,
  HotelMealPlanCode,
  resolveHotelMealPlanCode,
  shouldFetchHotelOptionsForKey,
} from "./journeyDetailsHotelOptionsPerformanceUtil"
import {
  excursionSupportsAddOns,
  getAvailableExcursionAddOns,
  resolveExcursionAddOnCatalog,
} from "./journeyDetailsExcursionAddOnUtil"
import {
  applyTripExcursionPaxMode,
  applyPaxToTicketedRow,
  defaultExcursionPaxCounts,
  excursionPaxTargetForTrip,
} from "./journeyDetailsSicExcursionUtil"
import {getQuotationStatusIdFromQuotationStatusName, QuotationStatusVisualVariant} from "./quotationStatusVariant";

export type JourneyDetailsSaveIntent = QuotationStatusVisualVariant

interface JourneyDetailsProps {
  onBindSaveHandler?: (handler: (intent?: JourneyDetailsSaveIntent) => Promise<boolean>) => void
}

const HOTEL_TAX_RATE = 1.02
// Business rule for the per-person surcharge: 1 room is counted as 2 adults. Extra beds
// each add 1 adult on top. Children come from Step-1 basic details (with or without bed).
const HOTEL_ADULTS_PER_ROOM = 2

const MEAL_TYPES = [
  { id: 1, name: "Breakfast", short: "B" },
  { id: 2, name: "Lunch", short: "L" },
  { id: 3, name: "Dinner", short: "D" },
] as const

const GUIDE_OPTIONS = [
  { label: "No", value: "no" },
  { label: "Yes", value: "yes" },
]

type MealPlanIncludedType = {
  meal_plan_type_included_meal_id?: number
  meal_plan_type_id?: number
  meal_plan_type_included_meal_name?: string
}

const formatVnd = (n: number): string => `VND ${Math.round(n).toLocaleString("en-US")}`

/** Default meal price for a journey day city from generic `quotation_meal_city_pricing`. */
const resolveMealCityDefaultPrice = (
  rows: QuotationMealCityPricingGenericItem[],
  cityId: number | string | undefined | null,
  mealTypeId: number,
): number => {
  if (cityId === undefined || cityId === null || cityId === "") return 0
  const cid = Number(cityId)
  if (!Number.isFinite(cid)) return 0
  const row = rows.find(
    (r) => Number(r.city_id) === cid && Number(r.quotation_meal_type_id) === Number(mealTypeId),
  )
  if (!row) return 0
  const raw = row.price
  const n = typeof raw === "string" ? parseFloat(raw) : Number(raw)
  return Number.isFinite(n) ? Math.round(n) : 0
}

const getIncludedMealBadges = (hotel: JourneyDayHotelItem): string[] =>
  getIncludedMealBadgesFromHotel(hotel)

interface PackageServiceChargeTier {
  quotation_package_service_charge_tier_id: number
  nights_min: number
  nights_max: number | null
  pax_min: number
  pax_max: number | null
  price_per_pax: number
}

interface DayPriceBreakdown {
  transport: number
  excursion: number
  meal: number
  guide: number
  hotelRoomVnd: number
  hotelExtraBedVnd: number
  hotelChildWithBedVnd: number
  hotelChildWithoutBedVnd: number
  // Per-person surcharge: adults = rooms × 2 + extra beds, children from basic details.
  hotelSurchargeAdultCount: number
  hotelSurchargeChildCount: number
  hotelAdultSurchargeVnd: number
  hotelChildSurchargeVnd: number
  hotelPerPersonSurchargeVnd: number
  hotelVnd: number
  totalVnd: number
}

const isSicTrip = (trip: JourneyDayTripItem): boolean => {
  const byValue = String((trip as any)._seating_value || "").toUpperCase() === "SIC"
  const byName = String(trip.seating_type_name || "").toUpperCase() === "SIC"
  return byValue || byName
}

/** Named "SIC" row from trip seating_pricing (carries the real seating_type_id for API persistence). */
const findSicSeatingRow = (seatingPricing: SeatingPriceOptionItem[] | undefined) =>
  (seatingPricing || []).find(
    (s) => String(s.seating_type_name || "").trim().toUpperCase() === "SIC",
  )

const isSicSeatingSelection = (seatingTypeId: string, sicRow?: SeatingPriceOptionItem) =>
  seatingTypeId === "SIC" ||
  (sicRow != null && String(sicRow.seating_type_id ?? "") === seatingTypeId)

/** Whether the trip's selected seating is priced per person (SIC-style). Prefers the snapshot
 *  `flag_per_person` from the seating definition; falls back to the name-based SIC check. */
const isTripPerPerson = (trip: JourneyDayTripItem): boolean => {
  const snap = (trip as any)._seating_is_per_person
  if (typeof snap === "boolean") return snap
  if (typeof trip.flag_per_person === "boolean") return trip.flag_per_person
  return isSicTrip(trip)
}

/** Dinner transport applies when DT is selected, the trip supports dinner transfer, and
 *  dinner is not already included on that trip (`flag_dinner_included`). */
const tripDinnerApplies = (trip: JourneyDayTripItem, dinnerTransferSelected: boolean): boolean =>
  dinnerTransferSelected &&
  Boolean((trip as any)._flag_enable_dinner) &&
  !tripHasDinnerIncluded(asCruiseLikeTripFields(trip))

type SeatingTypeByPaxRow = {
  min_pax: number
  max_pax: number
  seating_type_id: number
  seating_type_name: string
}

/** Fallback when generic-data has not loaded `seating_type_by_pax` yet. */
const DEFAULT_SEATING_TYPE_BY_PAX: SeatingTypeByPaxRow[] = [
  { min_pax: 1, max_pax: 2, seating_type_id: 1, seating_type_name: "4 Seater" },
  { min_pax: 3, max_pax: 3, seating_type_id: 2, seating_type_name: "7 Seater" },
  { min_pax: 4, max_pax: 8, seating_type_id: 3, seating_type_name: "16 Seater" },
  { min_pax: 9, max_pax: 15, seating_type_id: 4, seating_type_name: "29 Seater" },
  { min_pax: 16, max_pax: 22, seating_type_id: 5, seating_type_name: "35 Seater" },
  { min_pax: 23, max_pax: 40, seating_type_id: 6, seating_type_name: "45 Seater" },
]

const SIC_SEATING_TYPE_ID = 7
const SIC_PAX_THRESHOLD = 40

const normalizeSeatingTypeByPax = (rows: SeatingTypeByPaxRow[] | undefined): SeatingTypeByPaxRow[] =>
  Array.isArray(rows) && rows.length > 0 ? rows : DEFAULT_SEATING_TYPE_BY_PAX

type ResolvedDinnerSeating = { seating_type_id: number; seating_type_name: string }

/** Map total pax (basic details) to the default seating type (DT fallback when trip seater has no DT price). */
const resolveSeatingTypeByPax = (
  paxCount: number,
  seatingTypeByPax: SeatingTypeByPaxRow[],
): ResolvedDinnerSeating | null => {
  const pax = Math.max(0, Number(paxCount) || 0)
  if (pax <= 0) return null
  if (pax > SIC_PAX_THRESHOLD) {
    return { seating_type_id: SIC_SEATING_TYPE_ID, seating_type_name: "SIC" }
  }
  const rows = normalizeSeatingTypeByPax(seatingTypeByPax)
  const match = rows.find((r) => pax >= Number(r.min_pax) && pax <= Number(r.max_pax))
  return match
    ? { seating_type_id: Number(match.seating_type_id), seating_type_name: match.seating_type_name }
    : null
}

/** Prefer the trip's selected column-2 seater for DT (including SIC). */
const resolveSeatingFromTrip = (
  trip: JourneyDayTripItem,
  tripDef?: TripOptionItem,
): ResolvedDinnerSeating | null => {
  const list = tripDef?.seating_pricing || []
  const sicRow = findSicSeatingRow(list)
  const isSic =
    isSicTrip(trip) ||
    Number(trip.seating_type_id) === SIC_SEATING_TYPE_ID ||
    (sicRow != null && Number(trip.seating_type_id) === Number(sicRow.seating_type_id))

  if (isSic) {
    return {
      seating_type_id: Number(sicRow?.seating_type_id ?? trip.seating_type_id) || SIC_SEATING_TYPE_ID,
      seating_type_name: String(sicRow?.seating_type_name || trip.seating_type_name || "SIC"),
    }
  }

  const id = Number(trip.seating_type_id)
  if (!id) return null
  const fromDef = list.find((s) => Number(s.seating_type_id) === id)
  const name =
    String(trip.seating_type_name || "").trim() ||
    String(fromDef?.seating_type_name || "").trim() ||
    `Seater ${id}`
  return { seating_type_id: id, seating_type_name: name }
}

const findDinnerSeatingRow = (
  seatingPricing: SeatingPriceOptionItem[] | undefined,
  resolved: ResolvedDinnerSeating,
): SeatingPriceOptionItem | undefined => {
  const list = seatingPricing || []
  const isSic =
    Number(resolved.seating_type_id) === SIC_SEATING_TYPE_ID ||
    String(resolved.seating_type_name || "").trim().toUpperCase() === "SIC"
  if (isSic) {
    return findSicSeatingRow(list) ?? list.find((s) => Number(s.seating_type_id) === SIC_SEATING_TYPE_ID)
  }
  return list.find((s) => Number(s.seating_type_id) === Number(resolved.seating_type_id))
}

/** True when the seating row exposes a dinner_transport_price (0 is valid; missing is not). */
const hasDinnerTransportPrice = (row: SeatingPriceOptionItem | undefined): boolean => {
  if (!row) return false
  const raw = (row as any).dinner_transport_price
  return raw !== null && raw !== undefined && raw !== ""
}

type TripDinnerTransportInfo = {
  unitPrice: number
  totalPrice: number
  seatingTypeName: string
  perPerson: boolean
}

/** Dinner transport follows the trip's selected seater (incl. SIC). Falls back to pax-default
 *  seating when options are loaded but the trip seater has no dinner_transport_price.
 *  When options are not loaded, uses journey-details `dinner_transport_price` snapshot. */
const resolveTripDinnerTransport = (
  trip: JourneyDayTripItem,
  paxCount: number,
  dinnerTransferSelected: boolean,
  seatingTypeByPax: SeatingTypeByPaxRow[],
  tripDef?: TripOptionItem,
): TripDinnerTransportInfo => {
  const empty: TripDinnerTransportInfo = { unitPrice: 0, totalPrice: 0, seatingTypeName: "", perPerson: false }
  if (!tripDinnerApplies(trip, dinnerTransferSelected)) return empty

  const tripResolved = resolveSeatingFromTrip(trip, tripDef)
  let resolved: ResolvedDinnerSeating | null = tripResolved
  let seatingRow = resolved ? findDinnerSeatingRow(tripDef?.seating_pricing, resolved) : undefined

  if (tripDef) {
    if (!resolved || !hasDinnerTransportPrice(seatingRow)) {
      resolved = resolveSeatingTypeByPax(paxCount, seatingTypeByPax)
      seatingRow = resolved ? findDinnerSeatingRow(tripDef?.seating_pricing, resolved) : undefined
    }

    if (resolved && hasDinnerTransportPrice(seatingRow)) {
      const unitPrice = Number((seatingRow as any)?.dinner_transport_price ?? 0)
      const perPerson = Boolean((seatingRow as any)?.flag_per_person)
      const pax = Math.max(0, Number(paxCount) || 0)
      const totalPrice = perPerson ? unitPrice * pax : unitPrice
      return {
        unitPrice,
        totalPrice,
        seatingTypeName: resolved.seating_type_name,
        perPerson,
      }
    }
  }

  return resolveDinnerTransportFromTripSnapshot(trip, paxCount)
}

const findTripDefForDay = (
  day: JourneyDayItem,
  trip: JourneyDayTripItem,
  journeyOptionsByCityDate: Record<string, JourneyOptionsByDateValue>,
): TripOptionItem | undefined => {
  const key = buildCityDateKey(day.city_id, day.date)
  const opts = journeyOptionsByCityDate[key]
  const allDefs = [...(opts?.tours || []), ...(opts?.transfers || [])]
  return allDefs.find((t) => t.day_trip_id === trip.day_trip_id)
}

const lastTripOfDay = (day: JourneyDayItem): JourneyDayTripItem | undefined => {
  const trips = day.trips || []
  return trips.length > 0 ? trips[trips.length - 1] : undefined
}

const lastTripHasDinnerIncluded = (
  day: JourneyDayItem,
  journeyOptionsByCityDate: Record<string, JourneyOptionsByDateValue>,
): boolean => {
  const last = lastTripOfDay(day)
  if (!last) return false
  if (last.flag_dinner_included === true) return true
  if (last.flag_dinner_included === false) return false
  return tripHasDinnerIncluded(findTripDefForDay(day, last, journeyOptionsByCityDate) as any)
}

/** DT can be selected only when there is a last trip and that trip does not include dinner. */
const canSelectDinnerTransfer = (
  day: JourneyDayItem,
  journeyOptionsByCityDate: Record<string, JourneyOptionsByDateValue>,
): boolean => Boolean(lastTripOfDay(day)) && !lastTripHasDinnerIncluded(day, journeyOptionsByCityDate)

const lastTripSupportsDinnerTransfer = (trip: JourneyDayTripItem | undefined): boolean =>
  Boolean(trip && (trip as any)._flag_enable_dinner)

const isDinnerMealSelected = (day: JourneyDayItem): boolean =>
  (day.meals || []).some((m) => m.quotation_meal_type_id === 3)

/** While Dinner is on, DT cannot be turned off. */
const isDinnerTransferLockedOn = (day: JourneyDayItem): boolean => isDinnerMealSelected(day)

/** Trip-list changes never auto-select DT. Only force off when DT cannot apply. */
const syncFlagDinnerTransferForDay = (
  day: JourneyDayItem,
  journeyOptionsByCityDate: Record<string, JourneyOptionsByDateValue>,
): JourneyDayItem => {
  if (!lastTripOfDay(day) || lastTripHasDinnerIncluded(day, journeyOptionsByCityDate)) {
    return { ...day, flag_dinner_transfer: false }
  }
  return day
}

/** Turning Dinner on selects DT only when last trip `flag_dinner_included` is false. */
const applyDtWhenDinnerTurnsOn = (
  day: JourneyDayItem,
  journeyOptionsByCityDate: Record<string, JourneyOptionsByDateValue>,
): JourneyDayItem => {
  if (!canSelectDinnerTransfer(day, journeyOptionsByCityDate)) {
    return { ...day, flag_dinner_transfer: false }
  }
  return { ...day, flag_dinner_transfer: true }
}

/** Hydrate keeps a saved DT-off. Only force off when DT cannot apply. */
const applyDinnerTransferGateOnHydrate = (
  day: JourneyDayItem,
  journeyOptionsByCityDate: Record<string, JourneyOptionsByDateValue>,
): JourneyDayItem => {
  if (!lastTripOfDay(day) || lastTripHasDinnerIncluded(day, journeyOptionsByCityDate)) {
    return { ...day, flag_dinner_transfer: false }
  }
  return { ...day, flag_dinner_transfer: Boolean(day.flag_dinner_transfer) }
}

const getTripNormalTransport = (trip: JourneyDayTripItem, paxCount: number): number => {
  const base = Number((trip as any)._seating_price ?? trip.seating_price ?? 0)
  return isTripPerPerson(trip) ? base * Math.max(0, Number(paxCount) || 0) : base
}

const getTripDinnerTransport = (
  trip: JourneyDayTripItem,
  paxCount: number,
  dinnerTransferSelected: boolean,
  seatingTypeByPax: SeatingTypeByPaxRow[],
  tripDef?: TripOptionItem,
): number =>
  resolveTripDinnerTransport(trip, paxCount, dinnerTransferSelected, seatingTypeByPax, tripDef).totalPrice

/** Total trip transport = normal seating transport + (additive) dinner transport when it applies. */
const getTripTransportPrice = (
  trip: JourneyDayTripItem,
  paxCount: number,
  dinnerTransferSelected: boolean,
  seatingTypeByPax: SeatingTypeByPaxRow[],
  tripDef?: TripOptionItem,
): number =>
  getTripNormalTransport(trip, paxCount) +
  getTripDinnerTransport(trip, paxCount, dinnerTransferSelected, seatingTypeByPax, tripDef)

/** Lantern-boat detection used by both the Boat-Count input and the day-cost breakdown.
 *  Excursion is a lantern boat iff the name matches AND boat_pricing.flag_speed_boat === false.
 *  Reads from excursion-level snapshot fields (`_is_lantern_boat` etc.) populated at add /
 *  hydrate time from `excDef.boat_pricing` — same pattern as `_adult_price`. */
const isLanternBoatExcursion = (e: any): boolean => Boolean(e?._is_lantern_boat)

/** Speed boat (cano): priced by a rate table looked up on total pax (adults + children, no age
 *  rule). Driven purely by boat_pricing.flag_speed_boat === true. */
const isSpeedBoatExcursion = (e: any): boolean => Boolean(e?._is_speed_boat)

/** Snapshot boat-pricing fields from an excursion definition onto an excursion item.
 *  Mirrors the existing `_adult_price` / `_child_price` snapshot pattern so price + boat
 *  config are locked to what was shown when the user picked the excursion — they don't
 *  silently change if the backend definition is edited later. Returns an empty object for
 *  non-boat (or unknown) excursions so spreading is a no-op.
 *   - flag_speed_boat === true  → speed boat (cano): snapshot the journey-options
 *     cano_rates_table (empty for saved quotations in edit mode). The generic-data table and the
 *     static "Cano Private" fallback are resolved at price time (see computeSpeedBoatTotal).
 *   - flag_speed_boat === false + name "lantern boat" → lantern boat: snapshot boat/pax fields. */
const buildBoatPricingSnapshot = (
  excDef: Pick<ExcursionOptionItem, "excursion_name" | "boat_pricing"> | undefined,
): Record<string, any> => {
  const bp = excDef?.boat_pricing
  if (!bp) return {}
  if (bp.flag_speed_boat === true) {
    return {
      _is_speed_boat: true,
      _cano_rates_table: Array.isArray(bp.cano_rates_table) ? bp.cano_rates_table : [],
    }
  }
  const isLantern =
    String(excDef?.excursion_name || "").trim().toLowerCase().includes("lantern boat")
    && bp.flag_speed_boat === false
  if (!isLantern) return {}
  return {
    _is_lantern_boat: true,
    _boat_price: Number(bp.boat_price || 0),
    _pax_per_boat: Number(bp.pax_per_boat || 0),
    _child_max_age: Number(bp.child_max_age || 0),
  }
}

/** Speed-boat (cano) total: total pax = adults + children (no age rule). Look up the matching
 *  `pax` row in the cano_rates_table → its `total_rate`. If pax exceeds the table, clamp to the
 *  largest row. Table priority: generic-data → excursion's journey-options table → static Cano
 *  Private (see resolveCanoRatesTable). Returns 0 when no table / no pax. */
const computeSpeedBoatTotal = (e: any, genericCanoRates: CanoRateRow[]): number => {
  const table: any[] = resolveCanoRatesTable(genericCanoRates, e?._cano_rates_table, e?.excursion_name)
  if (table.length === 0) return 0
  const totalPax = Number(e?._adult_count || 0) + Number(e?._child_count || 0)
  if (totalPax <= 0) return 0
  const exact = table.find((r) => Number(r?.pax) === totalPax)
  if (exact) return Number(exact.total_rate || 0)
  // Beyond the table's range → clamp to the highest available pax row.
  const maxRow = table.reduce((m, r) => (Number(r?.pax) > Number(m?.pax) ? r : m), table[0])
  return totalPax > Number(maxRow?.pax || 0) ? Number(maxRow?.total_rate || 0) : 0
}

/** Boat count = ceil((adults + qualifying-children) / pax_per_boat).
 *  Qualifying children = childDetails entries from Step 1 whose age > child_max_age.
 *  All boat fields come from the snapshot on the excursion item (`_pax_per_boat`,
 *  `_child_max_age`). Returns 0 when boat capacity is missing/invalid. */
const computeLanternBoatCount = (e: any, childAges: number[]): number => {
  const adultsCount = Number((e as any)._adult_count || 0)
  const paxPerBoat = Number((e as any)._pax_per_boat || 0)
  if (paxPerBoat <= 0) return 0
  const childMaxAge = Number((e as any)._child_max_age || 0)
  const qualifyingChildren = (childAges || []).reduce(
    (count, age) => (Number(age) > childMaxAge ? count + 1 : count),
    0,
  )
  return Math.ceil((adultsCount + qualifyingChildren) / paxPerBoat)
}

/** Minimal child-policy row shape from journey-options / journey-details. */
type JourneyChildPolicyRow = {
  category_id?: number
  category_name?: string
  flag_free_of_charge?: boolean
  max_age?: number | null
}

type ExcursionChildPriceSegment = {
  count: number
  unitPrice: number
}

type ExcursionChildPolicyPricing = {
  adultCount: number
  adultPrice: number
  childrenSegments: ExcursionChildPriceSegment[]
  total: number
}

const findChildPolicyRow = (
  policy: JourneyChildPolicyRow[] | undefined,
  name: string,
): JourneyChildPolicyRow | undefined =>
  (policy || []).find((p) => String(p.category_name || "").trim().toLowerCase() === name)

/** Flat adults + children × child price (manual override / missing policy / age mismatch). */
const buildFlatExcursionChildPricing = (
  adultCount: number,
  adultPrice: number,
  childCount: number,
  childPrice: number,
): ExcursionChildPolicyPricing => {
  const childrenSegments: ExcursionChildPriceSegment[] =
    childCount > 0 ? [{ count: childCount, unitPrice: childPrice }] : []
  return {
    adultCount,
    adultPrice,
    childrenSegments,
    total: adultCount * adultPrice + childCount * childPrice,
  }
}

/**
 * Normal-excursion pricing from child_policy + Step-1 ages.
 * - Free: age ≤ infant max and infant flag_free_of_charge
 * - Paid child: else age ≤ child max → child price
 * - Older child: else age > child max → still a child, adult price (does not bump adult count)
 * Manual / missing policy / age-count mismatch → flat children × child price.
 * Add-ons and boats do not use this helper.
 */
const computeExcursionChildPolicyPricing = (
  e: any,
  childAges: number[],
): ExcursionChildPolicyPricing => {
  const adultCount = Math.max(0, Number(e?._adult_count || 0))
  const adultPrice = Number(e?._adult_price || 0)
  const childCount = Math.max(0, Number(e?._child_count || 0))
  const childPrice = Number(e?._child_price || 0)
  const countsManual = Boolean(e?._counts_manual)
  const policy = Array.isArray(e?.child_policy) ? (e.child_policy as JourneyChildPolicyRow[]) : []

  if (countsManual || policy.length === 0) {
    return buildFlatExcursionChildPricing(adultCount, adultPrice, childCount, childPrice)
  }

  const ages = Array.isArray(childAges) ? childAges : []
  if (ages.length !== childCount) {
    return buildFlatExcursionChildPricing(adultCount, adultPrice, childCount, childPrice)
  }

  const infant = findChildPolicyRow(policy, "infant")
  const child = findChildPolicyRow(policy, "child")
  const infantMax = infant != null ? Number(infant.max_age) : NaN
  const childMax = child != null ? Number(child.max_age) : NaN
  const infantFree = Boolean(infant?.flag_free_of_charge)

  if (!Number.isFinite(childMax) && !Number.isFinite(infantMax)) {
    return buildFlatExcursionChildPricing(adultCount, adultPrice, childCount, childPrice)
  }

  let freeCount = 0
  let paidChildCount = 0
  let adultPricedChildCount = 0

  for (const rawAge of ages) {
    const age = Number(rawAge) || 0
    if (Number.isFinite(infantMax) && age <= infantMax && infantFree) {
      freeCount += 1
      continue
    }
    if (Number.isFinite(childMax) && age <= childMax) {
      paidChildCount += 1
      continue
    }
    if (Number.isFinite(childMax) && age > childMax) {
      adultPricedChildCount += 1
      continue
    }
    // No usable child max (or age between bands without a child row) → charge child price.
    paidChildCount += 1
  }

  const childrenSegments: ExcursionChildPriceSegment[] = []
  if (freeCount > 0) childrenSegments.push({ count: freeCount, unitPrice: 0 })
  if (paidChildCount > 0) childrenSegments.push({ count: paidChildCount, unitPrice: childPrice })
  if (adultPricedChildCount > 0) childrenSegments.push({ count: adultPricedChildCount, unitPrice: adultPrice })

  const childrenTotal = childrenSegments.reduce((sum, s) => sum + s.count * s.unitPrice, 0)
  return {
    adultCount,
    adultPrice,
    childrenSegments,
    total: adultCount * adultPrice + childrenTotal,
  }
}

/** Popover formula body (no excursion name): `2 adults * VND 200 + 1 children * VND 0 + … = VND 740` */
const formatExcursionChildPolicyFormula = (pricing: ExcursionChildPolicyPricing): string => {
  const parts: string[] = [`${pricing.adultCount} adults * ${formatVnd(pricing.adultPrice)}`]
  for (const seg of pricing.childrenSegments) {
    parts.push(`${seg.count} children * ${formatVnd(seg.unitPrice)}`)
  }
  return `${parts.join(" + ")} = ${formatVnd(pricing.total)}`
}

/**
 * Add-on pricing: same age split as the parent excursion, using the add-on's own unit prices
 * and the parent excursion's `child_policy`. Manual flag is per add-on row.
 */
const computeAddOnChildPolicyPricing = (
  ao: any,
  parentExcursion: any,
  childAges: number[],
): ExcursionChildPolicyPricing =>
  computeExcursionChildPolicyPricing(
    {
      ...ao,
      child_policy: Array.isArray(parentExcursion?.child_policy) ? parentExcursion.child_policy : [],
    },
    childAges,
  )

/** Snapshot child_policy from an option/detail definition onto the journey excursion row. */
const buildChildPolicySnapshot = (
  excDef: Pick<ExcursionOptionItem, "child_policy"> | Record<string, any> | undefined,
): { child_policy: JourneyChildPolicyRow[]; _counts_manual: boolean } => ({
  child_policy: Array.isArray((excDef as any)?.child_policy)
    ? ((excDef as any).child_policy as JourneyChildPolicyRow[])
    : [],
  _counts_manual: false,
})

/** Group guide rate (e.g. 1500) applies only when With Tip is selected and pax is above
 *  `guide_group_pricing.min_pax` (API sends 8 → applies at 9+). Tip is not added on top. */
const appliesGuideGroupPricing = (
  guideGroupPricing: IGuideGroupPricing | null,
  paxCount: number,
  flagWithTip: boolean,
): guideGroupPricing is IGuideGroupPricing =>
  Boolean(guideGroupPricing && flagWithTip && paxCount > Number(guideGroupPricing.min_pax))

/** Per-day pricing per Step-2 spec.
 *  All non-hotel costs sum in VND.
 *  Hotel cost is kept in VND (`rate × rooms × tax`).
 *  Checkout (last) day never includes hotel — stay is billed on night days only.
 *  Total displayed in VND.
 *
 *  Guide rule: when `flag_guide` is true, charge `guide_base_price` (fallback `guide_charge`);
 *  if `flagWithTip` is true, add `guide_tip_amount` on top — unless group pricing applies. */
const computeDayPriceBreakdown = (
  day: JourneyDayItem,
  paxCount: number,
  flagWithTip: boolean,
  totalChildrenCount: number,
  childAges: number[],
  seatingTypeByPax: SeatingTypeByPaxRow[],
  journeyOptionsByCityDate: Record<string, JourneyOptionsByDateValue>,
  guideGroupPricing: IGuideGroupPricing | null,
  genericCanoRates: CanoRateRow[],
  isCheckoutDay = false,
): DayPriceBreakdown => {
  const trips = day.trips || []
  const dinnerTransferSelected = Boolean(day.flag_dinner_transfer)
  const lastTrip = lastTripOfDay(day)
  const transport = trips.reduce(
    (sum, t) =>
      sum +
      getTripTransportPrice(
        t,
        paxCount,
        t === lastTrip ? dinnerTransferSelected : false,
        seatingTypeByPax,
        findTripDefForDay(day, t, journeyOptionsByCityDate),
      ),
    0,
  )
  const excursion = trips.reduce((tripSum, t) => {
    const inner = (t.excursions || []).reduce((excSum, e) => {
      // Lantern boats price by boat count, not by pax category. Boat count is recomputed
      // from current basic-details child ages (see computeLanternBoatCount).
      let excTotal: number
      if (isLanternBoatExcursion(e)) {
        const boatCount = computeLanternBoatCount(e, childAges)
        const boatPrice = Number((e as any)._boat_price || 0)
        excTotal = boatCount * boatPrice
      } else if (isSpeedBoatExcursion(e)) {
        // Speed boat (cano): flat total looked up from the rate table by total pax.
        excTotal = computeSpeedBoatTotal(e, genericCanoRates)
      } else {
        // Normal excursion: apply child_policy age split (or flat when manual / fallback).
        excTotal = computeExcursionChildPolicyPricing(e, childAges).total
      }
      const addOnsTotal = ((e as any).add_ons || []).reduce((aSum: number, ao: any) => {
        // Add-ons use parent excursion child_policy + add-on unit prices (or flat when manual).
        return aSum + computeAddOnChildPolicyPricing(ao, e, childAges).total
      }, 0)
      return excSum + excTotal + addOnsTotal
    }, 0)
    return tripSum + inner
  }, 0)
  // Meals: per-pax VND × pax across selected meal types.
  const meal = (day.meals || []).reduce((sum, m) => sum + Number(m.price || 0), 0) * paxCount
  let guideBaseRate = 0;
  let flagGuideGroupPricing = false;
  if (appliesGuideGroupPricing(guideGroupPricing, paxCount, flagWithTip)) {
    guideBaseRate = Number(guideGroupPricing.amount);
    flagGuideGroupPricing = true;
  }
  else
    guideBaseRate = Number(day.guide_base_price ?? day.guide_charge ?? 0)
  const guideTipRate = flagWithTip ? Number(day.guide_tip_amount ?? 0) : 0
  const guide = day.flag_guide ? flagWithTip && flagGuideGroupPricing === false && guideTipRate > 0 ? (guideBaseRate + (guideTipRate * paxCount)) : guideBaseRate : 0
  const hotel = isCheckoutDay ? null : day.hotel
  const hotelRoomVnd = hotel
    ? Number(hotel.room_price || 0) * Number(hotel.rooms || 0) * HOTEL_TAX_RATE
    : 0
  const hotelExtraBedVnd = hotel
    ? Number(hotel.extra_bed_price || 0) * Number(hotel.extra_beds || 0) * HOTEL_TAX_RATE
    : 0
  const hotelChildWithBedVnd = hotel
    ? Number(hotel.child_with_bed_price || 0) * Number(hotel.child_with_bed || 0) * HOTEL_TAX_RATE
    : 0
  const childWithoutBedCount = hotel ? Math.max(0, Number(totalChildrenCount || 0) - Number(hotel.child_with_bed || 0)) : 0
  const hotelChildWithoutBedVnd = hotel
    ? Number(hotel.child_without_bed_price || 0) * childWithoutBedCount * HOTEL_TAX_RATE
    : 0
  // Per-person surcharge. Adults = rooms × 2 + extra beds (per this hotel row); children =
  // total children from Step-1 basic details (with/without bed doesn't matter). Applied per
  // person WITHOUT tax (confirmed by business — unlike the room/bed lines).
  const hotelSurchargeAdultCount = hotel
    ? Number(hotel.rooms || 0) * HOTEL_ADULTS_PER_ROOM + Number(hotel.extra_beds || 0)
    : 0
  const hotelSurchargeChildCount = hotel ? Math.max(0, Number(totalChildrenCount || 0)) : 0
  const hotelAdultSurchargeVnd = hotel
    ? hotelSurchargeAdultCount * Number(hotel.per_person_adult_surcharge || 0)
    : 0
  const hotelChildSurchargeVnd = hotel
    ? hotelSurchargeChildCount * Number(hotel.per_person_child_surcharge || 0)
    : 0
  const hotelPerPersonSurchargeVnd = hotelAdultSurchargeVnd + hotelChildSurchargeVnd
  const hotelVnd = hotelRoomVnd + hotelExtraBedVnd + hotelChildWithBedVnd + hotelChildWithoutBedVnd + hotelPerPersonSurchargeVnd
  const totalVnd = transport + excursion + meal + guide + hotelVnd
  return { transport, excursion, meal, guide, hotelRoomVnd, hotelExtraBedVnd, hotelChildWithBedVnd, hotelChildWithoutBedVnd, hotelSurchargeAdultCount, hotelSurchargeChildCount, hotelAdultSurchargeVnd, hotelChildSurchargeVnd, hotelPerPersonSurchargeVnd, hotelVnd, totalVnd }
}

const InlineSpinner: React.FC<{ size?: number; color?: string }> = ({ size = 12, color = "#3b82f6" }) => (
  <span
    aria-label="Loading"
    style={{
      display: "inline-block",
      width: size,
      height: size,
      border: `2px solid #cbd5e1`,
      borderTopColor: color,
      borderRadius: "50%",
      animation: "_jdSpin 0.7s linear infinite",
      verticalAlign: "middle",
    }}
  />
)

/** One-time keyframes injection for InlineSpinner — avoids editing global SCSS. */
const SpinnerKeyframes: React.FC = () => (
  <style>{`@keyframes _jdSpin { to { transform: rotate(360deg) } }`}</style>
)

export const normalizeIsoDate = (raw: string | undefined | null): string => {
  if (!raw) return ""
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return ""
  return parsed.toISOString().slice(0, 10)
}

const formatDateForDisplay = (iso: string): string => {
  if (!iso) return ""
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
}

const addDays = (iso: string, offset: number): string => {
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  d.setDate(d.getDate() + offset)
  return d.toISOString().slice(0, 10)
}

/** Stable row id for drag-drop sorting. Survives day_number/date recomputation. */
const genRowId = (): string => {
  const g: any = typeof globalThis !== "undefined" ? globalThis : {}
  if (g.crypto?.randomUUID) return g.crypto.randomUUID()
  return `r_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

/** Ensure every day carries a `_row_id` for sortable identity. Preserve existing ids. */
const withRowIds = (days: JourneyDayItem[]): JourneyDayItem[] =>
  days.map((d) => ((d as any)._row_id ? d : ({ ...d, _row_id: genRowId() } as JourneyDayItem)))

/** Sortable row id resolver — prefer BE id, else local synthetic. */
const rowIdOf = (d: JourneyDayItem): string =>
  String(d.quotation_journey_day_id || (d as any)._row_id || `day-${d.day_number}`)

/** Keep excursions on trips regardless of source city (neighbor-city tours may carry defaults). */
const pruneExcursionsForTripOnDay = (trip: JourneyDayTripItem, _day: JourneyDayItem): JourneyDayTripItem =>
  trip

/** Per-spec prune: trip._source_city_id must be in {prev, current, next} of the day's new index. */
const pruneTripsByNeighborCities = (
  days: JourneyDayItem[],
  journeyOptionsByCityDate: Record<string, JourneyOptionsByDateValue> = {},
): JourneyDayItem[] =>
  days.map((d, i) => {
    const prevCity = Number(days[Math.max(0, i - 1)].city_id ?? d.city_id ?? 0)
    const currCity = Number(d.city_id ?? 0)
    const nextCity = Number(days[Math.min(days.length - 1, i + 1)].city_id ?? d.city_id ?? 0)
    const allowed = new Set([prevCity, currCity, nextCity])
    const survivors = (d.trips || [])
      .filter((t: any) => {
        const src = Number(t._source_city_id ?? t.city_id ?? d.city_id ?? 0)
        return allowed.has(src)
      })
      .map((t) => pruneExcursionsForTripOnDay(t, d))
    return syncFlagDinnerTransferForDay({ ...d, trips: survivors }, journeyOptionsByCityDate)
  })

const buildTicketPairLocal = (
  adultCatId: number | undefined,
  adultCount: number,
  childCatId: number | undefined,
  childCount: number,
  existingAdultTix?: JourneyDayTripExcursionTicketItem | null,
  existingChildTix?: JourneyDayTripExcursionTicketItem | null,
) => {
  const out: JourneyDayTripExcursionTicketItem[] = []
  if (adultCatId != null || existingAdultTix || adultCount > 0) {
    out.push({
      ...(existingAdultTix || {}),
      category_id: adultCatId ?? existingAdultTix?.category_id ?? 1,
      quantity: adultCount,
    })
  }
  // Child row is required when count > 0 even if SIC zeroing left only an adult ticket behind.
  if (childCatId != null || existingChildTix || childCount > 0) {
    out.push({
      ...(existingChildTix || {}),
      category_id: childCatId ?? existingChildTix?.category_id ?? 2,
      quantity: childCount,
    })
  }
  return out
}

/** Resolve adult/child ticket rows from persisted tickets.
 *  Prefer legacy category_id 1/2, then category_name, then [0]/[1] order
 *  (matches how excursion_pricing category ids are stored — not always 1/2). */
const resolveAdultChildTickets = (tix: JourneyDayTripExcursionTicketItem[] | undefined) => {
  const list = Array.isArray(tix) ? tix : []
  const adultTix =
    list.find((t) => Number(t.category_id) === 1) ??
    list.find((t) => /adult/i.test(String(t.category_name || ""))) ??
    list[0]
  const childTix =
    list.find((t) => Number(t.category_id) === 2) ??
    list.find((t) => /child|kid/i.test(String(t.category_name || ""))) ??
    (list.length > 1 && list[1] !== adultTix ? list[1] : undefined)
  return { adultTix, childTix }
}

/** Rebuild the client-side adult/child snapshot (`_adult_count`, `_child_count`,
 *  `_adult_category_id`, `_child_category_id`) for an excursion (and its add-ons) loaded from the
 *  API. The persisted `tickets[]` are the source of truth — keep ticket row ids so PUT updates
 *  existing quantities instead of leaving the previous adult/child counts unchanged. */
const backfillExcursionCountsFromTickets = (exc: any): any => {
  if (!exc || typeof exc !== "object") return exc
  const tix = Array.isArray(exc.tickets) ? exc.tickets : []
  const { adultTix, childTix } = resolveAdultChildTickets(tix)
  const addOns = Array.isArray(exc.add_ons)
    ? exc.add_ons.map((ao: any) => {
      const aTix = Array.isArray(ao?.tickets) ? ao.tickets : []
      const aoResolved = resolveAdultChildTickets(aTix)
      const adultCount = Number(aoResolved.adultTix?.quantity ?? ao?._adult_count ?? 0)
      const childCount = Number(aoResolved.childTix?.quantity ?? ao?._child_count ?? 0)
      const adultCatId = ao?._adult_category_id ?? aoResolved.adultTix?.category_id
      const childCatId = ao?._child_category_id ?? aoResolved.childTix?.category_id
      return {
        ...ao,
        _adult_category_id: adultCatId,
        _child_category_id: childCatId,
        _adult_count: adultCount,
        _child_count: childCount,
        _adult_price: Number(ao?._adult_price ?? Number(aoResolved.adultTix?.price ?? 10)),
        _child_price: Number(ao?._child_price ?? Number(aoResolved.childTix?.price ?? 10)),
        _counts_manual: Boolean(ao?._counts_manual),
        excursion_add_on_id: ao.excursion_add_on_id,
        _add_on_name: ao.add_on_name || ao._add_on_name || "",
        tickets: buildTicketPairLocal(
          adultCatId,
          adultCount,
          childCatId,
          childCount,
          aoResolved.adultTix,
          aoResolved.childTix,
        ),
      }
    })
    : exc.add_ons
  // Tickets from API are source of truth on reload (do not prefer stale client `_adult_count`).
  const adultCount = Number(adultTix?.quantity ?? exc._adult_count ?? 0)
  const childCount = Number(childTix?.quantity ?? exc._child_count ?? 0)
  const adultCatId = exc._adult_category_id ?? adultTix?.category_id
  const childCatId = exc._child_category_id ?? childTix?.category_id
  const boatSnap = buildBoatPricingSnapshot(exc)
  return {
    ...exc,
    _adult_category_id: adultCatId,
    _child_category_id: childCatId,
    _adult_count: adultCount,
    _child_count: childCount,
    _adult_price: exc._adult_price ?? Number(adultTix?.price ?? 0),
    _child_price: exc._child_price ?? Number(childTix?.price ?? 0),
    // Keep API child_policy; manual flag is client-only (defaults false on load).
    child_policy: Array.isArray(exc.child_policy) ? exc.child_policy : [],
    _counts_manual: Boolean(exc._counts_manual),
    add_ons: addOns,
    tickets: buildTicketPairLocal(adultCatId, adultCount, childCatId, childCount, adultTix, childTix),
    ...boatSnap
  }
}

const deriveJourneyDays = (
  cities: CityNightRow[],
  startDate: string,
  cityNameLookup: (cityId: string) => string,
): JourneyDayItem[] => {
  const iso = normalizeIsoDate(startDate)
  if (!iso || cities.length === 0) return []
  const totalNights = cities.reduce((sum, c) => sum + (Number(c.nights) || 0), 0)
  if (totalNights === 0) return []

  const citySequence: { cityId: number; cityName: string }[] = []
  cities.forEach((c) => {
    const n = Number(c.nights) || 0
    const cityIdNum = Number(c.city) || 0
    const cityName = cityNameLookup(c.city) || ""
    for (let i = 0; i < n; i++) citySequence.push({ cityId: cityIdNum, cityName })
  })
  const lastCity = citySequence[citySequence.length - 1]

  const totalDays = totalNights + 1
  const days: JourneyDayItem[] = []
  for (let i = 0; i < totalDays; i++) {
    const slot = i < totalNights ? citySequence[i] : lastCity
    days.push({
      day_number: i + 1,
      date: addDays(iso, i),
      city_id: slot?.cityId,
      city_name: slot?.cityName,
      flag_guide: false,
      flag_dinner_transfer: false,
      guide_charge: 0,
      guide_base_price: 0,
      guide_tip_amount: 0,
      meals: [],
      hotel: null,
      trips: [],
    })
  }
  return days
}

const matchTripOption = (
  trip: JourneyDayTripItem,
  list: TripOptionItem[],
): TripOptionItem | undefined => {
  if (!trip.day_trip_id) return undefined
  const tripSrc = Number((trip as any)._source_city_id ?? (trip as any).city_id ?? 0)
  return list.find((t) => {
    if (String(t.day_trip_id) !== String(trip.day_trip_id)) return false
    const optSrc = Number((t as any)._source_city_id ?? t.city_id ?? 0)
    if (tripSrc && optSrc) return tripSrc === optSrc
    return true
  })
}

const findTripInOptionList = (
  trip: JourneyDayTripItem,
  list: TripOptionItem[],
): TripOptionItem | undefined =>
  matchTripOption(trip, list) ?? list.find((t) => String(t.day_trip_id) === String(trip.day_trip_id))

const resolveTripSourceCity = (
  trip: JourneyDayTripItem,
  day: JourneyDayItem,
  options?: JourneyOptionsByDateValue,
  allowDayFallback = false,
): { cityId: number; cityName: string } => {
  const existingId = Number((trip as any)._source_city_id ?? 0)
  const existingName = String((trip as any)._source_city_name ?? "").trim()
  if (existingId > 0) {
    return { cityId: existingId, cityName: existingName || `City ${existingId}` }
  }
  if (options) {
    const def =
      findTripInOptionList(trip, options.tours || []) ??
      findTripInOptionList(trip, options.transfers || [])
    if (def) {
      const cityId = Number((def as any)._source_city_id ?? def.city_id ?? 0)
      const cityName = String((def as any)._source_city_name ?? def.city_name ?? "").trim()
      if (cityId > 0) return { cityId, cityName: cityName || `City ${cityId}` }
    }
  }
  if (allowDayFallback) {
    const cityId = Number(day.city_id ?? 0)
    return { cityId, cityName: String(day.city_name ?? "").trim() || `City ${cityId}` }
  }
  return { cityId: 0, cityName: "" }
}

const sourceCityFieldsFromTripDef = (
  _trip: JourneyDayTripItem,
  def: TripOptionItem,
): Pick<JourneyDayTripItem, "_source_city_id" | "_source_city_name"> => ({
  _source_city_id: Number((def as any)._source_city_id ?? def.city_id ?? 0),
  _source_city_name: String((def as any)._source_city_name ?? def.city_name ?? ""),
})

const resolveSelectedTripOptionValue = (
  trip: JourneyDayTripItem,
  tripList: TripOptionItem[] | undefined,
  fallbackCityId?: unknown,
): string => {
  if (!String(trip.day_trip_id ?? "").trim()) return ""
  if (isPresentSourceCityId((trip as any)._source_city_id) || isPresentSourceCityId((trip as any).city_id)) {
    return buildTripSelectValue(trip, fallbackCityId)
  }
  const matched = findTripInOptionList(trip, tripList || [])
  if (matched) return buildTripSelectValue({ ...trip, ...matched }, fallbackCityId)
  return buildTripSelectValue(trip, fallbackCityId)
}

const findTripDefinition = (
  trip: JourneyDayTripItem,
  options: JourneyOptionsByDateValue | undefined,
): { kind: "tour" | "transfer" | "unknown"; def: TripOptionItem | undefined } => {
  if (!trip.day_trip_id) return { kind: "unknown", def: undefined }
  if (options) {
    const tour = findTripInOptionList(trip, options.tours || [])
    if (tour) return { kind: "tour", def: tour }
    const transfer = findTripInOptionList(trip, options.transfers || [])
    if (transfer) return { kind: "transfer", def: transfer }
  }
  const kindFromType = tripKindFromDayTripType(trip)
  if (kindFromType) return { kind: kindFromType, def: undefined }
  const savedKind = String((trip as any)._trip_kind || "").toLowerCase()
  if (savedKind === "tour" || savedKind === "transfer") {
    return { kind: savedKind, def: undefined }
  }
  return { kind: "unknown", def: undefined }
}

const buildJourneyOptionCityIds = (days: JourneyDayItem[], dayIndex: number): number[] => {
  const currentCityId = Number(days[dayIndex]?.city_id || 0)
  const prevCityId = Number(days[Math.max(0, dayIndex - 1)]?.city_id || currentCityId || 0)
  const nextCityId = Number(days[Math.min(days.length - 1, dayIndex + 1)]?.city_id || currentCityId || 0)
  return [prevCityId, currentCityId, nextCityId].map((x) => Number(x) || 0)
}

const groupTripOptionsByCity = (
  list: TripOptionItem[],
): { label: string; value: string; disabled?: boolean }[] => {
  const grouped = new Map<string, { label: string; items: TripOptionItem[] }>()
  list.forEach((t) => {
    const cityKey = String((t as any)._source_city_id ?? t.city_id ?? "")
    const cityLabel = String((t as any)._source_city_name || t.city_name || `City ${cityKey}`).trim() || "City"
    if (!grouped.has(cityKey)) grouped.set(cityKey, { label: cityLabel, items: [] })
    const bucket = grouped.get(cityKey)!
    if (!bucket.items.some((x) => String(x.day_trip_id) === String(t.day_trip_id))) {
      bucket.items.push(t)
    }
  })
  const out: { label: string; value: string; disabled?: boolean }[] = []
  grouped.forEach(({ label, items }) => {
    out.push({ label, value: `__header__${label}`, disabled: true })
    items.forEach((t) => {
      out.push({
        label: toTitleCase(t.day_trip_name) || "",
        value: buildTripSelectValue(t),
      })
    })
  })
  return out.filter((o) => o.value)
}

const buildTripSelectOptions = (
  optionsByDay: JourneyOptionsByDateValue | undefined,
  kind: "tour" | "transfer" | "unknown",
): { label: string; value: string; disabled?: boolean }[] => {
  const list = kind === "tour" ? (optionsByDay?.tours || []) : kind === "transfer" ? (optionsByDay?.transfers || []) : []
  return groupTripOptionsByCity(list)
}


// Build a minimal hotel-options list from the currently selected hotel (if any),
// so the hotel modal can render + highlight immediately before API options arrive.
const buildSelectedHotelFallbackOptions = (day: JourneyDayItem): QuotationHotelOptionsByDateHotel[] => {
  const h = day.hotel
  if (!h?.hotel_id || !h.hotel_room_type_id) return []

  const pricingOption: QuotationHotelOptionsPricingOption = {
    hotel_room_pricing_id: String(h.hotel_room_pricing_id || "selected"),
    hotel_room_day_group_pricing_id: String(h.hotel_room_day_group_pricing_id || null),
    pricing_mode: "meal",
    meal_plan_type_id: Number((h as any).meal_plan_type_id ?? 0) || null,
    meal_plan_type_name: (h as any).hotel_meal_plan_type_name ?? (h as any).meal_plan_type_name ?? null,
    meal_plan_type_included_types: (h as any).hotel_meal_plan_included_types ?? [],
    git_fit_type_id: null,
    git_fit_type_name: null,
    git_min_rooms: null,
    room_price: Number(h.room_price ?? 0),
    extra_bed_price: Number(h.extra_bed_price ?? 0),
    child_without_bed_price: Number(h.child_without_bed_price ?? 0),
    child_with_bed_price: Number((h as any).child_with_bed_price ?? 0),
    additional_pax_surcharge_unit: 0,
  }

  const roomType: QuotationHotelOptionsByDateRoomType = {
    hotel_room_type_id: String(h.hotel_room_type_id),
    hotel_room_type_name: String(h.hotel_room_type_name || ""),
    extra_bed_price: Number(h.extra_bed_price ?? 0),
    child_with_bed_price: Number((h as any).child_with_bed_price ?? 0),
    child_without_bed_price: Number(h.child_without_bed_price ?? 0),
    additional_pax_surcharge_unit: 0,
    per_person_adult_surcharge: Number((h as any).per_person_adult_surcharge ?? 0),
    per_person_child_surcharge: Number((h as any).per_person_child_surcharge ?? 0),
    extra_beds: null,
    git_min_rooms: null,
    pricing_options: [pricingOption],
  }

  const hotel: QuotationHotelOptionsByDateHotel = {
    hotel_id: String(h.hotel_id),
    hotel_name: String(h.hotel_name || ""),
    city_id: Number(day.city_id || 0),
    city_name: String(day.city_name || ""),
    rating: 0,
    hotel_room_types: [roomType],
  }

  return [hotel]
}

const resolveSelectedHotelSelection = (
  hotels: QuotationHotelOptionsByDateHotel[],
  savedHotel: JourneyDayHotelItem | null | undefined,
): {
  selectedHotelId: string
  selectedRoomTypeId: string
  selectedMealPlanTypeId: number | null
  selectedMealPlanTypeName: string | null
} => {
  const fallback = {
    selectedHotelId: String(savedHotel?.hotel_id || ""),
    selectedRoomTypeId: String(savedHotel?.hotel_room_type_id || ""),
    selectedMealPlanTypeId: Number(savedHotel?.meal_plan_type_id || 0) || null,
    selectedMealPlanTypeName:
      savedHotel?.hotel_meal_plan_type_name ??
      savedHotel?.meal_plan_type_name ??
      null,
  }
  if (!savedHotel) return fallback

  const wantedRoomTypeId = String(savedHotel.hotel_room_type_id || "")
  const wantedPricingId = String(savedHotel.hotel_room_pricing_id || "")

  // Phase 2: prefer hotel_id, then room/pricing ids.
  const matchedHotel = findMatchingHotelOption(hotels, savedHotel)
  const matchedRt = (matchedHotel?.hotel_room_types || []).find((rt) =>
    String(rt.hotel_room_type_id) === wantedRoomTypeId ||
    (rt.pricing_options || []).some((po) => String(po.hotel_room_pricing_id) === wantedPricingId),
  )
  const matchedPo = (matchedRt?.pricing_options || []).find((po) =>
    wantedPricingId ? String(po.hotel_room_pricing_id) === wantedPricingId : false,
  )

  return {
    selectedHotelId: String(savedHotel.hotel_id || matchedHotel?.hotel_id || ""),
    selectedRoomTypeId: String(wantedRoomTypeId || matchedRt?.hotel_room_type_id || ""),
    selectedMealPlanTypeId:
      Number(savedHotel.meal_plan_type_id ?? matchedPo?.meal_plan_type_id ?? 0) || null,
    selectedMealPlanTypeName:
      savedHotel.hotel_meal_plan_type_name ??
      savedHotel.meal_plan_type_name ??
      matchedPo?.meal_plan_type_name ??
      null,
  }
}

/** Merge API trip definitions into saved day trips so Seater + pricing fields resolve. */
const enrichTripsWithJourneyOptions = (
  trips: JourneyDayTripItem[],
  value: JourneyOptionsByDateValue,
): JourneyDayTripItem[] => {
  return trips.map((trip, index) => {
    const def =
      findTripInOptionList(trip, value.tours || []) ??
      findTripInOptionList(trip, value.transfers || [])
    if (!def) {
      const savedKind = String((trip as any)._trip_kind || "").toLowerCase()
      if (savedKind === "tour" || savedKind === "transfer") {
        return { ...trip, _trip_kind: savedKind } as JourneyDayTripItem
      }
      return trip
    }
    const sourceCity = sourceCityFieldsFromTripDef(trip, def)
    const seatingList = def.seating_pricing || []
    const sicRow = findSicSeatingRow(seatingList)
    const isSic = isSicTrip(trip) || (sicRow != null && Number(trip.seating_type_id) === Number(sicRow.seating_type_id))
    const flagLastTrip = trips.length - 1 === index;
    // Re-snapshot each excursion's boat-pricing config (cano rate table / lantern fields) from the
    // live definition. Counts/tickets/prices are preserved — this only refreshes the boat config,
    // which otherwise gets lost after navigating away and back (cano price falls to 0).
    // Also refresh child_policy when options have it; never clear sticky `_counts_manual`.
    const excDefs = value.excursions || []
    const enrichedExcursions = (trip.excursions || []).map((exc: any) => {
      const excDef =
        excDefs.find((x: any) => String(x.excursion_id) === String(exc.excursion_id)) ||
        (def.excursions || []).find((x: any) => String(x.excursion_id) === String(exc.excursion_id))
      if (!excDef) return exc
      const nextPolicy = Array.isArray(excDef.child_policy)
        ? excDef.child_policy
        : Array.isArray(exc.child_policy)
          ? exc.child_policy
          : []
      return {
        ...exc,
        ...buildBoatPricingSnapshot(excDef),
        child_policy: nextPolicy,
      }
    })
    const tourMatch = findTripInOptionList(trip, value.tours || [])
    const tripKind = tourMatch ? "tour" : "transfer"
    if (isSic) {
      // SIC price/dinner from the named "SIC" row (new data); fall back to the legacy
      // flag_enable_sic row's sic_price_per_person so existing trips are unaffected.
      const seatingLevelSic = seatingList.find((s: any) => Boolean(s?.flag_enable_sic))
      const sicPrice = Number(
        (sicRow as any)?.price ??
        (seatingLevelSic as any)?.sic_price_per_person ??
        def?.sic_price_per_person ??
        0,
      )
      const resolvedSicId = Number(trip.seating_type_id) || Number(sicRow?.seating_type_id ?? 0) || 0
      const enableDinner = flagLastTrip ? Boolean((def as any)?.flag_enable_dinner) : false
      return {
        ...trip,
        ...sourceCity,
        seating_type_id: resolvedSicId || trip.seating_type_id,
        seating_type_name: trip.seating_type_name ?? sicRow?.seating_type_name ?? "SIC",
        excursions: enrichedExcursions,
        _trip_kind: tripKind,
        _seating_value: resolvedSicId ? String(resolvedSicId) : "SIC",
        ...buildPersistedSeatingSnapshotFields(sicRow as any, enableDinner, {
          seatingPrice: sicPrice,
          perPerson: true,
        }),
        ...snapshotTripMealIncludedFields(def as any, trip),
        seating_pricing: sicRow,
      }
    }
    const seating = seatingList.find((s) => Number(s.seating_type_id) === Number(trip.seating_type_id))
    const enableDinner = flagLastTrip ? Boolean((def as any)?.flag_enable_dinner) : false
    const seatingPrice = Number((trip as any)._seating_price ?? seating?.price ?? 0)
    return {
      ...trip,
      ...sourceCity,
      excursions: enrichedExcursions,
      _trip_kind: tripKind,
      _seating_value: String(trip.seating_type_id ?? ""),
      ...buildPersistedSeatingSnapshotFields(seating as any, enableDinner, {
        seatingPrice,
      }),
      ...snapshotTripMealIncludedFields(def as any, trip),
      seating_pricing: seating
    }
  }) as JourneyDayTripItem[]
}

/** Step 2 API expects 1-based sequence_number on trips, excursions, and additional costs. */
const withJourneySequenceNumbers = (
  days: JourneyDayItem[] | undefined,
  additionalCostsIn: AdditionalCostItem[] | undefined,
): { days: JourneyDayItem[]; additional_costs: AdditionalCostItem[] } => ({
  days: (days || []).map((day) => ({
    ...day,
    trips: (day.trips || []).map((trip, ti) => ({
      ...trip,
      sequence_number: ti + 1,
      excursions: (trip.excursions || []).map((ex, ei) => ({
        ...ex,
        sequence_number: ei + 1,
      })),
    })),
  })),
  additional_costs: (additionalCostsIn || []).map((row, i) => ({
    ...row,
    sequence_number: i + 1,
  })),
})

const buildSeaterOptions = (
  def: TripOptionItem | undefined,
  trip?: JourneyDayTripItem,
): { label: string; value: string }[] =>
  (() => {
    // SIC now arrives as its own row in seating_pricing (seating_type_name "SIC"). Exclude it from
    // the vehicle list — SIC is offered via the single synthetic "SIC" option below — otherwise
    // it shows twice (once as the real row, once as the synthetic option).
    const base = (def?.seating_pricing || [])
      .filter((s) => String(s.seating_type_name || "").trim().toUpperCase() !== "SIC")
      .map((s) => ({
        label: s.seating_type_name || `Seater ${s.seating_type_id}`,
        value: String(s.seating_type_id ?? ""),
      }))
      .filter((o) => o.value)
    const seatingSicEnabled = (def?.seating_pricing || []).some((s) => Boolean((s as any).flag_enable_sic))
    const tripSicEnabled = Boolean(def?.flag_enable_sic)
    const sicRow = findSicSeatingRow(def?.seating_pricing)
    if (sicRow?.seating_type_id != null) {
      const sicValue = String(sicRow.seating_type_id)
      if (!base.some((o) => o.value === sicValue)) {
        base.push({ label: sicRow.seating_type_name || "SIC", value: sicValue })
      }
    } else if (seatingSicEnabled || tripSicEnabled) {
      if (!base.some((o) => o.value === "SIC")) {
        base.push({ label: "SIC", value: "SIC" })
      }
    }
    if (base.length > 0) return base

    if (!trip) return []
    const isSic =
      String(trip.seating_type_name || "").toUpperCase() === "SIC" ||
      String((trip as any)._seating_value || "") === "SIC"
    if (isSic) {
      const sid = trip.seating_type_id
      if (sid != null && Number(sid) !== 0) {
        return [{ label: "SIC", value: String(sid) }]
      }
      return [{ label: "SIC", value: "SIC" }]
    }
    const sid = trip.seating_type_id
    const name = String(trip.seating_type_name || "").trim() || (sid != null ? `Seater ${sid}` : "")
    if (sid != null && Number(sid) !== 0 && name) {
      return [{ label: name, value: String(sid) }]
    }
    if (name) return [{ label: name, value: String(sid ?? "") }]
    return []
  })()

const getSeaterCapacity = (seating: any): number => {
  const name = String(seating?.seating_type_name || "")
  if (/bus\s*\+\s*car|bus\s*and\s*car/i.test(name)) return 999
  const fromName = Number(name.match(/\d+/)?.[0] || 0)
  if (fromName > 0) return fromName
  const fromId = Number(seating?.seating_type_id || 0)
  return fromId > 0 ? fromId : 0
}

const resolveTargetCapacityByPax = (paxRequired: number): number => {
  if (paxRequired <= 2) return 4
  if (paxRequired === 3) return 7
  if (paxRequired <= 8) return 16
  if (paxRequired <= 15) return 29
  if (paxRequired <= 22) return 35
  if (paxRequired <= 40) return 45
  // >40 => Bus + Car Combination (prefer named option when available)
  return 999
}

const pickDefaultSeatingForPax = (
  def: TripOptionItem | undefined,
  paxRequired: number,
) => {
  const list = def?.seating_pricing || []
  if (list.length === 0) return undefined
  const normalized = list
    .map((s) => ({ seating: s, cap: getSeaterCapacity(s) }))
    .filter((x) => x.cap > 0)
    .sort((a, b) => a.cap - b.cap)
  if (normalized.length === 0) return list[0]
  const targetCap = resolveTargetCapacityByPax(Math.max(0, Number(paxRequired) || 0))
  // Prefer exact mapped vehicle (4/7/16/29/35/45 or Bus+Car marker).
  const exact = normalized.find((x) => x.cap === targetCap)
  if (exact) return exact.seating
  // Fallback: next higher available option; if none, largest.
  return normalized.find((x) => x.cap >= targetCap)?.seating ?? normalized[normalized.length - 1].seating
}

/** Snapshot seating fields when auto-selecting a vehicle row (add/change trip, pax sync). */
const buildAutoSeatingFields = (
  seating: SeatingPriceOptionItem | undefined,
  def: TripOptionItem | undefined,
) => ({
  seating_type_id: seating?.seating_type_id ?? 0,
  seating_type_name: toTitleCase(seating?.seating_type_name) ?? null,
  _seating_value: String(seating?.seating_type_id ?? ""),
  ...buildPersistedSeatingSnapshotFields(
    seating as any,
    Boolean((def as any)?.flag_enable_dinner),
  ),
  ...snapshotTripMealIncludedFields(def as any),
})

const uniqueExcursionsById = (list: ExcursionOptionItem[] | undefined): ExcursionOptionItem[] => {
  const map = new Map<string, ExcursionOptionItem>()
    ; (list || []).forEach((e) => {
      const id = String(e.excursion_id || "")
      if (!id) return
      if (!map.has(id)) map.set(id, e)
    })
  return Array.from(map.values())
}

const filterExcursionsForTripSourceCity = (
  excursions: ExcursionOptionItem[] | undefined,
  trip: JourneyDayTripItem,
  day: JourneyDayItem,
  options?: JourneyOptionsByDateValue,
): ExcursionOptionItem[] => {
  const { cityId } = resolveTripSourceCity(trip, day, options)
  if (!cityId) return []
  return uniqueExcursionsById(excursions).filter(
    (e) => Number((e as any)._source_city_id ?? e.city_id ?? 0) === cityId,
  )
}

const buildExcursionOptions = (
  list: ExcursionOptionItem[] | undefined,
): { label: string; value: string; disabled?: boolean }[] => {
  const grouped = new Map<string, { label: string; items: ExcursionOptionItem[] }>()
    ; (list || []).forEach((e) => {
      const cityKey = String((e as any)._source_city_id ?? e.city_id ?? "")
      const cityLabel = String((e as any)._source_city_name ?? e.city_name ?? `City ${cityKey}`).trim() || "City"
      if (!grouped.has(cityKey)) grouped.set(cityKey, { label: cityLabel, items: [] })
      const bucket = grouped.get(cityKey)!
      if (!bucket.items.some((x) => String(x.excursion_id) === String(e.excursion_id))) {
        bucket.items.push(e)
      }
    })
  const out: { label: string; value: string; disabled?: boolean }[] = []
  grouped.forEach(({ label, items }) => {
    out.push({ label, value: `__header__${label}`, disabled: true })
    items.forEach((e) => {
      out.push({ label: e.excursion_name || "", value: e.excursion_id || "" })
    })
  })
  return out.filter((o) => o.value)
}

const dedupeApiCitiesById = (cities: unknown[]): any[] => {
  const map = new Map<number, any>()
    ; (cities as any[]).forEach((c) => {
      const id = Number(c?.city_id)
      if (!Number.isFinite(id) || id <= 0) return
      if (!map.has(id)) map.set(id, c)
    })
  return Array.from(map.values())
}

const mapJourneyOptionsApiResponse = (cities: unknown[]): JourneyOptionsByDateValue => {
  const uniqueCities = dedupeApiCitiesById(cities)
  return {
    tours: uniqueCities.flatMap((c) =>
      (Array.isArray(c.tours) ? c.tours : []).map((t: TripOptionItem) => ({
        ...t,
        _source_city_id: c.city_id,
        _source_city_name: c.city_name,
      })),
    ) as TripOptionItem[],
    transfers: uniqueCities.flatMap((c) =>
      (Array.isArray(c.transfers) ? c.transfers : []).map((t: TripOptionItem) => ({
        ...t,
        _source_city_id: c.city_id,
        _source_city_name: c.city_name,
      })),
    ) as TripOptionItem[],
    excursions: uniqueCities.flatMap((c) =>
      (Array.isArray(c.excursions) ? c.excursions : []).map((e: ExcursionOptionItem) => ({
        ...e,
        _source_city_id: c.city_id,
        _source_city_name: c.city_name,
      })),
    ) as ExcursionOptionItem[],
  }
}

// Resolve `git_fit_type_id` per spec: 1 = GIT, 2 = FIT.
const GIT_TYPE_ID = 1
const FIT_TYPE_ID = 2

/** Pick the active pricing option for a room type given roomsNeeded & (optional) selected meal plan.
 *  - `simple`  → only one option, return it.
 *  - `git_fit` → pick GIT (id=1) when roomsNeeded ≥ git_min_rooms, else FIT (id=2).
 *  - `meal`    → filter by selected meal_plan_type_id, then apply git/fit resolution within that subset. */
const pickPricingOption = (
  rt: QuotationHotelOptionsByDateRoomType,
  roomsNeeded: number,
  mealPlanId?: number | null,
): QuotationHotelOptionsPricingOption | undefined => {
  const opts = rt.pricing_options || []
  if (opts.length === 0) return undefined
  const mode = opts[0].pricing_mode

  if (mode === "simple") return opts[0]

  if (mode === "git_fit") {
    const minRooms = opts[0].git_min_rooms ?? rt.git_min_rooms
    if (minRooms == null) return opts[0]
    const wantedTypeId = Number(roomsNeeded) >= Number(minRooms) ? GIT_TYPE_ID : FIT_TYPE_ID
    return opts.find((o) => Number(o.git_fit_type_id) === wantedTypeId) ?? opts[0]
  }

  if (mode === "meal") {
    const filtered = mealPlanId != null
      ? opts.filter((o) => Number(o.meal_plan_type_id) === Number(mealPlanId))
      : opts
    const pool = filtered.length > 0 ? filtered : opts
    // Apply git/fit within the meal-filtered pool when applicable.
    const minRooms = pool[0]?.git_min_rooms ?? rt.git_min_rooms
    if (minRooms != null && pool.some((o) => o.git_fit_type_id != null)) {
      const wantedTypeId = Number(roomsNeeded) >= Number(minRooms) ? GIT_TYPE_ID : FIT_TYPE_ID
      return pool.find((o) => Number(o.git_fit_type_id) === wantedTypeId) ?? pool[0]
    }
    return pool[0]
  }

  return opts[0]
}

const resolveRoomPrice = (
  rt: QuotationHotelOptionsByDateRoomType,
  roomsNeeded: number,
  mealPlanId?: number | null,
): number => Number(pickPricingOption(rt, roomsNeeded, mealPlanId)?.room_price || 0)

const formatDollar = (n: number): string => `VND ${Math.round(n).toLocaleString("en-US")}`

const lowestRoomPriceForHotel = (
  h: QuotationHotelOptionsByDateHotel,
  roomsNeeded: number,
  mealPlanId?: number | null,
): number => {
  const prices = (h.hotel_room_types || [])
    .map((rt) => resolveRoomPrice(rt, roomsNeeded, mealPlanId))
    .filter((p) => p > 0)
  return prices.length === 0 ? 0 : Math.min(...prices)
}

/** Enumerate distinct meal plans offered across a hotel's `meal`-mode pricing options. */
const collectHotelMealPlans = (
  h: QuotationHotelOptionsByDateHotel,
): { id: number; name: string; includedTypes: MealPlanIncludedType[] }[] => {
  const map = new Map<number, { name: string; includedTypes: MealPlanIncludedType[] }>()
    ; (h.hotel_room_types || []).forEach((rt) => {
      ; (rt.pricing_options || []).forEach((po) => {
        if (po.pricing_mode === "meal" && po.meal_plan_type_id != null) {
          const id = Number(po.meal_plan_type_id)
          const incomingIncluded = (po.meal_plan_type_included_types || []) as MealPlanIncludedType[]
          const existing = map.get(id)
          if (!existing) {
            map.set(id, {
              name: po.meal_plan_type_name || `Plan ${po.meal_plan_type_id}`,
              includedTypes: incomingIncluded,
            })
            return
          }
          if (existing.includedTypes.length === 0 && incomingIncluded.length > 0) {
            map.set(id, { ...existing, includedTypes: incomingIncluded })
          }
        }
      })
    })
  return Array.from(map.entries()).map(([id, value]) => ({
    id,
    name: value.name,
    includedTypes: value.includedTypes || [],
  }))
}

const MEAL_PLAN_DESCRIPTIONS: Record<string, string> = {
  BB: "Bed & Breakfast",
  HB: "Half Board",
  AI: "All Inclusive",
}

const formatMealPlanLabel = (name: string): string => {
  const desc = MEAL_PLAN_DESCRIPTIONS[name.toUpperCase()]
  return desc ? `${name} (${desc})` : name
}

const MEAL_PLAN_INFO_TEXT =
  "BB: Bed & Breakfast (breakfast only). " +
  "HB: Half Board (breakfast + lunch OR dinner — pick one). " +
  "AI: All Inclusive (breakfast + lunch + dinner)."

const STAR_FILTER_OPTIONS = [
  { label: "All Stars", value: "all" },
  { label: "5 Stars", value: "5" },
  { label: "4 Stars", value: "4" },
  { label: "3 Stars", value: "3" },
  { label: "2 Stars", value: "2" },
  { label: "1 Star", value: "1" },
]

const SORT_OPTIONS = [
  { label: "Pricing", value: "default" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
]

interface HotelPickerModalProps {
  open: boolean
  onClose: () => void
  cityName: string
  hotels: QuotationHotelOptionsByDateHotel[]
  roomsNeeded: number
  selectedHotelId?: string
  selectedRoomTypeId?: string
  selectedMealPlanTypeName?: string | null
  onSelect: (
    hotel: QuotationHotelOptionsByDateHotel,
    roomType: QuotationHotelOptionsByDateRoomType,
    pricingOption: QuotationHotelOptionsPricingOption | undefined,
    resolvedPrice: number,
  ) => void
}

const StarRow: React.FC<{ rating: number }> = ({ rating }) => (
  <span style={{ color: "#f59e0b", letterSpacing: 2 }}>{"★".repeat(Math.max(0, Math.min(5, rating)))}</span>
)

const HotelPickerModal: React.FC<HotelPickerModalProps> = ({
  open,
  onClose,
  hotels,
  onSelect,
  roomsNeeded,
  selectedHotelId,
  selectedRoomTypeId,
  selectedMealPlanTypeName,
}) => {
  const [search, setSearch] = useState("")
  const [starFilter, setStarFilter] = useState("all")
  const [sort, setSort] = useState("default")
  // Per-hotel selected meal plan id. Only meaningful when hotel has `meal`-mode pricing options.
  const [mealPlanByHotel, setMealPlanByHotel] = useState<Record<string, number>>({})

  // Reset internal state on open + seed default meal plan per meal-mode hotel.
  useEffect(() => {
    if (!open) return
    setSearch("")
    setStarFilter("all")
    setSort("default")
    const initial: Record<string, number> = {}
    hotels.forEach((h) => {
      const plans = collectHotelMealPlans(h)
      if (plans.length > 0) {
        initial[h.hotel_id] = plans[0].id
        // Keep selected meal plan when reopening picker for same hotel/room.
        if (selectedHotelId && h.hotel_id === selectedHotelId && selectedMealPlanTypeName) {
          const wantedCode = resolveHotelMealPlanCode(selectedMealPlanTypeName)
          const matched = plans.find((p) => resolveHotelMealPlanCode(p.name) === wantedCode)
          if (matched) initial[h.hotel_id] = matched.id
        }
      }
    })
    setMealPlanByHotel(initial)
  }, [open, hotels, selectedHotelId, selectedMealPlanTypeName])

  const filteredHotels = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = hotels.filter((h) => {
      if (starFilter !== "all" && h.rating !== Number(starFilter)) return false
      if (!q) return true
      const inName = (h.hotel_name || "").toLowerCase().includes(q)
      const inRoomType = (h.hotel_room_types || []).some((rt) => (rt.hotel_room_type_name || "").toLowerCase().includes(q))
      return inName || inRoomType
    })
    const lowestForSort = (h: QuotationHotelOptionsByDateHotel) =>
      lowestRoomPriceForHotel(h, roomsNeeded, mealPlanByHotel[h.hotel_id])
    if (sort === "price_asc") {
      list = [...list].sort((a, b) => lowestForSort(a) - lowestForSort(b))
    } else if (sort === "price_desc") {
      list = [...list].sort((a, b) => lowestForSort(b) - lowestForSort(a))
    }
    return list
  }, [hotels, search, starFilter, sort, roomsNeeded, mealPlanByHotel])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* <DialogTitle sx={{ pb: 0 }}> */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "16px 24px 10px 16px" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>Select Hotel & Room Type</div>
        </div>
        <IconButton onClick={onClose} aria-label="Close" size="small" style={{ padding: "0  5px" }}>
          <span style={{ fontSize: 20, lineHeight: 1 }}><IconsColor iconName="closeIcon" width="20px" height="20px" fillColor="none" strokeColor="currentColor" /></span>
        </IconButton>
      </div>
      {/* </DialogTitle> */}
      <DialogContent dividers>
        <div style={{ display: "flex", gap: 10, marginBottom: 12, justifyContent: "flex-end" }}>
          <input
            type="text"
            placeholder="Search hotel or room type"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              fontSize: 13,
              outline: "none",
              maxWidth: "40%"
            }}
          />
          <select
            value={starFilter}
            onChange={(e) => setStarFilter(e.target.value)}
            style={{
              padding: "8px 36px 8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              fontSize: 13,
              backgroundColor: "#fff",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 12px center",
              width: "120px"
            }}
          >
            {STAR_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            style={{
              padding: "8px 36px 8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              fontSize: 13,
              backgroundColor: "#fff",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 12px center",
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {filteredHotels.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, color: "#64748b" }}>No Hotels Match The Filters.</div>
        )}

        {filteredHotels.map((hotel) => {
          const mealPlans = collectHotelMealPlans(hotel)
          const hasMealMode = mealPlans.length > 0
          const selectedMealPlanId = hasMealMode
            ? mealPlanByHotel[hotel.hotel_id] ?? mealPlans[0].id
            : undefined
          const firstRt = hotel.hotel_room_types?.[0]
          // Prefer meal-aware option's child prices when applicable; fall back to room-type level.
          const firstRtOption = firstRt ? pickPricingOption(firstRt, roomsNeeded, selectedMealPlanId) : undefined
          const childWithBed = firstRtOption?.child_with_bed_price ?? firstRt?.child_with_bed_price ?? 0
          const childWithoutBed = firstRtOption?.child_without_bed_price ?? firstRt?.child_without_bed_price ?? 0

          return (
            <div
              key={hotel.hotel_id}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
                background: "#ffffff",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{hotel.hotel_name}</div>
                  <div style={{ marginTop: 4, fontSize: 12, color: "#475569" }}>
                    <StarRow rating={hotel.rating} /> <span style={{ marginLeft: 6 }}>{hotel.city_name}</span>123
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "#475569", textAlign: "right", display: "flex", gap: "10px" }}>
                  <div>Child with Bed: <strong>{formatDollar(childWithBed)}</strong></div>
                  <div>Child without Bed: <strong>{formatDollar(childWithoutBed)}</strong></div>
                </div>
              </div>

              {hasMealMode && (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#0f172a", fontWeight: 600 }}>Meal Plan:</span>
                  <select
                    value={String(selectedMealPlanId ?? "")}
                    onChange={(e) =>
                      setMealPlanByHotel((prev) => ({
                        ...prev,
                        [hotel.hotel_id]: Number(e.target.value),
                      }))
                    }
                    style={{
                      padding: "6px 10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      fontSize: 12,
                      background: "#fff",
                      minWidth: 170
                    }}
                  >
                    {mealPlans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {formatMealPlanLabel(p.name)}
                      </option>
                    ))}
                  </select>
                  <span
                    title={MEAL_PLAN_INFO_TEXT}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: "1px solid #94a3b8",
                      color: "#64748b",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "help",
                      lineHeight: 1,
                    }}
                  >
                    i
                  </span>
                </div>
              )}

              <div style={{ marginTop: 10, fontSize: 12, color: "#0f172a", fontWeight: 600 }}>Room Types:</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                {(hotel.hotel_room_types || []).map((rt) => {
                  const pricingOption = pickPricingOption(rt, roomsNeeded, selectedMealPlanId)
                  const price = Number(pricingOption?.room_price || 0)
                  const isSelected =
                    hotel.hotel_id === selectedHotelId &&
                    rt.hotel_room_type_id === selectedRoomTypeId
                  return (
                    <button
                      key={rt.hotel_room_type_id}
                      type="button"
                      onClick={() => onSelect(hotel, rt, pricingOption, price)}
                      style={{
                        padding: "8px 14px",
                        border: isSelected ? "2px solid #16a34a" : "1px solid #1e3a8a",
                        borderRadius: 6,
                        background: isSelected ? "#eaf8ee" : "#1e3a8a",
                        color: isSelected ? "#166534" : "#ffffff",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        textTransform: "uppercase",
                      }}
                    >
                      {rt.hotel_room_type_name} {formatDollar(price)}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </DialogContent>
    </Dialog>
  )
}

const findExcursionDefinition = (
  excursionId: string,
  list: ExcursionOptionItem[] | undefined,
) => uniqueExcursionsById(list).find((e) => e.excursion_id === excursionId)

interface SortableDayRowProps {
  id: string
  collapsed?: boolean
  children: (handle: { listeners: any; attributes: any; isDragging: boolean }) => React.ReactNode
  extraStyle?: React.CSSProperties;
}

/** Wraps one day row with `useSortable`. Drag listeners are NOT attached to the row itself —
 *  they're exposed via render-prop so only the day-card drag handle initiates drag, leaving
 *  inputs/dropdowns inside cells fully usable.
 *
 *  Behaviour during drag:
 *  - The source row collapses to 0 height (`display: none`-equivalent via the wrapper).
 *    This removes any "gap" between neighbour rows because the slot stops being reserved.
 *  - A floating clone is rendered by `<DragOverlay>` (defined in the parent component) at the
 *    cursor, with the full pre-drag dimensions. Together this gives "no gap + full-height
 *    preview" UX even when rows have very different heights.
 *  - We expose `data-row-id={id}` so the parent can snapshot the source DOM into the overlay. */
const SortableDayRow: React.FC<SortableDayRowProps> = ({ id, collapsed, children, extraStyle }) => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({ id })
  const style: React.CSSProperties = {
    ...extraStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
    // Source rows stay in place (stable layout) and just dim while dragging — the full-width
    // DragOverlay clone is the moving preview, and a full-width band marks the drop target.
    opacity: isDragging || collapsed ? 0.4 : undefined,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-row-id={id}
      className={styles.bodyRow}
    >
      {children({ listeners, attributes, isDragging })}
    </div>
  )
}

const isLastDay = (day: JourneyDayItem, allDays: JourneyDayItem[]): boolean =>
  (day.day_number ?? 0) === allDays.length

/** Indices of the contiguous same-city block of NIGHT days around `index` (checkout excluded).
 *  Point 2: selecting any day selects its whole city block. */
const cityBlockIndices = (days: JourneyDayItem[], index: number): number[] => {
  if (index < 0 || index >= days.length) return []
  if (isLastDay(days[index], days)) return []
  const c = Number(days[index].city_id)
  const lastNight = days.length - 2
  let lo = index
  let hi = index
  while (lo > 0 && !isLastDay(days[lo - 1], days) && Number(days[lo - 1].city_id) === c) lo--
  while (hi < lastNight && Number(days[hi + 1].city_id) === c) hi++
  return Array.from({ length: hi - lo + 1 }, (_, k) => lo + k)
}

/** Point 1 — a single day of `draggedCity` may only land where the actual prev OR next neighbour
 *  is the same city. `staticCities` = night-day cities with the dragged day removed; the checkout
 *  day is the real "next" neighbour for the last-night gap. */
const isValidSingleDropGap = (
  staticCities: number[],
  checkoutCity: number,
  draggedCity: number,
  gap: number,
): boolean => {
  const prev = gap > 0 ? staticCities[gap - 1] : null
  const next = gap < staticCities.length ? staticCities[gap] : checkoutCity
  return prev === draggedCity || next === draggedCity
}

/** Point 3 — a selected city block may only land on a city boundary (incl. the very top and the
 *  gap just before the pinned checkout day). */
const isValidBlockDropGap = (staticCities: number[], gap: number): boolean =>
  gap === 0 || gap === staticCities.length || staticCities[gap - 1] !== staticCities[gap]

const JourneyDetails: React.FC<JourneyDetailsProps> = ({ onBindSaveHandler }) => {
  void HotelPickerModal
  const { showError, showSuccess } = useSnackbar()
  const {
    quotationId,
    quotationName,
    selectedCities,
    startDate,
    cityOptions,
    flagWithTip, setFlagWithTip,
    packageServiceCharge, setPackageServiceCharge,
    journeyDays, setJourneyDays,
    additionalCosts, setAdditionalCosts,
    hotelOptionsByCityDate, setHotelOptionsByCityDate,
    journeyOptionsByCityDate, setJourneyOptionsByCityDate,
    adults,
    children,
    childDetails,
    extraBed,
    rooms,
    noOfNights,
    guideGroupPricing, setGuideGroupPricing
  } = useQuotation()

  const [hasHydrated, setHasHydrated] = useState(false)
  const [activeTripPicker, setActiveTripPicker] = useState<{ dayIndex: number; kind: "tour" | "transfer" } | null>(null)
  const [activeHotelPicker, setActiveHotelPicker] = useState<number | null>(null)
  const [hotelLevelLoaderIndex, setHotelLevelLoaderIndex] = useState<number | null>(null);
  const [selectedHotelPickerOptions, setSelectedHotelPickerOptions] = useState<any>({});
  const [activeExcursionPicker, setActiveExcursionPicker] = useState<{ dayIndex: number; tripIndex: number } | null>(null)
  const [activeAddOnPicker, setActiveAddOnPicker] = useState<{ dayIndex: number; tripIndex: number; excIndex: number } | null>(null)
  const [loadingKeys, setLoadingKeys] = useState<Record<string, boolean>>({})
  /** Coalesce concurrent hotel-options fetches (same quotation + city/date). */
  const hotelOptionsInflightRef = useRef<Map<string, Promise<QuotationHotelOptionsByDateHotel[]>>>(new Map())
  /** After a successful hotel-options API load, skip network for that quotation+city+date. */
  const hotelOptionsLoadedRef = useRef<Set<string>>(new Set())
  const hotelOptionsCacheRef = useRef<Record<string, QuotationHotelOptionsByDateHotel[]>>({})
  /** Coalesce concurrent journey-options fetches (same quotation + city/date). */
  const journeyOptionsInflightRef = useRef<Map<string, Promise<void>>>(new Map())
  /** After a successful options API load, skip network for that quotation+city+date. */
  const journeyOptionsLoadedRef = useRef<Set<string>>(new Set())
  const [packageChargeTiers, setPackageChargeTiers] = useState<PackageServiceChargeTier[]>([])
  const [mealPlanIncludedTypeOptions, setMealPlanIncludedTypeOptions] = useState<MealPlanIncludedType[]>([])
  const [mealCityPricingRows, setMealCityPricingRows] = useState<QuotationMealCityPricingGenericItem[]>([])
  const [seatingTypeByPax, setSeatingTypeByPax] = useState<SeatingTypeByPaxRow[]>(DEFAULT_SEATING_TYPE_BY_PAX)
  /** generic-data `cano_speed_boat_pricing`; empty until loaded or when the call fails (static fallback then applies). */
  const [genericCanoRates, setGenericCanoRates] = useState<CanoRateRow[]>([])

  const cityNameLookup = useCallback(
    (cityId: string): string => cityOptions.find((c) => c.value === cityId)?.label || "",
    [cityOptions],
  )

  useEffect(() => {
    if (activeHotelPicker != null) {
      const dayIndex = activeHotelPicker
      const day = journeyDays[dayIndex]
      if (!day) return
      const key = buildCityDateKey(day.city_id, day.date)
      const cached = hotelOptionsByCityDate[key] || []
      const hotels = cached.length > 0 ? cached : buildSelectedHotelFallbackOptions(day)
      const roomsNeeded = Math.max(1, Number(day.hotel?.rooms || rooms || 1))
      setSelectedHotelPickerOptions({
        dayIndex,
        day,
        hotels,
        roomsNeeded
      })
    }
    else {
      setSelectedHotelPickerOptions({})
    }
  }, [activeHotelPicker, hotelOptionsByCityDate, journeyDays, rooms])

  useEffect(() => {
    let cancelled = false
    const fetchPackageServiceTiers = async () => {
      try {
        const response = await agent.GenericGlobalAPI.get(true)
        if (cancelled) return
        const genericRoot = Array.isArray(response?.data) ? response.data[0] : null
        const tiers = Array.isArray(genericRoot?.quotation_package_service_charge_tier)
          ? genericRoot.quotation_package_service_charge_tier
          : []
        const mealIncludedTypes = Array.isArray(genericRoot?.meal_plan_type_included_type)
          ? genericRoot.meal_plan_type_included_type
          : []
        const mealCityPricing = Array.isArray(genericRoot?.quotation_meal_city_pricing)
          ? (genericRoot.quotation_meal_city_pricing as QuotationMealCityPricingGenericItem[])
          : []
        const seatingByPax = Array.isArray(genericRoot?.seating_type_by_pax)
          ? (genericRoot.seating_type_by_pax as SeatingTypeByPaxRow[])
          : DEFAULT_SEATING_TYPE_BY_PAX
        setPackageChargeTiers(tiers)
        setMealPlanIncludedTypeOptions(mealIncludedTypes)
        setMealCityPricingRows(mealCityPricing)
        setSeatingTypeByPax(normalizeSeatingTypeByPax(seatingByPax))
        setGenericCanoRates(
          Array.isArray(genericRoot?.cano_speed_boat_pricing)
            ? (genericRoot.cano_speed_boat_pricing as CanoRateRow[])
            : [],
        )
        console.log("Printing value of genericRoot.guide_group_pricing", genericRoot.guide_group_pricing);
        setGuideGroupPricing(genericRoot.guide_group_pricing ?? null)
      } catch {
        if (!cancelled) {
          setPackageChargeTiers([])
          setMealPlanIncludedTypeOptions([])
          setMealCityPricingRows([])
          setSeatingTypeByPax(DEFAULT_SEATING_TYPE_BY_PAX)
          setGenericCanoRates([])
        }
      }
    }
    fetchPackageServiceTiers()
    return () => {
      cancelled = true
    }
  }, [setGuideGroupPricing])

  // ---- Hydration on mount ----
  // NewQuotation chains GET #9 right after PUT #8, so context.journeyDays is usually already
  // populated when this component mounts. Skip the duplicate fetch in that case.
  // Cases:
  //   1) journeyDays already populated → use as-is (chained pre-load).
  //   2) quotationId missing → derive locally from selectedCities (offline fallback).
  //   3) quotationId present but journeyDays empty → fetch #9 (edit-mode direct entry).
  useEffect(() => {
    let cancelled = false

    const hydrate = async () => {
      if (journeyDays.length > 0) {
        // Check each object has trips and hotel exists. If exists then setHasHydrated to true. Else continue to fetch the data.
        const flagReturn = journeyDays.every((day) => day.trips && day.hotel)
        if (flagReturn) {
          // Pre-loaded context days skip the GET path — still backfill excursion adult/child
          // snapshots from tickets so counts render and later edits rebuild valid tickets.
          // Phase 1: map seating/DT snapshots from journey-details; seed Select fallbacks;
          // do NOT call journey-options-by-date for every day.
          // Phase 2: map hotel meal-plan display from journey-details; no hotel-options.
          const fallbackOptionsPatch: Record<string, JourneyOptionsByDateValue> = {}
          setJourneyDays((prev) =>
            prev.map((day) => {
              const trips = applyTripsSeatingSnapshotFromApi(
                (day.trips || []).map((trip) => ({
                  ...trip,
                  excursions: (trip.excursions || []).map((exc) => backfillExcursionCountsFromTickets(exc)),
                })),
              )
              const hotel = applyHotelDisplaySnapshotFromApi(day.hotel) as JourneyDayHotelItem | null | undefined
              const mappedDay = { ...day, trips, hotel: hotel ?? day.hotel }
              if (trips.length > 0) {
                fallbackOptionsPatch[buildCityDateKey(day.city_id, day.date)] =
                  buildSelectedTripFallbackOptions(mappedDay)
              }
              return mappedDay
            }),
          )
          if (Object.keys(fallbackOptionsPatch).length > 0) {
            setJourneyOptionsByCityDate((prev) => ({ ...prev, ...fallbackOptionsPatch }))
          }
          setHasHydrated(true)
          return
        }
      }
      if (!quotationId) {
        const derived = deriveJourneyDays(selectedCities, startDate, cityNameLookup)
        if (!cancelled) {
          setJourneyDays(derived)
          setHasHydrated(true)
        }
        return
      }

      try {
        const response = await agent.QuotationJourneyDetailsAPI.get(quotationId)
        if (cancelled) return
        const data = response?.status ? response.data : undefined
        const responseDays = data?.days || []

        if (responseDays.length > 0) {
          const normalized = responseDays.map((d) => ({ ...d, date: normalizeIsoDate(d.date) }))

          // Rebuild selected-trip Select fallbacks + seating/DT snapshots on reload.
          // Phase 1: do NOT call journey-options-by-date per day on enter (performance).
          // Phase 2: do NOT call hotel-options-by-date per day on enter; map hotel from journey-details.
          const cachePatch: Record<string, JourneyOptionsByDateValue> = {}
          const enrichedDays = normalized.map((day) => {
            const key = buildCityDateKey(day.city_id, day.date)
            const trips = day.trips || []
            // Backfill each loaded excursion's adult/child snapshot from its persisted tickets.
            const enrichedTrips = applyTripsSeatingSnapshotFromApi(
              trips.map((trip) => ({
                ...trip,
                excursions: (trip.excursions || []).map((exc) => backfillExcursionCountsFromTickets(exc)),
              })) as JourneyDayTripItem[],
            )

            if (trips.length > 0) {
              cachePatch[key] = buildSelectedTripFallbackOptions({ ...day, trips: enrichedTrips })
            }
            if (!day.city_id || !day.date) {
              return { ...day, trips: enrichedTrips }
            }

            const enrichedHotel = day.hotel
              ? ({
                  ...applyHotelDisplaySnapshotFromApi(day.hotel),
                  hotel_name: toTitleCase(day.hotel.hotel_name),
                  meal_plan_type_name: toTitleCase(day.hotel.meal_plan_type_name ?? null),
                  meal_plan_type_included_meal_name: toTitleCase(
                    day.hotel.meal_plan_type_included_meal_name ?? null,
                  ),
                  hotel_meal_plan_type_name: toTitleCase(
                    day.hotel.hotel_meal_plan_type_name ?? day.hotel.meal_plan_type_name ?? null,
                  ),
                } as JourneyDayHotelItem)
              : day.hotel

            return applyDinnerTransferGateOnHydrate(
              {
                ...day,
                // Trust BE meal prices (including 0 for cruise-included meals). Do not recompute.
                meals: trustHydratedMealPrices(day.meals),
                trips: enrichedTrips,
                hotel: enrichedHotel,
              },
              cachePatch,
            )
          })

          if (Object.keys(cachePatch).length > 0) {
            setJourneyOptionsByCityDate((prev) => ({ ...prev, ...cachePatch }))
          }
          setJourneyDays(enrichedDays)
        } else {
          setJourneyDays(deriveJourneyDays(selectedCities, startDate, cityNameLookup))
        }

        if (Array.isArray(data?.additional_costs)) setAdditionalCosts(data!.additional_costs!)
        if (typeof data?.package_service_charge === "number" && Number(data.package_service_charge) > 0) {
          setPackageServiceCharge(String(data.package_service_charge))
        }
        if (typeof (data as any)?.flag_with_tip === "boolean") {
          setFlagWithTip(Boolean((data as any).flag_with_tip))
        }

        setHasHydrated(true)
      } catch {
        if (!cancelled) {
          setJourneyDays(deriveJourneyDays(selectedCities, startDate, cityNameLookup))
          setHasHydrated(true)
        }
      }
    }

    hydrate()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationId])

  // ---- Lazy fetchers (cell-click triggered) ----
  const fetchHotelOptionsForDay = useCallback(
    async (day: JourneyDayItem, fetchOpts?: { silent?: boolean }): Promise<QuotationHotelOptionsByDateHotel[]> => {
      const silent = Boolean(fetchOpts?.silent)
      const key = buildCityDateKey(day.city_id, day.date)
      if (!day.city_id || !day.date) return []
      const dayDate = day.date
      const cityId = day.city_id as number
      const dedupeKey = buildHotelOptionsDedupeKey(quotationId, key)

      // Phase 2: skip network when this city+date was already loaded from hotel-options API.
      if (!shouldFetchHotelOptionsForKey(hotelOptionsLoadedRef.current, dedupeKey)) {
        return hotelOptionsCacheRef.current[key] || []
      }

      if (!silent) {
        setLoadingKeys((prev) => ({ ...prev, [`hotel_${key}`]: true }))
      }

      let inflight = hotelOptionsInflightRef.current.get(dedupeKey)
      if (!inflight) {
        inflight = (async () => {
          try {
            const response = await agent.QuotationHotelOptionsByDateAPI.get([cityId], dayDate, true)
            const cities = response?.status ? response.data?.cities || [] : []
            const cityBlock: any = cities.find((c: any) => Number(c.city_id) === Number(cityId)) ?? cities[0] ?? {}
            const hotels = Array.isArray(cityBlock?.hotels) ? (cityBlock.hotels as QuotationHotelOptionsByDateHotel[]) : []
            hotelOptionsCacheRef.current[key] = hotels
            hotelOptionsLoadedRef.current.add(dedupeKey)
            setHotelOptionsByCityDate((prev) => ({ ...prev, [key]: hotels }))
            return hotels
          } catch {
            setHotelOptionsByCityDate((prev) => {
              if (prev[key]) return prev
              return { ...prev, [key]: [] }
            })
            return []
          } finally {
            hotelOptionsInflightRef.current.delete(dedupeKey)
          }
        })()
        hotelOptionsInflightRef.current.set(dedupeKey, inflight)
      }

      try {
        return await inflight
      } finally {
        if (!silent) {
          setLoadingKeys((prev) => {
            const next = { ...prev }
            delete next[`hotel_${key}`]
            return next
          })
        }
      }
    },
    [quotationId, setHotelOptionsByCityDate],
  )

  const fetchJourneyOptionsForDay = useCallback(
    async (day: JourneyDayItem, dayIndex: number | null, fetchOpts?: { silent?: boolean }) => {
      const silent = Boolean(fetchOpts?.silent)
      const key = buildCityDateKey(day.city_id, day.date)
      if (!quotationId || !day.city_id || !day.date) return
      const dayDate = day.date

      const dedupeKey = buildJourneyOptionsDedupeKey(quotationId, key)
      // Phase 1: skip network when this city+date was already loaded from options API.
      if (!shouldFetchJourneyOptionsForKey(journeyOptionsLoadedRef.current, dedupeKey)) return

      if (!silent) {
        setLoadingKeys((prev) => ({ ...prev, [`journey_${key}`]: true }))
      }

      let inflightPromise = journeyOptionsInflightRef.current.get(dedupeKey)
      if (!inflightPromise) {
        inflightPromise = (async () => {
          try {
            const ids = dayIndex != null ? buildJourneyOptionCityIds(journeyDays, dayIndex) : [Number(day.city_id) || 0, Number(day.city_id) || 0, Number(day.city_id) || 0];
            const response = await agent.QuotationJourneyOptionsByDateAPI.get(ids, dayDate, quotationId, true)
            const cities = response?.status ? response.data?.cities || [] : []
            const value = mapJourneyOptionsApiResponse(cities)
            setJourneyOptionsByCityDate((prev) => ({
              ...prev,
              [key]: mergeJourneyOptionsWithSelected(value, prev[key]),
            }))
            setJourneyDays((prev) =>
              prev.map((d) => {
                const k = buildCityDateKey(d.city_id, d.date)
                if (k !== key) return d
                if (!(d.trips || []).length) return d
                const enriched = {
                  ...d,
                  trips: enrichTripsWithJourneyOptions(
                    applyTripsSeatingSnapshotFromApi(d.trips || []),
                    value,
                  ),
                }
                return applyDinnerTransferGateOnHydrate(enriched, { [key]: value })
              }),
            )
            journeyOptionsLoadedRef.current.add(dedupeKey)
          } catch {
            setJourneyOptionsByCityDate((prev) => {
              if (prev[key]) return prev
              return {
                ...prev,
                [key]: { tours: [], transfers: [], excursions: [] },
              }
            })
          } finally {
            journeyOptionsInflightRef.current.delete(dedupeKey)
          }
        })()
        journeyOptionsInflightRef.current.set(dedupeKey, inflightPromise)
      }

      try {
        await inflightPromise
      } finally {
        if (!silent) {
          setLoadingKeys((prev) => {
            const next = { ...prev }
            delete next[`journey_${key}`]
            return next
          })
        }
      }
    },
    [quotationId, setJourneyDays, setJourneyOptionsByCityDate, journeyDays],
  )

  useEffect(() => {
    journeyOptionsInflightRef.current = new Map()
    journeyOptionsLoadedRef.current = new Set()
    hotelOptionsInflightRef.current = new Map()
    hotelOptionsLoadedRef.current = new Set()
    hotelOptionsCacheRef.current = {}
  }, [quotationId])

  // Phase 1: no post-hydrate prefetch of journey-options-by-date for every day.
  // Options load only on Add tour/transfer, Select open, Add excursion, or pricing hover
  // when seating snapshot is missing (old quotes).

  const handleJourneyOptionsSelectOpen = useCallback(
    (dayIndex: number) => {
      const day = journeyDays[dayIndex]
      if (day?.city_id && day.date) {
        void fetchJourneyOptionsForDay(day, dayIndex, { silent: true })
      }
    },
    [journeyDays, fetchJourneyOptionsForDay],
  )

  // ---- Day mutators ----
  const updateDay = (dayIndex: number, updater: (day: JourneyDayItem) => JourneyDayItem) => {
    setJourneyDays((prev) => prev.map((d, i) => (i === dayIndex ? updater(d) : d)))
  }

  const updateTripAt = (
    dayIndex: number,
    tripIndex: number,
    updater: (trip: JourneyDayTripItem) => JourneyDayTripItem,
  ) => {
    updateDay(dayIndex, (day) => ({
      ...day,
      trips: (day.trips || []).map((t, i) => (i === tripIndex ? updater(t) : t)),
    }))
  }

  const handleAddTripClick = async (dayIndex: number, kind: "tour" | "transfer") => {
    setActiveTripPicker({ dayIndex, kind })
    const day = journeyDays[dayIndex]
    if (day) await fetchJourneyOptionsForDay(day, dayIndex, { silent: false })
  }

  const applyTripMealDefaults = (day: JourneyDayItem, tripDef: TripOptionItem | undefined): JourneyDayItem => {
    // Trust saved meal prices on hydrate — this path is only for trip add/change.
    const tripFields = snapshotTripMealIncludedFields(tripDef as any)
    const { meals, dinnerWasAdded } = applyTripIncludedMeals({
      meals: day.meals,
      trip: tripFields,
      cityDefaultPriceFor: (mealTypeId) =>
        resolveMealCityDefaultPrice(mealCityPricingRows, day.city_id, mealTypeId),
    })
    const withMeals = { ...day, meals }
    if (dinnerWasAdded) return applyDtWhenDinnerTurnsOn(withMeals, journeyOptionsByCityDate)
    return syncFlagDinnerTransferForDay(withMeals, journeyOptionsByCityDate)
  }



  const buildAllExcursionsForTrip = (def: TripOptionItem | undefined): JourneyDayTripExcursionItem[] => {
    if (!def) return []
    const adultCount = Math.max(0, Number(adults) || 0)
    const childCount = Math.max(0, Number(children) || 0)
    const totalAdultCount = adultCount + (Number(extraBed) || 0)
    return (def.excursions || []).map((excDef, excIndex) => {
      const pricing = excDef.excursion_pricing || excDef.tickets || []
      const adultPricing = pricing.find((p) => p.category_id === 1) ?? pricing[0]
      const childPricing = pricing.find((p) => p.category_id === 2) ?? pricing[1]
      const defaultAddOns = (excDef.excursion_add_ons || [])
        .filter((ao) => ao.flag_default)
        .map((ao) => {
          const aoPricing = ao.add_on_pricing || []
          const aoAdult = aoPricing.find((p) => p.category_id === 1) ?? aoPricing[0]
          const aoChild = aoPricing.find((p) => p.category_id === 2) ?? aoPricing[1]
          return {
            excursion_add_on_id: ao.excursion_add_on_id,
            tickets: buildTicketPairLocal(aoAdult?.category_id, totalAdultCount, aoChild?.category_id, childCount),
            _add_on_name: ao.add_on_name || "",
            _adult_price: Number(aoAdult?.price ?? aoAdult?.final_price ?? 0),
            _child_price: Number(aoChild?.price ?? aoChild?.final_price ?? 0),
            _adult_category_id: aoAdult?.category_id,
            _child_category_id: aoChild?.category_id,
            _adult_count: totalAdultCount,
            _child_count: childCount,
            _counts_manual: false,
          }
        })
      const boatSnap = buildBoatPricingSnapshot(excDef)
      const childPolicySnap = buildChildPolicySnapshot(excDef)
      return {
        excursion_id: excDef.excursion_id,
        excursion_name: toTitleCase(excDef.excursion_name),
        sequence_number: excIndex + 1,
        tickets: buildTicketPairLocal(adultPricing?.category_id, totalAdultCount, childPricing?.category_id, childCount),
        add_ons: defaultAddOns,
        // Keep catalog on the saved excursion so + Add-on works without options reload.
        excursion_add_ons: excDef.excursion_add_ons || [],
        _adult_price: Number(adultPricing?.price ?? adultPricing?.final_price ?? 0),
        _child_price: Number(childPricing?.price ?? childPricing?.final_price ?? 0),
        _adult_category_id: adultPricing?.category_id,
        _child_category_id: childPricing?.category_id,
        _adult_count: totalAdultCount,
        _child_count: childCount,
        ...childPolicySnap,
        ...boatSnap,
      } as JourneyDayTripExcursionItem
    })
  }

  const handleSelectTrip = (dayIndex: number, kind: "tour" | "transfer", dayTripId: string) => {
    const day = journeyDays[dayIndex]
    if (!day) return
    const key = buildCityDateKey(day.city_id, day.date)
    const opts = journeyOptionsByCityDate[key]
    const list = kind === "tour" ? opts?.tours : opts?.transfers
    const [sourceCityId, rawTripId] = String(dayTripId).split("::")
    const def = (list || []).find((t) =>
      t.day_trip_id === (rawTripId || dayTripId) &&
      (sourceCityId ? String((t as any)._source_city_id ?? t.city_id ?? "") === sourceCityId : true),
    )
    if (!def) return
    const paxRequired = Math.max(0, (Number(adults) || 0) + (Number(children) || 0) + (Number(extraBed) || 0))
    const firstSeating = pickDefaultSeatingForPax(def, paxRequired) ?? def.seating_pricing?.[0]
    // Persist the trip's SOURCE city so prune can drop it when that city is no longer a neighbour.
    const srcCityId = Number(sourceCityId) || Number((def as any)._source_city_id) || Number(def.city_id) || Number(day.city_id) || 0
    const srcCityName = String((def as any)._source_city_name ?? def.city_name ?? day.city_name ?? "")
    const mealSnap = snapshotTripMealIncludedFields(def as any)
    const newTrip: JourneyDayTripItem = {
      day_trip_id: def.day_trip_id,
      day_trip_name: toTitleCase(def.day_trip_name),
      day_trip_type_id: mealSnap.day_trip_type_id,
      seating_type_id: firstSeating?.seating_type_id ?? 0,
      seating_type_name: toTitleCase(firstSeating?.seating_type_name) ?? null,
      sequence_number: (day.trips || []).length + 1,
      excursions: kind === "tour" ? buildAllExcursionsForTrip(def) : [],
      // Extra props for client-side pricing — md schema allows [property: string]: any.
      _seating_value: String(firstSeating?.seating_type_id ?? ""),
      ...buildPersistedSeatingSnapshotFields(
        firstSeating as any,
        Boolean((def as any)?.flag_enable_dinner),
      ),
      flag_breakfast_included: mealSnap.flag_breakfast_included,
      flag_lunch_included: mealSnap.flag_lunch_included,
      flag_dinner_included: mealSnap.flag_dinner_included,
      _trip_kind: kind,
      _source_city_id: srcCityId,
      _source_city_name: srcCityName,
    } as JourneyDayTripItem
    updateDay(dayIndex, (d) => {
      const withTrip = { ...d, trips: [...(d.trips || []), newTrip] }
      return applyTripMealDefaults(withTrip, def)
    })
    setActiveTripPicker(null)
  }

  /** Swap selected trip in-place via inline dropdown.
   *  Reset seater + excursions because they belong to the previous trip definition. */
  const handleChangeTrip = (
    dayIndex: number,
    tripIndex: number,
    kind: "tour" | "transfer" | "unknown",
    dayTripId: string,
  ) => {
    if (!dayTripId || kind === "unknown") return
    const day = journeyDays[dayIndex]
    if (!day) return
    const key = buildCityDateKey(day.city_id, day.date)
    const opts = journeyOptionsByCityDate[key]
    const list = kind === "tour" ? opts?.tours : opts?.transfers
    const [sourceCityId, rawTripId] = String(dayTripId).split("::")
    const def = (list || []).find((t) =>
      t.day_trip_id === (rawTripId || dayTripId) &&
      (sourceCityId ? String((t as any)._source_city_id ?? t.city_id ?? "") === sourceCityId : true),
    )
    if (!def) return
    const paxRequired = Math.max(0, (Number(adults) || 0) + (Number(children) || 0) + (Number(extraBed) || 0))
    const firstSeating = pickDefaultSeatingForPax(def, paxRequired) ?? def.seating_pricing?.[0]
    // Persist the trip's SOURCE city so prune can drop it when that city is no longer a neighbour.
    const srcCityId = Number(sourceCityId) || Number((def as any)._source_city_id) || Number(def.city_id) || Number(day.city_id) || 0
    const srcCityName = String((def as any)._source_city_name ?? def.city_name ?? day.city_name ?? "")
    updateDay(dayIndex, (d) => {
      const oldTrip = (d.trips || [])[tripIndex]
      const mealSnap = snapshotTripMealIncludedFields(def as any)
      const nextTrips = (d.trips || []).map((trip, i) => (i === tripIndex
        ? ({
          day_trip_id: def.day_trip_id,
          day_trip_name: toTitleCase(def.day_trip_name),
          day_trip_type_id: mealSnap.day_trip_type_id,
          seating_type_id: firstSeating?.seating_type_id ?? 0,
          seating_type_name: toTitleCase(firstSeating?.seating_type_name) ?? null,
          sequence_number: tripIndex + 1,
          excursions: kind === "tour" ? buildAllExcursionsForTrip(def) : [],
          _seating_value: String(firstSeating?.seating_type_id ?? ""),
          ...buildPersistedSeatingSnapshotFields(
            firstSeating as any,
            Boolean((def as any)?.flag_enable_dinner),
          ),
          flag_breakfast_included: mealSnap.flag_breakfast_included,
          flag_lunch_included: mealSnap.flag_lunch_included,
          flag_dinner_included: mealSnap.flag_dinner_included,
          _trip_kind: kind,
          _source_city_id: srcCityId,
          _source_city_name: srcCityName,
        } as JourneyDayTripItem)
        : trip))
      const mealsAfterRemove = oldTrip
        ? syncMealsAfterTripRemoved({
          meals: d.meals,
          removedTrip: asCruiseLikeTripFields(oldTrip),
          remainingTrips: nextTrips.map(asCruiseLikeTripFields),
          cityDefaultPriceFor: (mealTypeId) =>
            resolveMealCityDefaultPrice(mealCityPricingRows, d.city_id, mealTypeId),
        })
        : (d.meals || [])
      const withTrip = { ...d, trips: nextTrips, meals: mealsAfterRemove }
      return applyTripMealDefaults(withTrip, def)
    })
  }

  const handleRemoveTrip = (dayIndex: number, tripIndex: number) => {
    updateDay(dayIndex, (d) => {
      const removed = (d.trips || [])[tripIndex]
      const remainingTrips = (d.trips || []).filter((_, i) => i !== tripIndex)
      const nextMeals = removed
        ? syncMealsAfterTripRemoved({
          meals: d.meals,
          removedTrip: asCruiseLikeTripFields(removed),
          remainingTrips: remainingTrips.map(asCruiseLikeTripFields),
          cityDefaultPriceFor: (mealTypeId) =>
            resolveMealCityDefaultPrice(mealCityPricingRows, d.city_id, mealTypeId),
        })
        : (d.meals || [])
      const dinnerRemoved =
        (d.meals || []).some((m) => m.quotation_meal_type_id === 3) &&
        !nextMeals.some((m) => m.quotation_meal_type_id === 3)
      const withMeals: JourneyDayItem = {
        ...d,
        trips: remainingTrips,
        meals: nextMeals,
        ...(dinnerRemoved ? { flag_dinner_transfer: false } : {}),
      }
      return syncFlagDinnerTransferForDay(withMeals, journeyOptionsByCityDate)
    })
  }

  const step1ExcursionPax = {
    adults: Math.max(0, Number(adults) || 0),
    extraBed: Math.max(0, Number(extraBed) || 0),
    children: Math.max(0, Number(children) || 0),
  }

  const handleSeaterChange = (dayIndex: number, tripIndex: number, seatingTypeId: string, def: TripOptionItem | undefined) => {
    const sicRow = findSicSeatingRow(def?.seating_pricing)
    const isSic = isSicSeatingSelection(seatingTypeId, sicRow)
    const seating = def?.seating_pricing?.find((s) => String(s.seating_type_id) === seatingTypeId)
    // SIC pricing source: new data exposes SIC as its own named row ("SIC") with the price in
    // `price` + `dinner_transport_price`; old data put it on a flag_enable_sic row's
    // `sic_price_per_person`. Support both (new first, legacy fallback).
    const seatingLevelSic = (def?.seating_pricing || []).find((s) => Boolean((s as any).flag_enable_sic))
    const sicPrice = Number(
      (sicRow as any)?.price ??
      (seatingLevelSic as any)?.sic_price_per_person ??
      def?.sic_price_per_person ??
      0,
    )
    const resolvedSicId = Number(sicRow?.seating_type_id ?? 0) || 0
    // SIC is per person; other seaters use their row's flag_per_person.
    const perPerson = isSic ? true : Boolean((seating as any)?.flag_per_person)
    const seatingPrice = isSic ? sicPrice : (seating?.price ?? 0)
    const snapRow = isSic ? (sicRow as any) : (seating as any)
    updateTripAt(dayIndex, tripIndex, (trip) => {
      const wasSic = isSicTrip(trip)
      let next: JourneyDayTripItem = {
        ...trip,
        seating_type_id: isSic ? resolvedSicId : (Number(seatingTypeId) || 0),
        seating_type_name: isSic ? toTitleCase(sicRow?.seating_type_name ?? "SIC") : toTitleCase(seating?.seating_type_name ?? null),
        _seating_value: isSic ? (resolvedSicId ? String(resolvedSicId) : "SIC") : seatingTypeId,
        ...buildPersistedSeatingSnapshotFields(snapRow, Boolean((def as any)?.flag_enable_dinner), {
          seatingPrice,
          perPerson,
        }),
        ...snapshotTripMealIncludedFields(def as any, trip),
        _seating_overridden: true,
      } as JourneyDayTripItem
      if (isSic && !wasSic) {
        next = applyTripExcursionPaxMode(next, "sic_zero", step1ExcursionPax, buildTicketPair)
      } else if (!isSic && wasSic) {
        next = applyTripExcursionPaxMode(next, "step1_default", step1ExcursionPax, buildTicketPair)
      }
      return next
    })
  }

  const handleAddExcursionClick = async (dayIndex: number, tripIndex: number) => {
    setActiveExcursionPicker({ dayIndex, tripIndex })
    const day = journeyDays[dayIndex]
    if (day) await fetchJourneyOptionsForDay(day, dayIndex)
  }

  /** Build adult/child ticket pair from pricing snapshot + counts.
   *  Preserve existing ticket row ids when present so PUT updates quantities in place. */
  const buildTicketPair = (
    adultCatId: number | undefined,
    adultCount: number,
    childCatId: number | undefined,
    childCount: number,
    existingAdultTix?: JourneyDayTripExcursionTicketItem | null,
    existingChildTix?: JourneyDayTripExcursionTicketItem | null,
  ) => buildTicketPairLocal(adultCatId, adultCount, childCatId, childCount, existingAdultTix, existingChildTix)

  const handleSelectExcursion = (
    dayIndex: number,
    tripIndex: number,
    excursionId: string,
    excursionDefs: ExcursionOptionItem[] | undefined,
  ) => {
    const excDef = findExcursionDefinition(excursionId, excursionDefs)
    if (!excDef) return
    const pricing = excDef.excursion_pricing || []
    const adultPricing = pricing.find((p) => p.category_id === 1) ?? pricing[0]
    const childPricing = pricing.find((p) => p.category_id === 2) ?? pricing[1]
    const parentTrip = journeyDays[dayIndex]?.trips?.[tripIndex]
    const onSic = parentTrip != null && isSicTrip(parentTrip)
    const { adultCount: totalAdultCount, childCount } = onSic
      ? { adultCount: 0, childCount: 0 }
      : defaultExcursionPaxCounts(step1ExcursionPax)

    // Auto-insert add-ons flagged as default. Snapshot pricing/category-ids on each so future
    // count edits don't need the option list back.
    const defaultAddOns = (excDef.excursion_add_ons || [])
      .filter((ao) => ao.flag_default)
      .map((ao) => {
        const aoPricing = ao.add_on_pricing || []
        const aoAdult = aoPricing.find((p) => p.category_id === 1) ?? aoPricing[0]
        const aoChild = aoPricing.find((p) => p.category_id === 2) ?? aoPricing[1]
        return {
          excursion_add_on_id: ao.excursion_add_on_id,
          tickets: buildTicketPair(aoAdult?.category_id, totalAdultCount, aoChild?.category_id, childCount),
          _add_on_name: ao.add_on_name || "",
          _adult_price: Number(aoAdult?.price ?? aoAdult?.final_price ?? 0),
          _child_price: Number(aoChild?.price ?? aoChild?.final_price ?? 0),
          _adult_category_id: aoAdult?.category_id,
          _child_category_id: aoChild?.category_id,
          _adult_count: totalAdultCount,
          _child_count: childCount,
          _counts_manual: false,
        }
      })

    const boatSnap = buildBoatPricingSnapshot(excDef)
    const childPolicySnap = buildChildPolicySnapshot(excDef)
    const newExcursion: JourneyDayTripExcursionItem = {
      excursion_id: excDef.excursion_id,
      excursion_name: excDef.excursion_name,
      tickets: buildTicketPair(adultPricing?.category_id, totalAdultCount, childPricing?.category_id, childCount),
      add_ons: defaultAddOns,
      excursion_add_ons: excDef.excursion_add_ons || [],
      _adult_price: Number(adultPricing?.price ?? adultPricing?.final_price ?? 0),
      _child_price: Number(childPricing?.price ?? childPricing?.final_price ?? 0),
      _adult_category_id: adultPricing?.category_id,
      _child_category_id: childPricing?.category_id,
      _adult_count: totalAdultCount,
      _child_count: childCount,
      ...childPolicySnap,
      ...boatSnap,
    } as JourneyDayTripExcursionItem
    updateTripAt(dayIndex, tripIndex, (trip) => ({
      ...trip,
      excursions: [...(trip.excursions || []), { ...newExcursion, sequence_number: (trip.excursions || []).length + 1 }],
    }))
    setActiveExcursionPicker(null)
  }

  const updateExcursionAt = (
    dayIndex: number,
    tripIndex: number,
    excIndex: number,
    updater: (e: JourneyDayTripExcursionItem) => JourneyDayTripExcursionItem,
  ) => {
    updateTripAt(dayIndex, tripIndex, (trip) => ({
      ...trip,
      excursions: (trip.excursions || []).map((e, i) => (i === excIndex ? updater(e) : e)),
    }))
  }

  const handleExcursionCountChange = (
    dayIndex: number,
    tripIndex: number,
    excIndex: number,
    kind: "adult" | "child",
    rawValue: string,
  ) => {
    if (rawValue !== "" && !/^\d+$/.test(rawValue)) return
    const next = rawValue === "" ? 0 : Number(rawValue)
    const parentTrip = journeyDays[dayIndex]?.trips?.[tripIndex]
    const onSic = parentTrip != null && isSicTrip(parentTrip)
    updateExcursionAt(dayIndex, tripIndex, excIndex, (e) => {
      const resolved = resolveAdultChildTickets(e.tickets)
      const adultCount = kind === "adult" ? next : Number((e as any)._adult_count ?? resolved.adultTix?.quantity ?? 0)
      const childCount = kind === "child" ? next : Number((e as any)._child_count ?? resolved.childTix?.quantity ?? 0)
      const markManual = onSic || kind === "child"
      return applyPaxToTicketedRow(e as any, adultCount, childCount, buildTicketPair, {
        ...(markManual ? { countsManual: true } : {}),
      }) as JourneyDayTripExcursionItem
    })
  }

  const handleAddAddOnClick = (dayIndex: number, tripIndex: number, excIndex: number) => {
    setActiveAddOnPicker({ dayIndex, tripIndex, excIndex })
  }

  const handleSelectAddOn = (
    dayIndex: number,
    tripIndex: number,
    excIndex: number,
    addOnId: string,
    catalog: ReturnType<typeof resolveExcursionAddOnCatalog>,
  ) => {
    if (!addOnId) return
    const aoDef = (catalog || []).find((a) => String(a.excursion_add_on_id) === String(addOnId))
    if (!aoDef) return
    const aoPricing = aoDef.add_on_pricing || []
    const aoAdult = aoPricing.find((p) => p.category_id === 1) ?? aoPricing[0]
    const aoChild = aoPricing.find((p) => p.category_id === 2) ?? aoPricing[1]
    const parentTrip = journeyDays[dayIndex]?.trips?.[tripIndex]
    const onSic = parentTrip != null && isSicTrip(parentTrip)
    const { adultCount: totalAdultCount, childCount } = onSic
      ? { adultCount: 0, childCount: 0 }
      : defaultExcursionPaxCounts(step1ExcursionPax)
    const newAddOn = {
      excursion_add_on_id: aoDef.excursion_add_on_id,
      tickets: buildTicketPair(aoAdult?.category_id, totalAdultCount, aoChild?.category_id, childCount),
      _add_on_name: aoDef.add_on_name || "",
      _adult_price: Number(aoAdult?.price ?? aoAdult?.final_price ?? 0),
      _child_price: Number(aoChild?.price ?? aoChild?.final_price ?? 0),
      _adult_category_id: aoAdult?.category_id,
      _child_category_id: aoChild?.category_id,
      _adult_count: totalAdultCount,
      _child_count: childCount,
      _counts_manual: false,
    }
    updateExcursionAt(dayIndex, tripIndex, excIndex, (e) => ({
      ...e,
      add_ons: [...((e as any).add_ons || []), newAddOn],
    } as JourneyDayTripExcursionItem))
    setActiveAddOnPicker(null)
  }

  const handleRemoveAddOn = (dayIndex: number, tripIndex: number, excIndex: number, addOnIndex: number) => {
    updateExcursionAt(dayIndex, tripIndex, excIndex, (e) => ({
      ...e,
      add_ons: ((e as any).add_ons || []).filter((_: any, i: number) => i !== addOnIndex),
    } as JourneyDayTripExcursionItem))
  }

  const handleAddOnCountChange = (
    dayIndex: number,
    tripIndex: number,
    excIndex: number,
    addOnIndex: number,
    kind: "adult" | "child",
    rawValue: string,
  ) => {
    if (rawValue !== "" && !/^\d+$/.test(rawValue)) return
    const next = rawValue === "" ? 0 : Number(rawValue)
    const parentTrip = journeyDays[dayIndex]?.trips?.[tripIndex]
    const onSic = parentTrip != null && isSicTrip(parentTrip)
    updateExcursionAt(dayIndex, tripIndex, excIndex, (e) => ({
      ...e,
      add_ons: ((e as any).add_ons || []).map((ao: any, i: number) => {
        if (i !== addOnIndex) return ao
        const resolved = resolveAdultChildTickets(ao.tickets)
        const adultCount = kind === "adult" ? next : Number(ao._adult_count ?? resolved.adultTix?.quantity ?? 0)
        const childCount = kind === "child" ? next : Number(ao._child_count ?? resolved.childTix?.quantity ?? 0)
        const markManual = onSic || kind === "child"
        return applyPaxToTicketedRow(ao, adultCount, childCount, buildTicketPair, {
          ...(markManual ? { countsManual: true } : {}),
        })
      }),
    } as JourneyDayTripExcursionItem))
  }

  const handleRemoveExcursion = (dayIndex: number, tripIndex: number, excIndex: number) => {
    updateTripAt(dayIndex, tripIndex, (trip) => ({
      ...trip,
      excursions: (trip.excursions || []).filter((_, i) => i !== excIndex),
    }))
  }

  const handleHotelOpen = async (dayIndex: number) => {

    const day = journeyDays[dayIndex]
    // Stale-while-revalidate: show cached/fallback immediately, refresh in background.
    if (day) {
      setHotelLevelLoaderIndex(dayIndex);
      await fetchHotelOptionsForDay(day, { silent: false })
      setHotelLevelLoaderIndex(null);
      setActiveHotelPicker(dayIndex)
    }
  }

  const handleSelectHotelFromModal = async (
    dayIndex: number,
    hotel: QuotationHotelOptionsByDateHotel,
    roomType: QuotationHotelOptionsByDateRoomType,
    pricingOption: QuotationHotelOptionsPricingOption | undefined,
    resolvedPrice: number,
  ) => {
    const existingHotel = journeyDays[dayIndex]?.hotel
    const defaultRooms = Math.max(1, Number(existingHotel?.rooms ?? rooms) || 1)
    const totalChildren = Math.max(0, Number(children) || 0)
    const withBedChildren = (childDetails || []).reduce((sum, child) => sum + (child.withBed ? 1 : 0), 0)
    const withoutBedChildren = (childDetails || []).reduce((sum, child) => sum + (child.withBed ? 0 : 1), 0)
    // Auto-fill only when children exist in Step 1 AND not all are marked with bed.
    // If all are with bed, keep manual/default behavior as requested.
    const autoChildWithBed =
      totalChildren > 0 && withBedChildren < totalChildren
        ? Math.max(0, withBedChildren)
        : null
    const autoExtraBed = Number(extraBed) || 0;
    const mealPlanTypeName = pricingOption?.meal_plan_type_name || null
    const mealPlanCode = resolveHotelMealPlanCode(mealPlanTypeName)
    const includedTypes = getIncludedTypesForMealPlan(
      Number(pricingOption?.meal_plan_type_id || 0) || null,
      (pricingOption?.meal_plan_type_included_types || []) as MealPlanIncludedType[],
    )
    const existingIncludedId = Number((existingHotel as any)?.meal_plan_type_included_meal_id || 0)
    const selectedIncluded =
      includedTypes.find((it) => Number(it.meal_plan_type_included_meal_id) === existingIncludedId) ??
      includedTypes[0]
    const hotelEntry: JourneyDayHotelItem = {
      hotel_id: hotel.hotel_id,
      hotel_room_type_id: roomType.hotel_room_type_id,
      hotel_room_pricing_id: pricingOption?.hotel_room_pricing_id,
      hotel_room_day_group_pricing_id: pricingOption?.hotel_room_day_group_pricing_id ?? null,
      hotel_name: toTitleCase(hotel.hotel_name),
      hotel_room_type_name: toTitleCase(roomType.hotel_room_type_name),
      meal_plan_type_id: pricingOption?.meal_plan_type_id ?? null,
      meal_plan_type_name: toTitleCase(mealPlanTypeName),
      meal_plan_type_included_meal_id: includedTypes.length > 0 ? Number(selectedIncluded?.meal_plan_type_included_meal_id ?? 0) || null : null,
      meal_plan_type_included_meal_name: includedTypes.length > 0 ? toTitleCase(selectedIncluded?.meal_plan_type_included_meal_name || null) : null,
      hotel_meal_plan_type_name: toTitleCase(mealPlanTypeName),
      hotel_meal_plan_code: mealPlanCode ?? undefined,
      hotel_meal_plan_included_types: includedTypes,
      rooms: defaultRooms,
      extra_beds: Number(existingHotel?.extra_beds ?? autoExtraBed ?? 0),
      child_with_bed: Number(existingHotel?.child_with_bed ?? autoChildWithBed ?? 0),
      child_without_bed: withoutBedChildren,
      room_price: resolvedPrice,
      extra_bed_price: pricingOption?.extra_bed_price ?? roomType.extra_bed_price ?? 0,
      child_with_bed_price:
        pricingOption?.child_with_bed_price ?? roomType.child_with_bed_price ?? 0,
      child_without_bed_price:
        pricingOption?.child_without_bed_price ?? roomType.child_without_bed_price ?? 0,
      // Per-person surcharge rates live at the room-type level (with PO fallback for safety).
      per_person_adult_surcharge:
        (pricingOption as any)?.per_person_adult_surcharge ?? (roomType as any)?.per_person_adult_surcharge ?? 0,
      per_person_child_surcharge:
        (pricingOption as any)?.per_person_child_surcharge ?? (roomType as any)?.per_person_child_surcharge ?? 0,
    }

    // Apply to the contiguous same-city block around the selected day:
    // - selected day always gets `hotelEntry` (priced from ITS OWN date's options).
    // - other same-city days (only if no hotel yet) get the same hotel/room/meal + counts, but
    //   their room price AND per-person surcharge are resolved from THEIR OWN date's options —
    //   seasonal rates change by date, so each night can differ.
    const blockCityId = journeyDays[dayIndex]?.city_id
    let blockStart = dayIndex
    let blockEnd = dayIndex
    if (blockCityId != null) {
      while (blockStart - 1 >= 0 && journeyDays[blockStart - 1]?.city_id === blockCityId) blockStart--
      while (blockEnd + 1 < journeyDays.length && journeyDays[blockEnd + 1]?.city_id === blockCityId) blockEnd++
    }
    const lastIndex = journeyDays.length - 1
    const mealPlanTypeId = pricingOption?.meal_plan_type_id ?? null

    type HotelPriceFields = Pick<
      JourneyDayHotelItem,
      | "hotel_room_pricing_id"
      | "room_price"
      | "extra_bed_price"
      | "child_with_bed_price"
      | "child_without_bed_price"
      | "per_person_adult_surcharge"
      | "per_person_child_surcharge"
    >
    const perDayPrice: Record<number, HotelPriceFields> = {}
    const targetIdxs: number[] = []
    for (let i = blockStart; i <= blockEnd; i++) {
      if (i === lastIndex || i === dayIndex) continue
      if (journeyDays[i]?.hotel) continue
      targetIdxs.push(i)
    }
    // Resolve each target day's seasonal price from its own date's options (fetch if not cached).
    await Promise.all(
      targetIdxs.map(async (i) => {
        const d = journeyDays[i]
        if (!d) return
        const key = buildCityDateKey(d.city_id, d.date)
        let hotels = hotelOptionsByCityDate[key]
        if (!hotels || hotels.length === 0) {
          hotels = await fetchHotelOptionsForDay(d, { silent: true })
        }
        const h = findMatchingHotelOption(hotels || [], {
          hotel_id: hotel.hotel_id,
          hotel_room_type_id: roomType.hotel_room_type_id,
          hotel_room_pricing_id: pricingOption?.hotel_room_pricing_id,
          hotel_room_day_group_pricing_id: pricingOption?.hotel_room_day_group_pricing_id ?? null
        })
        const rt = h?.hotel_room_types?.find(
          (x) => String(x.hotel_room_type_id) === String(roomType.hotel_room_type_id),
        )
        if (!rt) return // no match for this date → keep fallback (selected day's values)
        const po = pickPricingOption(rt, defaultRooms, mealPlanTypeId)
        perDayPrice[i] = {
          // Season-correct pricing id for this date (low-season option differs from high).
          hotel_room_pricing_id: po?.hotel_room_pricing_id ?? hotelEntry.hotel_room_pricing_id,
          room_price: Number(po?.room_price ?? (rt as any).room_price ?? resolvedPrice),
          extra_bed_price: Number(po?.extra_bed_price ?? rt.extra_bed_price ?? hotelEntry.extra_bed_price ?? 0),
          child_with_bed_price: Number(po?.child_with_bed_price ?? rt.child_with_bed_price ?? hotelEntry.child_with_bed_price ?? 0),
          child_without_bed_price: Number(po?.child_without_bed_price ?? rt.child_without_bed_price ?? hotelEntry.child_without_bed_price ?? 0),
          per_person_adult_surcharge: Number((po as any)?.per_person_adult_surcharge ?? (rt as any)?.per_person_adult_surcharge ?? hotelEntry.per_person_adult_surcharge ?? 0),
          per_person_child_surcharge: Number((po as any)?.per_person_child_surcharge ?? (rt as any)?.per_person_child_surcharge ?? hotelEntry.per_person_child_surcharge ?? 0),
        }
      }),
    )

    setJourneyDays((prev) =>
      prev.map((d, i) => {
        if (i === prev.length - 1) return d // never auto-assign the departure day
        if (i < blockStart || i > blockEnd) return d
        if (i === dayIndex) return { ...d, hotel: { ...hotelEntry } }
        if (d.hotel) return d
        const pf = perDayPrice[i]
        return { ...d, hotel: { ...hotelEntry, ...(pf || {}) } }
      }),
    )
    setActiveHotelPicker(null)
  }

  const handleRemoveHotel = (dayIndex: number) => {
    updateDay(dayIndex, (d) => ({ ...d, hotel: null }))
  }

  const handleHotelMetaChange = (
    dayIndex: number,
    key: "rooms" | "extra_beds" | "child_with_bed" | "child_without_bed",
    rawValue: string,
  ) => {
    if (rawValue !== "" && !/^\d+$/.test(rawValue)) return

    let roomPricingData: QuotationHotelOptionsPricingOption | undefined;
    if (key === "rooms") {
      const findingHotelOptionKey = buildCityDateKey(
        journeyDays[dayIndex].city_id,
        journeyDays[dayIndex].date,
      )
      const hotel = journeyDays[dayIndex].hotel
      const hotelRoomTypeId = hotel ? hotel.hotel_room_type_id : ""
      const mealPlanTypeId = hotel ? hotel.meal_plan_type_id : null
      const flagHotelOptionsExist = hotelOptionsByCityDate[findingHotelOptionKey]

      if (flagHotelOptionsExist) {
        const selectedHotel = findMatchingHotelOption(hotelOptionsByCityDate[findingHotelOptionKey], hotel)
        if (selectedHotel) {
          const roomType = selectedHotel.hotel_room_types.find((value) => value.hotel_room_type_id === hotelRoomTypeId)
          if (roomType) {
            roomPricingData = pickPricingOption(roomType, Number(rawValue), mealPlanTypeId)
          }
        }
      }
    }
    else if (key === "child_without_bed" || key === "child_with_bed") {
      const totalChildCount = childDetails.length;
      let changedKey = "";
      let oppositeKey = "";

      if (key === "child_with_bed") {
        changedKey = "child_with_bed";
        oppositeKey = "child_without_bed";
      }
      else {
        changedKey = "child_without_bed";
        oppositeKey = "child_with_bed";
      }

      if (Number(rawValue) >= totalChildCount) {
        updateDay(dayIndex, (d) => {
          if (!d.hotel) return d;
          return { ...d, hotel: { ...d.hotel, [oppositeKey]: 0, [changedKey]: totalChildCount } }
        })
        return;
      }
      if (Number(rawValue) <= 0) {
        updateDay(dayIndex, (d) => {
          if (!d.hotel) return d;
          return { ...d, hotel: { ...d.hotel, [changedKey]: 0, [oppositeKey]: totalChildCount } }
        })
        return;
      }

      const oppositeKeyCount = totalChildCount - Number(rawValue);
      updateDay(dayIndex, (d) => {
        if (!d.hotel) return d;
        return { ...d, hotel: { ...d.hotel, [changedKey]: Number(rawValue), [oppositeKey]: oppositeKeyCount } }
      })
      return;
    }


    updateDay(dayIndex, (d) => {
      if (!d.hotel) return d
      return { ...d, hotel: { ...d.hotel, [key]: rawValue === "" ? 0 : Number(rawValue), room_price: key === "rooms" && roomPricingData ? roomPricingData["room_price"] : d.hotel.room_price } }
    })
  }

  const handleHotelIncludedMealChange = (dayIndex: number, option: MealPlanIncludedType) => {
    updateDay(dayIndex, (d) => {
      if (!d.hotel) return d
      return {
        ...d,
        hotel: {
          ...d.hotel,
          meal_plan_type_included_meal_id: Number(option.meal_plan_type_included_meal_id || 0) || null,
          meal_plan_type_included_meal_name: toTitleCase(option.meal_plan_type_included_meal_name) || null,
        } as JourneyDayHotelItem,
      }
    })
  }

  const toggleMeal = (dayIndex: number, mealTypeId: number, mealTypeName: string) => {
    updateDay(dayIndex, (d) => {
      const existing = (d.meals || []).find((m) => m.quotation_meal_type_id === mealTypeId)
      if (existing) {
        const withoutMeal = { ...d, meals: (d.meals || []).filter((m) => m.quotation_meal_type_id !== mealTypeId) }
        if (mealTypeId === 3) return { ...withoutMeal, flag_dinner_transfer: false }
        return withoutMeal
      }
      const cityDefault = resolveMealCityDefaultPrice(mealCityPricingRows, d.city_id, mealTypeId)
      const unitPrice = resolveManualMealTogglePrice({
        trips: (d.trips || []).map(asCruiseLikeTripFields),
        mealTypeId,
        cityDefaultPrice: cityDefault,
      })
      const newMeal: JourneyDayMealItem = {
        quotation_meal_type_id: mealTypeId,
        quotation_meal_type_name: toTitleCase(mealTypeName),
        price: unitPrice,
      }
      const withMeal = { ...d, meals: [...(d.meals || []), newMeal] }
      if (mealTypeId === 3) return applyDtWhenDinnerTurnsOn(withMeal, journeyOptionsByCityDate)
      return withMeal
    })
  }

  const toggleDinnerTransfer = (dayIndex: number) => {
    updateDay(dayIndex, (d) => {
      if (!canSelectDinnerTransfer(d, journeyOptionsByCityDate)) return d
      const turningOff = Boolean(d.flag_dinner_transfer)
      if (turningOff && isDinnerTransferLockedOn(d)) return d
      return { ...d, flag_dinner_transfer: !d.flag_dinner_transfer }
    })
  }

  const handleMealPriceChange = (dayIndex: number, mealTypeId: number, value: string) => {
    if (value !== "" && !/^\d+$/.test(value)) return
    updateDay(dayIndex, (d) => ({
      ...d,
      meals: (d.meals || []).map((m) =>
        m.quotation_meal_type_id === mealTypeId ? { ...m, price: value === "" ? 0 : Number(value) } : m,
      ),
    }))
  }

  const handleGuideToggle = (dayIndex: number, value: string) => {
    updateDay(dayIndex, (d) => ({ ...d, flag_guide: value === "yes" }))
  }

  // ---- Additional costs ----
  // Pax for additional costs includes extra beds per spec.
  const paxIncludingBed = useMemo(
    () => Math.max(0, (Number(adults) || 0) + (Number(children) || 0) + (Number(extraBed) || 0)),
    [adults, children, extraBed],
  )

  const addAdditionalCostRow = () => {
    setAdditionalCosts((prev) => [
      ...prev,
      {
        description: "",
        cost_per_pax: 0,
        number_of_pax: paxIncludingBed,
        sequence_number: prev.length + 1,
      } as AdditionalCostItem,
    ])
  }

  const updateAdditionalCostRow = (rowIndex: number, key: keyof AdditionalCostItem, value: string | number) => {
    setAdditionalCosts((prev) =>
      prev.map((row, i) => {
        if (i !== rowIndex) return row
        // User-edited pax → mark as overridden so auto-sync stops touching this row.
        if (key === "number_of_pax") {
          return { ...row, [key]: value, _pax_overridden: true } as AdditionalCostItem
        }
        return { ...row, [key]: value }
      }),
    )
  }

  const removeAdditionalCostRow = (rowIndex: number) => {
    setAdditionalCosts((prev) => prev.filter((_, i) => i !== rowIndex))
  }

  // ---- Seed default additional cost rows (Water bottle, Travel Insurance) on first hydration ----
  // Only seeds when hydrated AND list empty (BE returns existing rows for saved quotations).
  useEffect(() => {
    if (!hasHydrated) return
    if ((additionalCosts || []).length > 0) return
    setAdditionalCosts([
      {
        description: "2 Water Bottle",
        cost_per_pax: 10,
        number_of_pax: paxIncludingBed,
        sequence_number: 1,
      } as AdditionalCostItem,
      {
        description: "Travel Insurance",
        cost_per_pax: 2,
        number_of_pax: paxIncludingBed,
        sequence_number: 2,
      } as AdditionalCostItem,
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated])

  // ---- Auto-sync number_of_pax when pax count changes (only on rows the user hasn't overridden) ----
  useEffect(() => {
    setAdditionalCosts((prev) =>
      prev.map((row) => ((row as any)._pax_overridden ? row : { ...row, number_of_pax: paxIncludingBed })),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paxIncludingBed])

  const prevPaxIncludingBedRef = useRef<number | null>(null)

  // Auto-sync default seater only when pax count changes (not when journey options reload).
  // Otherwise a saved manual seater is overwritten by pickDefaultSeatingForPax after GET #9.
  useEffect(() => {
    if (!hasHydrated) return

    if (prevPaxIncludingBedRef.current === null) {
      prevPaxIncludingBedRef.current = paxIncludingBed
      return
    }

    if (prevPaxIncludingBedRef.current === paxIncludingBed) return
    prevPaxIncludingBedRef.current = paxIncludingBed

    setJourneyDays((prev) => {
      let anyChanged = false
      const nextDays = prev.map((day) => {
        const key = buildCityDateKey(day.city_id, day.date)
        const opts = journeyOptionsByCityDate[key]
        if (!opts || !(day.trips || []).length) return day

        let dayChanged = false
        const nextTrips = (day.trips || []).map((trip) => {
          const wasSic = isSicTrip(trip)
          // Manual non-SIC seater is sticky; SIC is re-evaluated when Step-1 pax changes.
          if (!wasSic && (trip as any)._seating_overridden) return trip

          const { def } = findTripDefinition(trip, opts)
          if (!def?.seating_pricing?.length) return trip

          const seating = pickDefaultSeatingForPax(def, paxIncludingBed) ?? def.seating_pricing[0]
          if (!seating) return trip

          const sicRow = findSicSeatingRow(def?.seating_pricing)
          const newIsSic = isSicSeatingSelection(String(seating.seating_type_id ?? ""), sicRow)

          const nextSeatingId = Number(seating.seating_type_id ?? 0)
          const currSeatingId = Number(trip.seating_type_id ?? 0)
          const currValue = String((trip as any)._seating_value ?? trip.seating_type_id ?? "")
          const nextValue = String(seating.seating_type_id ?? "")
          if (!wasSic && nextSeatingId === currSeatingId && currValue === nextValue) return trip

          let updatedTrip = {
            ...trip,
            ...buildAutoSeatingFields(seating, def),
            _seating_overridden: false,
          } as JourneyDayTripItem

          if (wasSic && !newIsSic) {
            updatedTrip = applyTripExcursionPaxMode(
              updatedTrip,
              "step1_default",
              step1ExcursionPax,
              buildTicketPair,
            )
          }

          dayChanged = true
          anyChanged = true
          return updatedTrip
        })

        return dayChanged ? { ...day, trips: nextTrips } : day
      })
      return anyChanged ? nextDays : prev
    })
    // journeyOptionsByCityDate is read when pax changes; intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated, paxIncludingBed, setJourneyDays, adults, children, extraBed])

  // ---- Auto-calc package service charge from generic tier table ----
  const packageChargeTierMeta = useMemo(() => {
    const nightCount = Math.max(0, (journeyDays.length || 0) - 1)
    const pax = Math.max(0, (Number(adults) || 0) + (Number(extraBed) || 0) + (Number(children) || 0))
    const matchedTier = packageChargeTiers.find((tier) => {
      const nightsInRange =
        nightCount >= Number(tier.nights_min || 0) &&
        (tier.nights_max == null || nightCount <= Number(tier.nights_max))
      const paxInRange =
        pax >= Number(tier.pax_min || 0) &&
        (tier.pax_max == null || pax <= Number(tier.pax_max))
      return nightsInRange && paxInRange
    })
    const pricePerPax = Number(matchedTier?.price_per_pax || 0)
    return { nightCount, pax, pricePerPax, total: pax * pricePerPax }
  }, [journeyDays.length, adults, children, packageChargeTiers, extraBed])

  const autoPackageCharge = packageChargeTierMeta.total

  // Package service charge is tier-driven and view-only.
  useEffect(() => {
    setPackageServiceCharge(String(autoPackageCharge))
  }, [autoPackageCharge, setPackageServiceCharge])

  // ---- Save handler — PUT #10 ----
  // Keep latest days/costs in refs so stepper navigation save never uses a stale closure
  // (e.g. user edits excursion adults then immediately clicks Basic Details).
  const journeyDaysSaveRef = useRef(journeyDays)
  journeyDaysSaveRef.current = journeyDays
  const additionalCostsSaveRef = useRef(additionalCosts)
  additionalCostsSaveRef.current = additionalCosts

  const saveJourneyDetails = useCallback(
    async (intent?: JourneyDetailsSaveIntent): Promise<boolean> => {
      if (!quotationId) {
        showError("Save Step 1 first to obtain a quotation id.")
        return false
      }
      const sequenced = withJourneySequenceNumbers(journeyDaysSaveRef.current, additionalCostsSaveRef.current)
      const payload: QuotationJourneyDetailsPutBody = {
        quotation_id: quotationId,
        quotation_name: quotationName,
        quotation_status_id: getQuotationStatusIdFromQuotationStatusName(intent ?? "draft"),
        package_service_charge: Number(packageServiceCharge) || 0,
        days: sequenced.days,
        additional_costs: sequenced.additional_costs,
        flag_with_tip: flagWithTip
      }
      try {
        const response = await agent.QuotationJourneyDetailsAPI.put(payload)
        if (!response?.status) {
          showError("Failed to save journey details")
          return false
        }
        showSuccess("Journey details saved")
        return true
      } catch {
        showError("Failed to save journey details")
        return false
      }
    },
    [quotationId, quotationName, packageServiceCharge, flagWithTip, showError, showSuccess],
  )

  const saveHandlerRef = useRef(saveJourneyDetails)
  saveHandlerRef.current = saveJourneyDetails
  useEffect(() => {
    if (!onBindSaveHandler) return
    onBindSaveHandler((intent) => saveHandlerRef.current(intent))
  }, [onBindSaveHandler])

  // ---- Bulk header toggles (B/L/D + All-guide) ----
  const isMealOnAllDays = useCallback(
    (mealTypeId: number): boolean =>
      journeyDays.length > 0 &&
      journeyDays.every((d) => (d.meals || []).some((m) => m.quotation_meal_type_id === mealTypeId)),
    [journeyDays],
  )

  const handleBulkMealToggle = (mealTypeId: number, mealTypeName: string) => {
    const allOn = isMealOnAllDays(mealTypeId)
    setJourneyDays((prev) =>
      prev.map((d) => {
        const has = (d.meals || []).some((m) => m.quotation_meal_type_id === mealTypeId)
        if (allOn) {
          const withoutMeal = { ...d, meals: (d.meals || []).filter((m) => m.quotation_meal_type_id !== mealTypeId) }
          if (mealTypeId === 3) return { ...withoutMeal, flag_dinner_transfer: false }
          return withoutMeal
        }
        if (has) return d
        const cityDefault = resolveMealCityDefaultPrice(mealCityPricingRows, d.city_id, mealTypeId)
        const withMeal = {
          ...d,
          meals: [
            ...(d.meals || []),
            {
              quotation_meal_type_id: mealTypeId,
              quotation_meal_type_name: toTitleCase(mealTypeName),
              price: resolveManualMealTogglePrice({
                trips: (d.trips || []).map(asCruiseLikeTripFields),
                mealTypeId,
                cityDefaultPrice: cityDefault,
              }),
            },
          ],
        }
        if (mealTypeId === 3) return applyDtWhenDinnerTurnsOn(withMeal, journeyOptionsByCityDate)
        return withMeal
      }),
    )
  }

  const isDinnerTransferOnAllEligibleDays = useCallback((): boolean => {
    if (journeyDays.length === 0) return false
    const eligible = journeyDays.filter((d) => canSelectDinnerTransfer(d, journeyOptionsByCityDate))
    if (eligible.length === 0) return false
    return eligible.every((d) => Boolean(d.flag_dinner_transfer))
  }, [journeyDays, journeyOptionsByCityDate])

  const handleBulkDinnerTransferToggle = () => {
    const allOn = isDinnerTransferOnAllEligibleDays()
    setJourneyDays((prev) =>
      prev.map((d) => {
        if (!canSelectDinnerTransfer(d, journeyOptionsByCityDate)) return d
        if (allOn) {
          if (isDinnerTransferLockedOn(d)) return d
          return { ...d, flag_dinner_transfer: false }
        }
        return { ...d, flag_dinner_transfer: true }
      }),
    )
  }

  const isAllGuidesOn = useMemo(
    () => journeyDays.length > 0 && journeyDays.every((d) => Boolean(d.flag_guide)),
    [journeyDays],
  )

  const handleBulkGuideToggle = () => {
    const next = !isAllGuidesOn
    setJourneyDays((prev) => prev.map((d) => ({ ...d, flag_guide: next })))
  }

  // Ensure every day carries a stable id for sortable identity. Runs lazily after first hydrate,
  // then no-ops once every row has either a BE id or local `_row_id`.
  useEffect(() => {
    if (journeyDays.length === 0) return
    const allIdsPresent = journeyDays.every(
      (d) => Boolean((d as any)._row_id || d.quotation_journey_day_id),
    )
    if (allIdsPresent) return
    setJourneyDays((prev) => withRowIds(prev))
  }, [journeyDays, setJourneyDays])

  // ---- Drag-and-drop reorder (dnd-kit) ----
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  // `activeDragId` controls which row is currently being dragged.
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  // Live drop indicator: the blue (allowed) / disabled (blocked) line shown at the candidate gap.
  const [dropLine, setDropLine] = useState<{ beforeRowId: string; valid: boolean } | null>(null)
  // Floating overlay card shown at the cursor while dragging (compact row + summary badge).
  const [dragCard, setDragCard] = useState<{
    dayNumber: number
    cityName: string
    hotelLabel: string
    isGroup: boolean
    rangeLabel: string
    count: number
  } | null>(null)
  // Full-width row clone shown in the DragOverlay so the ghost looks like the real row.
  const [dragOverlayHTML, setDragOverlayHTML] = useState<string | null>(null)
  // Footprint (width × height) of the dragged block — drives the full-width drop band.
  const [dragDims, setDragDims] = useState<{ width: number; height: number } | null>(null)
  // Scroll container for the journey grid — anchors the absolutely-positioned drop band.
  const gridRef = useRef<HTMLDivElement>(null)
  // Drag context computed once at drag start; the pointer-move handler fills in `gap`/`valid`.
  const dragCtxRef = useRef<{
    mode: "single" | "group"
    movingIds: Set<string>
    staticIds: string[]
    staticCities: number[]
    checkoutCity: number
    checkoutRowId: string
    draggedCity: number
    staticMidYs: number[]
    gap: number
    valid: boolean
  } | null>(null)

  // ---- Multi-select ("group") state ----
  // `selectedRowIds` always holds one whole city block (Point 2). `isShiftDown` arms the
  // hover/checkbox affordance while Shift is held.
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set())
  const [isShiftDown, setIsShiftDown] = useState(false)

  const orderedRowIds = useMemo(() => journeyDays.map(rowIdOf), [journeyDays])
  const hasSelection = selectedRowIds.size > 0
  // True only while a multi-row group drag is in progress — drives the collapse + count visuals.
  const isGroupDragging =
    activeDragId != null && selectedRowIds.size > 1 && selectedRowIds.has(activeDragId)

  const clearSelection = useCallback(() => {
    setSelectedRowIds(new Set())
  }, [])

  // Track Shift globally so the dashed-hover + checkbox arm before the pointer enters a row.
  // Escape clears the current selection. `blur` guards against a stuck Shift if focus is lost.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") setIsShiftDown(true)
      if (e.key === "Escape") clearSelection()
    }
    const onKeyUp = (e: KeyboardEvent) => { if (e.key === "Shift") setIsShiftDown(false) }
    const onBlur = () => setIsShiftDown(false)
    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("keyup", onKeyUp)
    window.addEventListener("blur", onBlur)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("keyup", onKeyUp)
      window.removeEventListener("blur", onBlur)
    }
  }, [clearSelection])

  // Drop stale ids if the day list is re-derived (e.g. Step-1 changes).
  useEffect(() => {
    const live = new Set(orderedRowIds)
    setSelectedRowIds((prev) => {
      if (prev.size === 0) return prev
      const next = new Set<string>()
      prev.forEach((id) => { if (live.has(id)) next.add(id) })
      return next.size === prev.size ? prev : next
    })
  }, [orderedRowIds])

  /** Point 2 — selecting any day selects its whole contiguous same-city block (one block at a
   *  time). Clicking a day that's already selected clears the entire group. */
  const handleDaySelect = useCallback((rowId: string) => {
    if (selectedRowIds.has(rowId)) { clearSelection(); return }
    const idx = orderedRowIds.indexOf(rowId)
    if (idx < 0) return
    const blockIds = cityBlockIndices(journeyDays, idx).map((i) => orderedRowIds[i])
    setSelectedRowIds(new Set(blockIds))
  }, [selectedRowIds, orderedRowIds, journeyDays, clearSelection])

  /** Tracks the pointer during a drag to compute the candidate insertion gap, validate it
   *  (Point 1 for single days, Point 3 for blocks) and position the live drop line. Only
   *  re-renders when the gap actually changes. */
  const handleDragPointerMove = useCallback((ev: PointerEvent) => {
    const ctx = dragCtxRef.current
    const grid = gridRef.current
    if (!ctx || !grid) return
    // Pointer in grid-content coords, compared against gap midpoints cached at drag start. Using
    // cached midpoints means the in-flow band's own reflow never feeds back into the measurement,
    // so there's no jitter at row boundaries.
    const gridRect = grid.getBoundingClientRect()
    const pointerY = ev.clientY - gridRect.top + grid.scrollTop
    let gap = ctx.staticMidYs.length
    for (let k = 0; k < ctx.staticMidYs.length; k++) {
      if (pointerY < ctx.staticMidYs[k]) { gap = k; break }
    }
    const valid = ctx.mode === "group"
      ? isValidBlockDropGap(ctx.staticCities, gap)
      : isValidSingleDropGap(ctx.staticCities, ctx.checkoutCity, ctx.draggedCity, gap)
    if (ctx.gap === gap) return                                  // unchanged → skip render
    ctx.gap = gap
    ctx.valid = valid
    // The band renders in-flow BEFORE this row id (or before the checkout day for the last gap).
    const beforeRowId = gap < ctx.staticIds.length ? ctx.staticIds[gap] : ctx.checkoutRowId
    setDropLine({ beforeRowId, valid })
  }, [])

  const handleDayDragStart = useCallback((e: DragStartEvent) => {
    const id = String(e.active.id)
    setActiveDragId(id)

    const days = journeyDays
    const ids = days.map(rowIdOf)
    const activeIdx = ids.indexOf(id)
    const lastIdx = days.length - 1
    const checkoutCity = Number(days[lastIdx]?.city_id ?? 0)

    const grabbedInSelection = selectedRowIds.has(id)
    const mode: "single" | "group" = grabbedInSelection ? "group" : "single"
    const nightDays = days.slice(0, lastIdx)
    const movingIds = new Set<string>(
      grabbedInSelection
        ? nightDays.filter((d) => selectedRowIds.has(rowIdOf(d))).map(rowIdOf)
        : activeIdx === lastIdx ? [] : [id],
    )
    const staticDays = nightDays.filter((d) => !movingIds.has(rowIdOf(d)))
    dragCtxRef.current = {
      mode,
      movingIds,
      staticIds: staticDays.map(rowIdOf),
      staticCities: staticDays.map((d) => Number(d.city_id)),
      checkoutCity,
      checkoutRowId: rowIdOf(days[lastIdx]),
      draggedCity: Number(days[activeIdx]?.city_id ?? 0),
      staticMidYs: [],
      gap: -1,
      valid: false,
    }

    // Compact overlay card (real row look) + summary badge — Point 4.
    const movingArr = nightDays.filter((d) => movingIds.has(rowIdOf(d)))
    const first = movingArr[0] ?? days[activeIdx]
    const lastMoving = movingArr[movingArr.length - 1] ?? days[activeIdx]
    const count = movingArr.length || 1
    setDragCard({
      dayNumber: Number(days[activeIdx]?.day_number ?? 0),
      cityName: String(days[activeIdx]?.city_name ?? ""),
      hotelLabel: String((days[activeIdx]?.hotel as any)?.hotel_name || "Hotel"),
      isGroup: mode === "group",
      rangeLabel: count > 1
        ? `Day ${first.day_number}-${lastMoving.day_number}`
        : `Day ${days[activeIdx]?.day_number ?? ""}`,
      count,
    })

    // Capture the real row clone for the overlay ghost, a one-row band height, and the gap
    // midpoints (cached so the in-flow band's reflow can't cause measurement jitter).
    const grid = gridRef.current
    const activeNode = grid?.querySelector(`[data-row-id="${id}"]`) as HTMLElement | null
    setDragOverlayHTML(activeNode ? activeNode.outerHTML : null)
    const activeRect = activeNode?.getBoundingClientRect()
    setDragDims({ width: activeRect?.width ?? 0, height: activeRect?.height ?? 50 })
    const gridRect0 = grid?.getBoundingClientRect()
    if (dragCtxRef.current && grid && gridRect0) {
      dragCtxRef.current.staticMidYs = dragCtxRef.current.staticIds.map((sid) => {
        const el = grid.querySelector(`[data-row-id="${sid}"]`) as HTMLElement | null
        if (!el) return Number.POSITIVE_INFINITY
        const r = el.getBoundingClientRect()
        return (r.top + r.height / 2) - gridRect0.top + grid.scrollTop
      })
    }

    window.addEventListener("pointermove", handleDragPointerMove)
  }, [journeyDays, selectedRowIds, handleDragPointerMove])

  /** Apply the move only if the candidate gap is valid; otherwise abort (snap back, keep
   *  selection). The checkout day stays pinned and re-adopts the new last night's city, then
   *  renumber + prune. */
  const handleDayDragEnd = useCallback((_e: DragEndEvent) => {
    window.removeEventListener("pointermove", handleDragPointerMove)
    const ctx = dragCtxRef.current
    dragCtxRef.current = null
    setActiveDragId(null)
    setDragCard(null)
    setDropLine(null)
    setDragOverlayHTML(null)
    setDragDims(null)

    if (!ctx || ctx.gap < 0 || !ctx.valid || ctx.movingIds.size === 0) return  // invalid → no change
    const { gap, movingIds } = ctx

    setJourneyDays((prev) => {
      if (prev.length <= 1) return prev
      const lastIdx = prev.length - 1
      const extraDay = prev[lastIdx]
      const nightDays = prev.slice(0, lastIdx)
      const moving = nightDays.filter((d) => movingIds.has(rowIdOf(d)))
      const remaining = nightDays.filter((d) => !movingIds.has(rowIdOf(d)))
      if (moving.length === 0) return prev
      const insertAt = Math.max(0, Math.min(gap, remaining.length))

      console.log("Printing value of remaining and insertAt", remaining, insertAt);

      const previousDays = remaining.slice(0, insertAt);
      const nextDays = remaining.slice(insertAt);
      let updatedDraggingNodes = [...moving];
      const currentlyDragging = updatedDraggingNodes[0];

      if (
        moving.length === 1 &&
        (
          currentlyDragging.city_id === previousDays[previousDays.length - 1].city_id ||
          currentlyDragging.city_id === nextDays[0].city_id
        )
      ) {
        const flagPreviousMatched = currentlyDragging.city_id === previousDays[previousDays.length - 1].city_id;
        updatedDraggingNodes = [{
          ...updatedDraggingNodes[0],
          quotation_city_id: flagPreviousMatched ? previousDays[previousDays.length - 1].quotation_city_id : nextDays[0].quotation_city_id
        }]
      }
      else if (moving.length > 1 &&
        (
          updatedDraggingNodes[0].city_id === previousDays[previousDays.length - 1].city_id ||
          updatedDraggingNodes[updatedDraggingNodes.length - 1].city_id === nextDays[0].city_id
        )
      ) {
        const flagPreviousMatched = updatedDraggingNodes[0].city_id === previousDays[previousDays.length - 1].city_id;
        updatedDraggingNodes = updatedDraggingNodes.map((value) => {
          return {
            ...value,
            quotation_city_id: flagPreviousMatched ? previousDays[previousDays.length - 1].quotation_city_id : nextDays[0].quotation_city_id
          }
        })
      }

      const reorderedNights = [
        ...remaining.slice(0, insertAt),
        ...updatedDraggingNodes,
        ...remaining.slice(insertAt),
      ]

      // Regenerate the pinned checkout day from the new last night.
      const newLastNight = reorderedNights[reorderedNights.length - 1] ?? extraDay
      const regeneratedCheckout: JourneyDayItem = {
        ...newLastNight,
        quotation_journey_day_id: newLastNight.quotation_journey_day_id,
        city_id: newLastNight.city_id,
        city_name: newLastNight.city_name,
        hotel: null,
        trips: [],
        meals: [],
        flag_guide: false,
        flag_dinner_transfer: false,
        guide_charge: 0,
        guide_base_price: 0,
        guide_tip_amount: 0,
      }

      const combined = [...reorderedNights, regeneratedCheckout]
      const startIso = normalizeIsoDate(startDate)
      const renumbered = combined.map((d, i) => ({
        ...d,
        day_number: i + 1,
        date: startIso ? addDays(startIso, i) : d.date,
      }))
      return pruneTripsByNeighborCities(renumbered, journeyOptionsByCityDate)
    })

    setActiveTripPicker(null)
    setActiveExcursionPicker(null)
    setActiveAddOnPicker(null)
    setActiveHotelPicker(null)
    clearSelection()
  }, [startDate, setJourneyDays, handleDragPointerMove, clearSelection, journeyOptionsByCityDate])

  const handleDayDragCancel = useCallback(() => {
    window.removeEventListener("pointermove", handleDragPointerMove)
    dragCtxRef.current = null
    setActiveDragId(null)
    setDragCard(null)
    setDropLine(null)
    setDragOverlayHTML(null)
    setDragDims(null)
  }, [handleDragPointerMove])

  // ---- Pricing tooltip hover state ----
  const [hoverPricing, setHoverPricing] = useState<{
    dayIndex: number
    top: number
    left: number
    arrowLeft: number
    placement: "up" | "down"
  } | null>(null)
  const paxCount = useMemo(
    () => Math.max(0, (Number(adults) || 0) + (Number(children) || 0)) + (Number(extraBed) || 0),
    [adults, children, extraBed],
  )
  const totalChildrenCount = useMemo(() => Math.max(0, Number(children) || 0), [children])
  /** Child ages from Step 1 basic-details, used to count children that qualify toward lantern-boat
   *  pax (only ages > excursion's boat_pricing.child_max_age count). Mirrors childDetails order. */
  const childAges = useMemo(
    () => (childDetails || []).map((c) => Number(c.age) || 0),
    [childDetails],
  )

  // Sync non-manual excursion/add-on adult/child counts (+ tickets) when Step-1 pax/ages change.
  // Skip first run after hydrate so saved ticket quantities from BE remain source of truth.
  // Excursion and add-on `_counts_manual` flags are independent.
  const prevExcursionPaxSyncKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!hasHydrated) return
    const adultCount = Math.max(0, Number(adults) || 0) + (Number(extraBed) || 0)
    const childCount = Math.max(0, Number(children) || 0)
    const syncKey = `${adultCount}|${childCount}|${childAges.join(",")}`
    if (prevExcursionPaxSyncKeyRef.current === null) {
      prevExcursionPaxSyncKeyRef.current = syncKey
      return
    }
    if (prevExcursionPaxSyncKeyRef.current === syncKey) return
    prevExcursionPaxSyncKeyRef.current = syncKey

    setJourneyDays((prev) => {
      let anyChanged = false
      const nextDays = prev.map((day) => {
        let dayChanged = false
        const nextTrips = (day.trips || []).map((trip) => {
          const onSic = isSicTrip(trip)
          const { adultCount: targetAdult, childCount: targetChild } = excursionPaxTargetForTrip(onSic, {
            adults: Math.max(0, Number(adults) || 0),
            extraBed: Math.max(0, Number(extraBed) || 0),
            children: Math.max(0, Number(children) || 0),
          })

          let tripChanged = false
          const nextExcursions = (trip.excursions || []).map((exc) => {
            let excChanged = false
            let nextExc: JourneyDayTripExcursionItem = exc

            if (!(exc as any)._counts_manual) {
              const currAdult = Number((exc as any)._adult_count ?? 0)
              const currChild = Number((exc as any)._child_count ?? 0)
              if (currAdult !== targetAdult || currChild !== targetChild) {
                nextExc = applyPaxToTicketedRow(
                  exc as any,
                  targetAdult,
                  targetChild,
                  buildTicketPairLocal,
                ) as JourneyDayTripExcursionItem
                excChanged = true
              }
            }

            const prevAddOns = ((nextExc as any).add_ons || []) as any[]
            let addOnsChanged = false
            const nextAddOns = prevAddOns.map((ao: any) => {
              if (ao?._counts_manual) return ao
              const currAdult = Number(ao._adult_count ?? 0)
              const currChild = Number(ao._child_count ?? 0)
              if (currAdult === targetAdult && currChild === targetChild) return ao
              addOnsChanged = true
              return applyPaxToTicketedRow(ao, targetAdult, targetChild, buildTicketPairLocal)
            })

            if (!excChanged && !addOnsChanged) return exc
            tripChanged = true
            anyChanged = true
            dayChanged = true
            return addOnsChanged
              ? ({ ...nextExc, add_ons: nextAddOns } as JourneyDayTripExcursionItem)
              : nextExc
          })
          return tripChanged ? { ...trip, excursions: nextExcursions } : trip
        })
        return dayChanged ? { ...day, trips: nextTrips } : day
      })
      return anyChanged ? nextDays : prev
    })
  }, [hasHydrated, adults, children, extraBed, childAges, setJourneyDays])

  const getIncludedTypesForMealPlan = useCallback(
    (mealPlanTypeId?: number | null, fallback?: MealPlanIncludedType[]) => {
      if (mealPlanTypeId != null) {
        const fromGeneric = mealPlanIncludedTypeOptions.filter(
          (it) => Number(it.meal_plan_type_id || 0) === Number(mealPlanTypeId),
        )
        if (fromGeneric.length > 0) return fromGeneric
      }
      return (fallback || []) as MealPlanIncludedType[]
    },
    [mealPlanIncludedTypeOptions],
  )

  // ---- Render ----
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 12 }}>
        <div className={styles.contentCard}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className={styles.sectionTitle}>Journey Details</h2>
            <div className={styles.guidePricingRow}>
              <span className={styles.guidePricingLabel}>Guide Pricing:</span>
              <label>
                <input type="radio" checked={flagWithTip} onChange={() => setFlagWithTip(true)} />
                With Tip
              </label>
              <label>
                <input type="radio" checked={!flagWithTip} onChange={() => setFlagWithTip(false)} />
                Without Tip
              </label>
            </div>
          </div>

          {!hasHydrated && <p className={styles.mutedHint}>Loading journey details…</p>}

          {hasHydrated && journeyDays.length === 0 && (
            <p className={styles.mutedHint}>No Days Available. Save Step 1 With Cities And a Start Date First.</p>
          )}

          {hasHydrated && journeyDays.length > 0 && (
            <div ref={gridRef} className={`${styles.journeyGrid} ${hasSelection ? styles.hasSelection : ""}`}>
              <SpinnerKeyframes />
              <div className={styles.headerRow}>
                <div>
                  <span className={styles.dayHeaderWithTip}>
                    Day / City / Date
                    <span className={styles.tipWrap} tabIndex={0} aria-label="How to multi-select and move days">
                      <MdOutlineTipsAndUpdates className={styles.tipIcon} size={16} aria-hidden />
                      <span className={styles.tipBubble} role="tooltip">
                        Hold <strong>Shift</strong> and move your cursor over a day to reveal a checkbox. Select the days you want, then drag any selected day to move the entire group together.
                      </span>
                    </span>
                  </span>
                </div>
                <div>Trips Selection</div>
                <div>Seater</div>
                <div>Excursion Details</div>
                <div>Hotel</div>
                <div>
                  <div className={styles.mealHeaderInline}>
                    <span className={styles.mealHeaderLabel}>Meal</span>
                    <div style={{ display: "flex", justifyContent: "flex-start", width: "100%", gap: "8px" }}>
                      {MEAL_TYPES.map((mt) => (
                        <button
                          key={`bulk-${mt.id}`}
                          type="button"
                          className={`${styles.mealToggleButton} ${isMealOnAllDays(mt.id) ? styles.mealToggleButtonActive : ""}`}
                          onClick={() => handleBulkMealToggle(mt.id, mt.name)}
                          aria-pressed={isMealOnAllDays(mt.id)}
                        >
                          {mt.short}
                        </button>
                      ))}
                      <button
                        key="bulk-dt"
                        type="button"
                        className={`${styles.mealToggleButton} ${isDinnerTransferOnAllEligibleDays() ? styles.mealToggleButtonActive : ""}`}
                        onClick={handleBulkDinnerTransferToggle}
                        aria-pressed={isDinnerTransferOnAllEligibleDays()}
                      >
                        DT
                      </button>
                    </div>
                  </div>
                </div>
                <div>
                  <div>Guides</div>
                  <div style={{ marginTop: 4 }}>
                    <Checkbox label="All" checked={isAllGuidesOn} onChange={handleBulkGuideToggle} />
                  </div>
                </div>
                <div>Pricing</div>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDayDragStart}
                onDragEnd={handleDayDragEnd}
                onDragCancel={handleDayDragCancel}
              >
                {/* Drop band is rendered in-flow inside the rows map below (pushes rows to make room). */}
                <SortableContext items={journeyDays.map(rowIdOf)} strategy={() => null}>
                  {journeyDays.map((day, dayIndex) => {
                    const key = buildCityDateKey(day.city_id, day.date)
                    const journeyOpts = journeyOptionsByCityDate[key]
                    const last = isLastDay(day, journeyDays)
                    const trips = day.trips || []
                    const meals = day.meals || []
                    const sortableId = rowIdOf(day)
                    const rowSelected = selectedRowIds.has(sortableId)
                    // While a group drag is active, every selected row except the grabbed one
                    // collapses to a thin placeholder (Trello-style).
                    const collapsedDuringDrag = isGroupDragging && rowSelected && sortableId !== activeDragId

                    const flagNearToDropElement = false // superseded by the in-flow drop band
                    const showDropBand = dropLine?.beforeRowId === sortableId

                    return (
                      <React.Fragment key={sortableId}>
                        {showDropBand && dropLine && dragDims && (
                          <div
                            className={`${styles.dropBand} ${dropLine.valid ? styles.dropBandValid : styles.dropBandBlocked}`}
                            style={{ height: "50px" }}
                          >
                            <span className={styles.dropBandLabel}>
                              {dropLine.valid ? "Move here" : "Can not move here"}
                            </span>
                          </div>
                        )}
                        <SortableDayRow id={sortableId} collapsed={collapsedDuringDrag}>
                          {({ listeners, attributes }) => (<>

                            {/* Day info */}
                            <div style={{ marginTop: flagNearToDropElement && dropLine && dragDims ? "50px" : "0" }}>
                              <div
                                className={[
                                  styles.dayCard,
                                  isShiftDown ? styles.shiftArmed : "",
                                  rowSelected ? styles.dayCardSelected : "",
                                ].filter(Boolean).join(" ")}
                              >
                                {/* Grip only on night days — the checkout (last) day is never draggable. */}
                                {!last && (
                                  <button
                                    type="button"
                                    className={styles.dragHandle}
                                    aria-label="Drag to reorder day"
                                    title="Drag to reorder"
                                    {...attributes}
                                    {...listeners}
                                  >
                                    <span aria-hidden>≡</span>
                                  </button>
                                )}
                                {/* Group-select checkbox (Point 2): selects the whole city block. Not on checkout day. */}
                                {!last && (
                                  <button
                                    type="button"
                                    className={[
                                      styles.selectCheckbox,
                                      rowSelected ? styles.selectCheckboxChecked : "",
                                    ].filter(Boolean).join(" ")}
                                    aria-pressed={rowSelected}
                                    aria-label={`Select Day ${day.day_number}`}
                                    onClick={() => handleDaySelect(sortableId)}
                                  >
                                    {rowSelected && (
                                      <IconsColor iconName="checkIcon" width="12px" height="12px" fillColor="none" strokeColor="#ffffff" />
                                    )}
                                  </button>
                                )}
                                <div className={styles.dayLabel}>Day {day.day_number}</div>
                                <div className={styles.dayCity}>{day.city_name || ""}</div>
                                <div className={styles.dayDate}>{formatDateForDisplay(day.date || "")}</div>
                              </div>
                            </div>

                            {/* Trips + Seater + Excursions (grouped per trip) */}
                            <div
                              className={styles.tripBandsCell}
                              style={{ marginTop: flagNearToDropElement && dropLine && dragDims ? "50px" : "0" }}
                            >
                              {trips.map((trip, tripIndex) => {
                                const { kind, def } = findTripDefinition(trip, journeyOpts)
                                const tripList = kind === "tour" ? journeyOpts?.tours : kind === "transfer" ? journeyOpts?.transfers : []
                                const coveredExcursionsText =
                                  kind === "tour"
                                    ? ((trip.excursions || []).length > 0 ? (trip.excursions || []) : (def?.excursions || []))
                                      .map((exc: any) => toTitleCase(String(exc.excursion_name || "").trim()))
                                      .filter(Boolean)
                                      .join(", ")
                                    : ""
                                const selectedTripOptionValue = resolveSelectedTripOptionValue(
                                  trip,
                                  tripList,
                                  day.city_id,
                                )
                                const tripSelectOptions = ensureSelectedTripInSelectOptions(
                                  buildTripSelectOptions(journeyOpts, kind),
                                  trip,
                                  selectedTripOptionValue,
                                  coveredExcursionsText,
                                )
                                const tripExcursionDefs = filterExcursionsForTripSourceCity(
                                  journeyOpts?.excursions,
                                  trip,
                                  day,
                                  journeyOpts,
                                )
                                const excursions = trip.excursions || []
                                const selectedExcursionIds = new Set((excursions || []).map((e) => String(e.excursion_id || "")))
                                const availableExcursionDefs = tripExcursionDefs.filter(
                                  (e) => !selectedExcursionIds.has(String(e.excursion_id || "")),
                                )
                                return (
                                  <div key={`trip-band-${tripIndex}`} className={styles.tripBand}>
                                    <div className={styles.tripBandCol}>
                                      <div className={styles.tripSelectorBlock}>
                                        <div className={styles.tripSelectorRow}>
                                          <div className={styles.tripSelector}>
                                            <Select
                                              label=""
                                              value={selectedTripOptionValue}
                                              onChange={(e) => handleChangeTrip(dayIndex, tripIndex, kind, e.target.value)}
                                              options={tripSelectOptions}
                                              placeholder={kind === "tour" ? "Select tour" : kind === "transfer" ? "Select transfer" : "Select trip"}
                                              height="40px"
                                              onOpen={() => handleJourneyOptionsSelectOpen(dayIndex)}
                                            />
                                          </div>
                                          <button type="button" className={styles.iconButton} onClick={() => handleRemoveTrip(dayIndex, tripIndex)} aria-label="Remove trip">
                                            <IconsColor iconName="deleteIcon" width="20px" height="20px" fillColor="none" strokeColor="#e11d48" />
                                          </button>
                                        </div>
                                        {coveredExcursionsText && (
                                          <div className={styles.routeText}>{coveredExcursionsText}</div>
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.tripBandCol}>
                                      <Select
                                        label=""
                                        value={String((trip as any)._seating_value ?? trip.seating_type_id ?? "")}
                                        onChange={(e) => handleSeaterChange(dayIndex, tripIndex, e.target.value, def)}
                                        options={buildSeaterOptions(def, trip)}
                                        placeholder="Select seater"
                                        height="40px"
                                        onOpen={() => handleJourneyOptionsSelectOpen(dayIndex)}
                                      />
                                    </div>
                                    <div className={styles.tripBandCol}>
                                      {kind !== "tour" ? (
                                        <div className={styles.emptyStateCell} />
                                      ) : (
                                        <div className={styles.excursionPerTripList}>
                                          {excursions.map((exc, excIndex) => {
                                            const excDef = findExcursionDefinition(exc.excursion_id || "", tripExcursionDefs)
                                            const addOns = (exc.add_ons || []) as any[]
                                            const addOnCatalog = resolveExcursionAddOnCatalog(exc)
                                            const availableAddOns = getAvailableExcursionAddOns(addOnCatalog, addOns)
                                            const hasAnyAddOns = excursionSupportsAddOns(exc)
                                            const isAddOnPickerOpen =
                                              activeAddOnPicker?.dayIndex === dayIndex &&
                                              activeAddOnPicker?.tripIndex === tripIndex &&
                                              activeAddOnPicker?.excIndex === excIndex
                                            const flagDeleteExcursionDisabled = excursions.length === 1
                                            const resolvedExcTickets = resolveAdultChildTickets(exc.tickets)
                                            const adultsCount = Number(
                                              resolvedExcTickets.adultTix?.quantity ?? (exc as any)._adult_count ?? 0,
                                            )
                                            const childrenCount = Number(
                                              resolvedExcTickets.childTix?.quantity ?? (exc as any)._child_count ?? 0,
                                            )
                                            const isLanternBoat = isLanternBoatExcursion(exc)
                                            const boatCount = isLanternBoat ? computeLanternBoatCount(exc, childAges) : 0
                                            return (
                                              <div key={`excitem-${excIndex}`} className={styles.excursionCard}>
                                                <div className={styles.excursionTitleRow}>
                                                  <span>{toTitleCase(exc.excursion_name || excDef?.excursion_name)}</span>
                                                  <button
                                                    type="button"
                                                    className={styles.iconButton}
                                                    style={{
                                                      opacity: flagDeleteExcursionDisabled ? 0.5 : 1,
                                                      cursor: flagDeleteExcursionDisabled ? "not-allowed" : "pointer",
                                                    }}
                                                    onClick={() => handleRemoveExcursion(dayIndex, tripIndex, excIndex)}
                                                    aria-label="Remove excursion"
                                                    disabled={flagDeleteExcursionDisabled}
                                                  >
                                                    <IconsColor iconName="deleteIcon" width="20px" height="20px" fillColor="none" strokeColor="#e11d48" />
                                                  </button>
                                                </div>
                                                <div className={styles.excursionPaxFields}>
                                                  <div>
                                                    <span>Adults</span>
                                                    <InputText
                                                      label=""
                                                      hideLabel={true}
                                                      noMargin={true}
                                                      type="text"
                                                      height="32px"
                                                      value={String(adultsCount)}
                                                      onChange={(e) =>
                                                        handleExcursionCountChange(dayIndex, tripIndex, excIndex, "adult", e.target.value)
                                                      }
                                                    />
                                                  </div>
                                                  <div>
                                                    <span>Children</span>
                                                    <InputText
                                                      label=""
                                                      hideLabel={true}
                                                      noMargin={true}
                                                      type="text"
                                                      height="32px"
                                                      value={String(childrenCount)}
                                                      onChange={(e) =>
                                                        handleExcursionCountChange(dayIndex, tripIndex, excIndex, "child", e.target.value)
                                                      }
                                                    />
                                                  </div>
                                                </div>

                                                {isLanternBoat && (
                                                  <div className={styles.excursionPaxFields}>
                                                    <div>
                                                      <span>Boat Count</span>
                                                      <InputText
                                                        label=""
                                                        hideLabel={true}
                                                        noMargin={true}
                                                        type="text"
                                                        height="32px"
                                                        disabled={true}
                                                        value={String(boatCount)}
                                                        onChange={() => undefined}
                                                      />
                                                    </div>
                                                    <div aria-hidden="true" />
                                                  </div>
                                                )}

                                                {addOns.length > 0 && (
                                                  <div className={styles.addOnList}>
                                                    {addOns.map((ao, aoIndex) => (
                                                      <div key={`ao-${aoIndex}`} className={styles.addOnCard}>
                                                        <div className={styles.addOnTitleRow}>
                                                          <span>{toTitleCase(ao._add_on_name) || "Add-On"}</span>
                                                          <button
                                                            type="button"
                                                            className={styles.iconButton}
                                                            onClick={() => handleRemoveAddOn(dayIndex, tripIndex, excIndex, aoIndex)}
                                                            aria-label="Remove add-on"
                                                          >
                                                            <IconsColor iconName="deleteIcon" width="14px" height="14px" fillColor="none" strokeColor="#e11d48" />
                                                          </button>
                                                        </div>
                                                        <div className={styles.excursionPaxFields}>
                                                          <div>
                                                            <span>Adults</span>
                                                            <InputText
                                                              label=""
                                                              hideLabel={true}
                                                              noMargin={true}
                                                              type="text"
                                                              height="30px"
                                                              value={String(ao._adult_count ?? 0)}
                                                              onChange={(e) =>
                                                                handleAddOnCountChange(
                                                                  dayIndex,
                                                                  tripIndex,
                                                                  excIndex,
                                                                  aoIndex,
                                                                  "adult",
                                                                  e.target.value,
                                                                )
                                                              }
                                                            />
                                                          </div>
                                                          <div>
                                                            <span>Children</span>
                                                            <InputText
                                                              label=""
                                                              hideLabel={true}
                                                              noMargin={true}
                                                              type="text"
                                                              height="30px"
                                                              value={String(ao._child_count ?? 0)}
                                                              onChange={(e) =>
                                                                handleAddOnCountChange(
                                                                  dayIndex,
                                                                  tripIndex,
                                                                  excIndex,
                                                                  aoIndex,
                                                                  "child",
                                                                  e.target.value,
                                                                )
                                                              }
                                                            />
                                                          </div>
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}

                                                {hasAnyAddOns && isAddOnPickerOpen && availableAddOns.length > 0 && (
                                                  <div className={styles.addOnPickerCard}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                      <Select
                                                        label=""
                                                        value=""
                                                        onChange={(e) =>
                                                          handleSelectAddOn(
                                                            dayIndex,
                                                            tripIndex,
                                                            excIndex,
                                                            e.target.value,
                                                            addOnCatalog,
                                                          )
                                                        }
                                                        options={availableAddOns.map((a) => ({
                                                          label: a.add_on_name || "",
                                                          value: a.excursion_add_on_id || "",
                                                        }))}
                                                        placeholder="Select add-on"
                                                        height="36px"
                                                      />
                                                    </div>
                                                    <button
                                                      type="button"
                                                      className={styles.iconButton}
                                                      onClick={() => setActiveAddOnPicker(null)}
                                                      aria-label="Cancel"
                                                    >
                                                      <IconsColor iconName="deleteIcon" width="14px" height="14px" fillColor="none" strokeColor="#94a3b8" />
                                                    </button>
                                                  </div>
                                                )}

                                                {hasAnyAddOns && availableAddOns.length > 0 && !isAddOnPickerOpen && (
                                                  <button
                                                    type="button"
                                                    className={`${styles.outlineAction} ${styles.addAddOnAction}`}
                                                    onClick={() => handleAddAddOnClick(dayIndex, tripIndex, excIndex)}
                                                  >
                                                    <span style={{ flex: 1, textAlign: "left" }}>+ Add-on</span>
                                                    <span style={{ fontSize: 12 }}>▾</span>
                                                  </button>
                                                )}
                                              </div>
                                            )
                                          })}

                                          {activeExcursionPicker?.dayIndex === dayIndex && activeExcursionPicker?.tripIndex === tripIndex && (
                                            <div className={styles.excursionSelectCard}>
                                              <div className={styles.excursionSelectField}>
                                                <Select
                                                  label=""
                                                  value=""
                                                  onChange={(e) => handleSelectExcursion(dayIndex, tripIndex, e.target.value, tripExcursionDefs)}
                                                  options={buildExcursionOptions(availableExcursionDefs)}
                                                  placeholder="Select excursion"
                                                  height="40px"
                                                />
                                              </div>
                                              <button type="button" className={styles.iconButton} onClick={() => setActiveExcursionPicker(null)} aria-label="Cancel">
                                                <IconsColor iconName="deleteIcon" width="20px" height="20px" fillColor="none" strokeColor="#94a3b8" />
                                              </button>
                                            </div>
                                          )}

                                          {(() => {
                                            const journeyLoading = Boolean(loadingKeys[`journey_${key}`])
                                            return (
                                              <button
                                                type="button"
                                                className={`${styles.outlineAction} ${styles.addExcursionAction}`}
                                                onClick={() => handleAddExcursionClick(dayIndex, tripIndex)}
                                                disabled={journeyLoading}
                                              >
                                                + Add Excursion
                                                {journeyLoading && <span style={{ marginLeft: 6 }}><InlineSpinner color="#16a34a" /></span>}
                                              </button>
                                            )
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}

                              {activeTripPicker?.dayIndex === dayIndex && (
                                <div className={styles.tripBand}>
                                  <div className={styles.tripBandCol}>
                                    <div className={styles.tripSelectorRow}>
                                      <div className={styles.tripSelector}>
                                        <Select
                                          label=""
                                          value=""
                                          onChange={(e) => handleSelectTrip(dayIndex, activeTripPicker.kind, e.target.value)}
                                          options={buildTripSelectOptions(journeyOpts, activeTripPicker.kind)}
                                          placeholder={activeTripPicker.kind === "tour" ? "Select tour" : "Select transfer"}
                                          height="40px"
                                        />
                                      </div>
                                      <button type="button" className={styles.iconButton} onClick={() => setActiveTripPicker(null)} aria-label="Cancel">
                                        <IconsColor iconName="deleteIcon" width="20px" height="20px" fillColor="none" strokeColor="#94a3b8" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className={styles.tripBandCol} aria-hidden="true" />
                                  <div className={styles.tripBandCol} aria-hidden="true" />
                                </div>
                              )}

                              <div className={styles.tripBand}>
                                <div className={styles.tripBandCol}>
                                  <div className={styles.tripActions}>
                                    {(() => {
                                      const journeyLoading = Boolean(loadingKeys[`journey_${key}`])
                                      return (
                                        <>
                                          <button
                                            type="button"
                                            className={`${styles.outlineAction} ${styles.tripActionButton} ${styles.transferAction}`}
                                            onClick={() => handleAddTripClick(dayIndex, "transfer")}
                                            disabled={journeyLoading}
                                          >
                                            <span>+</span>
                                            Transfers
                                            {journeyLoading && <span style={{ marginLeft: 6 }}><InlineSpinner color="#3b82f6" /></span>}
                                          </button>
                                          <button
                                            type="button"
                                            className={`${styles.outlineAction} ${styles.tripActionButton} ${styles.tourAction}`}
                                            onClick={() => handleAddTripClick(dayIndex, "tour")}
                                            disabled={journeyLoading}
                                          >
                                            <span>+</span>
                                            Tours
                                            {journeyLoading && <span style={{ marginLeft: 6 }}><InlineSpinner color="#16a34a" /></span>}
                                          </button>
                                        </>
                                      )
                                    })()}
                                  </div>
                                </div>
                                <div className={styles.tripBandCol} />
                                <div className={styles.tripBandCol}>
                                  {trips.length === 0 && !activeTripPicker && (
                                    <div className={styles.mutedHint}>Add Day Trips First</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Hotel */}
                            <div style={{ marginTop: flagNearToDropElement && dropLine && dragDims ? "50px" : "0" }}>
                              {last ? (
                                <div className={styles.mutedHint}>No Stay Required – Departure Day</div>
                              ) : (
                                <div className={styles.hotelCell}>
                                  {!day.hotel && (() => {
                                    const hotelLoading = Boolean(loadingKeys[`hotel_${key}`])
                                    return (
                                      <button
                                        type="button"
                                        className={`${styles.outlineAction}`}
                                        onClick={() => handleHotelOpen(dayIndex)}
                                        disabled={hotelLoading}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", width: "100%" }}
                                      >
                                        Select Hotel
                                        {hotelLoading && <InlineSpinner />}
                                      </button>
                                    )
                                  })()}
                                  {day.hotel && (
                                    <div className={styles.hotelMeta}>
                                      <div className={styles.hotelNameBadge}>
                                        <button
                                          type="button"
                                          onClick={() => handleHotelOpen(dayIndex)}
                                          className={styles.hotelNameBadgeButton}
                                          style={{
                                            padding: 0,
                                            margin: 0,
                                            font: "inherit",
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 10
                                          }}
                                          title="Change hotel"
                                        >
                                          {day.hotel.hotel_name || ""}
                                          {hotelLevelLoaderIndex === dayIndex && <InlineSpinner color="#274672" />}
                                        </button>
                                      </div>
                                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span className={styles.hotelPlanBadge}>{toTitleCase(day.hotel.hotel_room_type_name) || ""}</span>
                                        <button type="button" className={styles.iconButton} onClick={() => handleRemoveHotel(dayIndex)} aria-label="Remove hotel">
                                          <IconsColor iconName="deleteIcon" width="20px" height="20px" fillColor="none" strokeColor="#e11d48" />
                                        </button>
                                      </div>
                                      <div className={styles.hotelMetaRow}>
                                        <span>Rooms:</span>
                                        <InputText label="" hideLabel={true} noMargin={true} type="text" width="72px" height="30px"
                                          value={String(day.hotel.rooms ?? 0)}
                                          onChange={(e) => handleHotelMetaChange(dayIndex, "rooms", e.target.value)}
                                        />
                                      </div>
                                      <div className={styles.hotelMetaRow}>
                                        <span>Extra Beds:</span>
                                        <InputText label="" hideLabel={true} noMargin={true} type="text" width="72px" height="30px"
                                          value={String(day.hotel.extra_beds ?? 0)}
                                          onChange={(e) => handleHotelMetaChange(dayIndex, "extra_beds", e.target.value)}
                                        />
                                      </div>
                                      <div className={styles.hotelMetaRow}>
                                        <span>Child With Bed:</span>
                                        <InputText label="" hideLabel={true} noMargin={true} type="text" width="72px" height="30px"
                                          value={String(day.hotel.child_with_bed ?? 0)}
                                          onChange={(e) => handleHotelMetaChange(dayIndex, "child_with_bed", e.target.value)}
                                        />
                                      </div>
                                      <div className={styles.hotelMetaRow}>
                                        <span>Child Without Bed:</span>
                                        <InputText label="" hideLabel={true} noMargin={true} type="text" width="72px" height="30px"
                                          value={String(childDetails.length - Number(day.hotel.child_with_bed ?? 0))}
                                          onChange={(e) => handleHotelMetaChange(dayIndex, "child_without_bed", e.target.value)}
                                        />
                                      </div>
                                      {(() => {
                                        const flagMealExists = day.hotel.meal_plan_type_id !== null && day.hotel.meal_plan_type_id !== undefined;
                                        const includedBadges = getIncludedMealBadges(day.hotel)
                                        const mealPlanCode = String((day.hotel as any).hotel_meal_plan_code || "").toUpperCase() as HotelMealPlanCode
                                        let includedTypes: MealPlanIncludedType[] = [];
                                        if (mealPlanCode === "HB") {
                                          includedTypes = mealPlanIncludedTypeOptions;
                                        }
                                        const selectedIncludedId = Number((day.hotel as any).meal_plan_type_included_meal_id || 0)
                                        if (!flagMealExists) return null
                                        return (
                                          <div className={styles.includedMealsBlock}>
                                            <div className={styles.includedMealsTitle}>Included Meals:</div>
                                            {includedTypes.length > 0 && (
                                              <div className={styles.hbMealOptions}>
                                                {includedTypes.map((opt, optIndex) => (
                                                  <label
                                                    key={String(opt.meal_plan_type_included_meal_id || opt.meal_plan_type_included_meal_name || "")}
                                                    className={styles.hbMealOption}
                                                  >
                                                    <input
                                                      type="radio"
                                                      name={`hb-meal-${dayIndex}`}
                                                      checked={
                                                        (selectedIncludedId > 0 && Number(opt.meal_plan_type_included_meal_id) === selectedIncludedId) ||
                                                        (selectedIncludedId <= 0 && optIndex === 0)
                                                      }
                                                      onChange={() => handleHotelIncludedMealChange(dayIndex, opt)}
                                                    />
                                                    <span>{opt.meal_plan_type_included_meal_name || ""}</span>
                                                  </label>
                                                ))}
                                              </div>
                                            )}
                                            {includedTypes.length === 0 && (
                                              <div className={styles.includedMealBadges}>
                                                {includedBadges.map((m) => (
                                                  <span key={m} className={styles.includedMealBadge}>{m}</span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Meal */}
                            <div style={{ marginTop: flagNearToDropElement && dropLine && dragDims ? "50px" : "0" }}>
                              <div className={styles.mealCell}>
                                <div className={styles.mealToggleRow}>
                                  {MEAL_TYPES.map((mt) => {
                                    const checked = meals.some((m) => m.quotation_meal_type_id === mt.id)
                                    return (
                                      <button
                                        key={mt.id}
                                        type="button"
                                        className={`${styles.mealToggleButton} ${checked ? styles.mealToggleButtonActive : ""}`}
                                        onClick={() => toggleMeal(dayIndex, mt.id, mt.name)}
                                        aria-pressed={checked}
                                      >
                                        {mt.short}
                                      </button>
                                    )
                                  })}
                                  <button
                                    type="button"
                                    className={`${styles.mealToggleButton} ${day.flag_dinner_transfer ? styles.mealToggleButtonActive : ""}`}
                                    onClick={() => toggleDinnerTransfer(dayIndex)}
                                    aria-pressed={Boolean(day.flag_dinner_transfer)}
                                  >
                                    DT
                                  </button>
                                </div>
                                <div className={styles.mealPriceList}>
                                  {MEAL_TYPES.map((mt) => {
                                    const meal = meals.find((m) => m.quotation_meal_type_id === mt.id)
                                    if (!meal) return null
                                    return (
                                      <div key={mt.id} className={styles.mealPriceRow}>
                                        <span>{mt.short}:</span>
                                        <InputText label="" hideLabel={true} noMargin={true} type="text" height="30px"
                                          value={String(meal.price ?? 0)}
                                          onChange={(e) => handleMealPriceChange(dayIndex, mt.id, e.target.value)}
                                        />
                                        <span>VND</span>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Guide */}
                            <div style={{ marginTop: flagNearToDropElement && dropLine && dragDims ? "50px" : "0" }}>
                              <div className={styles.guidesCell}>
                                <Select
                                  label=""
                                  value={day.flag_guide ? "yes" : "no"}
                                  onChange={(e) => handleGuideToggle(dayIndex, e.target.value)}
                                  options={GUIDE_OPTIONS}
                                  height="40px"
                                />
                              </div>
                            </div>

                            {/* Pricing — per-day total + hover breakdown */}
                            <div style={{ marginTop: flagNearToDropElement && dropLine && dragDims ? "50px" : "0" }}>
                              {(() => {
                                const breakdown = computeDayPriceBreakdown(
                                  day,
                                  paxCount,
                                  flagWithTip,
                                  totalChildrenCount,
                                  childAges,
                                  seatingTypeByPax,
                                  journeyOptionsByCityDate,
                                  guideGroupPricing,
                                  genericCanoRates,
                                  last,
                                )
                                console.log("Printing value of breakdown", breakdown, day, journeyOptionsByCityDate)
                                const isHover = hoverPricing?.dayIndex === dayIndex
                                return (
                                  <div
                                    style={{ position: "relative", display: "inline-block", marginTop: "10px" }}
                                    onMouseEnter={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect()
                                      const popupLeft = Math.max(8, rect.right - 380)
                                      const arrowLeft = Math.min(
                                        366,
                                        Math.max(14, rect.left + rect.width / 2 - popupLeft),
                                      )
                                      const popupHeight = 330
                                      const spaceBelow = window.innerHeight - rect.bottom
                                      const spaceAbove = rect.top
                                      const openUp = spaceBelow < popupHeight && spaceAbove > spaceBelow
                                      setHoverPricing({
                                        dayIndex,
                                        top: openUp ? Math.max(8, rect.top - popupHeight - (-60)) : rect.bottom + 8,
                                        left: popupLeft,
                                        arrowLeft,
                                        placement: openUp ? "up" : "down",
                                      })
                                      // Option A: old quotes missing seating snapshot — fetch that day only on hover.
                                      if (dayTripsMissingSeatingPriceSnapshot(day.trips)) {
                                        void fetchJourneyOptionsForDay(day, dayIndex, { silent: true })
                                      }
                                    }}
                                    onMouseLeave={() => setHoverPricing(null)}
                                  >
                                    <span
                                      style={{
                                        color: "#0e7490",
                                        fontWeight: 600,
                                        padding: "6px 10px",
                                        border: "1px solid #cffafe",
                                        borderRadius: 6,
                                        background: "#ecfeff",
                                        cursor: "default",
                                        whiteSpace: "nowrap",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 4,
                                      }}
                                    >
                                      {formatVnd(breakdown.totalVnd)}
                                      <MdOutlineInfo size={16} color="#0e7490" />
                                    </span>
                                    {isHover && (
                                      <div
                                        style={{
                                          position: "fixed",
                                          top: hoverPricing?.top ?? 0,
                                          left: hoverPricing?.left ?? 8,
                                          zIndex: 30,
                                          width: 380,
                                          background: "#ffffff",
                                          border: "1px solid #e2e8f0",
                                          borderRadius: 10,
                                          boxShadow: "0 6px 24px rgba(15,23,42,0.12)",
                                          padding: 12,
                                          fontSize: 12,
                                          color: "#0f172a",
                                          textAlign: "left",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: hoverPricing?.placement === "down" ? -6 : "auto",
                                            bottom: hoverPricing?.placement === "up" ? -6 : "auto",
                                            left: hoverPricing?.arrowLeft ?? 24,
                                            width: 12,
                                            height: 12,
                                            background: "#ffffff",
                                            borderLeft: hoverPricing?.placement === "down" ? "1px solid #e2e8f0" : "none",
                                            borderTop: hoverPricing?.placement === "down" ? "1px solid #e2e8f0" : "none",
                                            borderRight: hoverPricing?.placement === "up" ? "1px solid #e2e8f0" : "none",
                                            borderBottom: hoverPricing?.placement === "up" ? "1px solid #e2e8f0" : "none",
                                            transform: "rotate(45deg)",
                                          }}
                                        />
                                        <div style={{ marginBottom: 6 }}>
                                          <div style={{ fontWeight: 600, marginBottom: 2 }}>Transportation</div>
                                          {(() => {
                                            const dinnerTransferSelected = Boolean(day.flag_dinner_transfer)
                                            const lastTrip = lastTripOfDay(day)
                                            return (day.trips || []).map((t, i) => {
                                              const tripDef = findTripDefForDay(day, t, journeyOptionsByCityDate)
                                              const perPerson = isTripPerPerson(t)
                                              const unitPrice = Number((t as any)._seating_price || 0)
                                              const normal = getTripNormalTransport(t, paxCount)
                                              const dtSelected = t === lastTrip ? dinnerTransferSelected : false
                                              const dinnerInfo = resolveTripDinnerTransport(
                                                t,
                                                paxCount,
                                                dtSelected,
                                                seatingTypeByPax,
                                                tripDef,
                                              )
                                              const showDinner =
                                                t === lastTrip &&
                                                lastTripSupportsDinnerTransfer(t) &&
                                                tripDinnerApplies(t, dinnerTransferSelected)
                                              return (
                                                <React.Fragment key={`tr-${i}`}>
                                                  <div style={{ marginLeft: 10 }}>
                                                    {toTitleCase(t.day_trip_name) || "—"} ({toTitleCase(t.seating_type_name) || "—"}){" "}
                                                    {perPerson
                                                      ? `= ${formatVnd(unitPrice)} × ${paxCount} pax = ${formatVnd(normal)}`
                                                      : `= ${formatVnd(normal)}`}
                                                  </div>
                                                  {showDinner && (
                                                    <div style={{ marginLeft: 10 }}>
                                                      {toTitleCase(t.day_trip_name) || "—"} Dinner ({toTitleCase(dinnerInfo.seatingTypeName) || "—"}){" "}
                                                      {dinnerInfo.perPerson
                                                        ? `= ${formatVnd(dinnerInfo.unitPrice)} × ${paxCount} pax = ${formatVnd(dinnerInfo.totalPrice)}`
                                                        : `= ${formatVnd(dinnerInfo.totalPrice)}`}
                                                    </div>
                                                  )}
                                                </React.Fragment>
                                              )
                                            })
                                          })()}
                                        </div>
                                        <div style={{ marginBottom: 6 }}>
                                          <div style={{ fontWeight: 600, marginBottom: 2 }}>Excursions</div>
                                          {(day.trips || []).flatMap((t, ti) =>
                                            (t.excursions || []).map((e, ei) => {
                                              // Lantern boat lines show "N Boats × VND price = VND total" instead of
                                              // the adult/child breakdown (price model is per boat, not per pax).
                                              const lantern = isLanternBoatExcursion(e)
                                              const speedBoat = isSpeedBoatExcursion(e)
                                              const boatCount = lantern ? computeLanternBoatCount(e, childAges) : 0
                                              const boatPrice = lantern ? Number((e as any)._boat_price || 0) : 0
                                              const ea = Number((e as any)._adult_count || 0)
                                              const ec = Number((e as any)._child_count || 0)
                                              const policyPricing =
                                                !lantern && !speedBoat
                                                  ? computeExcursionChildPolicyPricing(e, childAges)
                                                  : null
                                              const canoPax = ea + ec
                                              const excSub = lantern
                                                ? boatCount * boatPrice
                                                : speedBoat
                                                  ? computeSpeedBoatTotal(e, genericCanoRates)
                                                  : (policyPricing?.total ?? 0)
                                              const addOns = ((e as any).add_ons || []) as any[]
                                              return (
                                                <div key={`ex-${ti}-${ei}`} style={{ marginLeft: 10 }}>
                                                  <div>
                                                    {toTitleCase(e.excursion_name)} ={" "}
                                                    {lantern
                                                      ? `${boatCount} Boats × ${formatVnd(boatPrice)} = ${formatVnd(excSub)}`
                                                      : speedBoat
                                                        ? `${canoPax} pax = ${formatVnd(excSub)}`
                                                        : formatExcursionChildPolicyFormula(policyPricing!)}
                                                  </div>
                                                  {addOns.map((ao, ai) => {
                                                    const aoPricing = computeAddOnChildPolicyPricing(ao, e, childAges)
                                                    return (
                                                      <div key={`ex-ao-${ti}-${ei}-${ai}`} style={{ marginLeft: 12, color: "#475569" }}>
                                                        ↳ {ao._add_on_name || "Add-on"} = {formatExcursionChildPolicyFormula(aoPricing)}
                                                      </div>
                                                    )
                                                  })}
                                                </div>
                                              )
                                            }),
                                          )}
                                          {(day.trips || []).every((t) => (t.excursions || []).length === 0) && (
                                            <div style={{ marginLeft: 10, color: "#64748b" }}>—</div>
                                          )}
                                        </div>
                                        <div style={{ marginBottom: 6 }}>
                                          <div style={{ fontWeight: 600, marginBottom: 2 }}>Meals</div>
                                          {(day.meals || []).length === 0 ? (
                                            <div style={{ marginLeft: 10, color: "#64748b" }}>—</div>
                                          ) : (
                                            (day.meals || []).map((m) => (
                                              <div key={`mp-${m.quotation_meal_type_id}`} style={{ marginLeft: 10 }}>
                                                {m.quotation_meal_type_name} = {formatVnd(Number(m.price || 0))} × {paxCount} pax = {formatVnd(Number(m.price || 0) * paxCount)}
                                              </div>
                                            ))
                                          )}
                                        </div>
                                        <div style={{ marginBottom: 6 }}>
                                          <div style={{ fontWeight: 600, marginBottom: 2 }}>Guide</div>
                                          <div style={{ marginLeft: 10 }}>
                                            {breakdown.guide > 0
                                              ? (() => {
                                                let guideBaseRate = 0;
                                                let flagGuideGroupPricing = false;
                                                if (appliesGuideGroupPricing(guideGroupPricing, paxCount, flagWithTip)) {
                                                  flagGuideGroupPricing = true;
                                                  guideBaseRate = Number(guideGroupPricing.amount)
                                                }
                                                else
                                                  guideBaseRate = Number(day.guide_base_price ?? day.guide_charge ?? 0)
                                                const guideTipRate = Number(day.guide_tip_amount ?? 0)
                                                const guideRate = (flagWithTip && flagGuideGroupPricing === false) ? (guideBaseRate + guideTipRate) : guideBaseRate
                                                return (flagWithTip && flagGuideGroupPricing === false)
                                                  ? `Guide = ${formatVnd(guideBaseRate)} + (${formatVnd(guideTipRate)} × ${paxCount} pax) = ${formatVnd(breakdown.guide)}`
                                                  : `Guide = ${formatVnd(guideRate)}`
                                              })()
                                              : day.flag_guide && Number(day.guide_base_price ?? day.guide_charge ?? 0) <= 0
                                                ? "— (no guide_base_price)"
                                                : "—"}
                                          </div>
                                        </div>
                                        <div style={{ marginBottom: 6 }}>
                                          <div style={{ fontWeight: 600, marginBottom: 2 }}>Hotel</div>
                                          {day.hotel && !last ? (
                                            <div style={{ marginLeft: 10 }}>
                                              <div>{toTitleCase(day.hotel.hotel_name)} ({toTitleCase(day.hotel.hotel_room_type_name)})</div>
                                              <div>
                                                Room: {formatVnd(Number(day.hotel.room_price || 0))} × {day.hotel.rooms} × {HOTEL_TAX_RATE} (tax) = {formatVnd(breakdown.hotelRoomVnd)}
                                              </div>
                                              {Number(day.hotel.extra_beds || 0) > 0 && (
                                                <div style={{ marginTop: "5px" }}>
                                                  Extra Bed: {formatVnd(Number(day.hotel.extra_bed_price || 0))} × {day.hotel.extra_beds} × {HOTEL_TAX_RATE} (tax) = {formatVnd(breakdown.hotelExtraBedVnd)}
                                                </div>
                                              )}
                                              {Number(day.hotel.child_with_bed || 0) > 0 && (
                                                <div style={{ marginTop: "5px" }}>
                                                  Child with Bed: {formatVnd(Number(day.hotel.child_with_bed_price || 0))} × {day.hotel.child_with_bed} × {HOTEL_TAX_RATE} (tax) = {formatVnd(breakdown.hotelChildWithBedVnd)}
                                                </div>
                                              )}
                                              {Math.max(0, totalChildrenCount - Number(day.hotel.child_with_bed || 0)) > 0 && (
                                                <div style={{ marginTop: "5px" }}>
                                                  Child without Bed: {formatVnd(Number(day.hotel.child_without_bed_price || 0))} × {Math.max(0, totalChildrenCount - Number(day.hotel.child_with_bed || 0))} × {HOTEL_TAX_RATE} (tax) = {formatVnd(breakdown.hotelChildWithoutBedVnd)}
                                                </div>
                                              )}
                                              {/* Per Person Surcharge: adults = rooms × 2 + extra beds, children from
                                              basic details. Adult + child segments shown only when both apply. */}
                                              {(() => {
                                                const adultRate = Number(day.hotel.per_person_adult_surcharge || 0)
                                                const childRate = Number(day.hotel.per_person_child_surcharge || 0)
                                                if (adultRate <= 0 && childRate <= 0) return null
                                                const adults = breakdown.hotelSurchargeAdultCount
                                                const children = breakdown.hotelSurchargeChildCount
                                                const hasChildSurcharge = children > 0 && childRate > 0
                                                return (
                                                  <div style={{ marginTop: "5px" }}>
                                                    Per Person Surcharge:{" "}
                                                    {hasChildSurcharge ? (
                                                      <>
                                                        ({adults} Adults × {formatVnd(adultRate)} = {formatVnd(breakdown.hotelAdultSurchargeVnd)})
                                                        {" + "}
                                                        ({children} Children × {formatVnd(childRate)} = {formatVnd(breakdown.hotelChildSurchargeVnd)})
                                                        {" = "}{formatVnd(breakdown.hotelPerPersonSurchargeVnd)}
                                                      </>
                                                    ) : (
                                                      <>
                                                        {adults} Adults × {formatVnd(adultRate)} = {formatVnd(breakdown.hotelAdultSurchargeVnd)}
                                                      </>
                                                    )}
                                                  </div>
                                                )
                                              })()}
                                              <div style={{ marginTop: 3, fontWeight: 600 }}>
                                                Hotel Total: {formatVnd(breakdown.hotelVnd)}
                                              </div>
                                            </div>
                                          ) : (
                                            <div style={{ marginLeft: 10, color: "#64748b" }}>—</div>
                                          )}
                                        </div>
                                        <hr style={{ border: 0, borderTop: "1px solid #e2e8f0", margin: "8px 0" }} />
                                        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
                                          <span>Total:</span>
                                          <span>{formatVnd(breakdown.totalVnd)}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )
                              })()}
                            </div>
                          </>)}
                        </SortableDayRow>
                      </React.Fragment>
                    )
                  })}
                </SortableContext>
                {/* Floating clone of the dragged row. Source row is collapsed (height:0) so
                    neighbours pack tightly with no gap, while this overlay shows the full-height
                    preview at the cursor. dangerouslySetInnerHTML is safe here because the HTML
                    comes from our own React-rendered DOM via outerHTML — no untrusted input. */}
                <DragOverlay dropAnimation={null}>
                  {dragCard && dragOverlayHTML ? (
                    <div className={styles.dragGhost}>
                      <div className={styles.dragGhostBadge}>
                        {dragCard.rangeLabel} · {dragCard.cityName} · Moving {dragCard.count} day{dragCard.count > 1 ? "s" : ""}
                      </div>
                      <div
                        className={styles.dragGhostRow}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: dragOverlayHTML }}
                      />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          )}
        </div>
      </Grid>

      {/* Other Additional Costs */}
      <Grid size={{ xs: 12, md: 12 }}>
        <div className={styles.contentCard}>
          <h2 className={styles.sectionTitle}>Other Additional Costs</h2>
          <div className={styles.addCostHeaderRow}>
            <div>Description</div>
            <div>Cost Per Pax (VND)</div>
            <div>No. Of Pax</div>
            <div className={styles.totalVNDOtherCostOuter}>
              Total (VND) <MdOutlineInfo className={styles.totalVNDOtherCostIcon} size={16} color="#1f3f9a" />
              <span className={styles.totalVNDOtherCostTooltip} role="tooltip">
                Total no. of pax * Cost per pax * Total no. of days
              </span>
            </div>
            <div></div>
          </div>
          {additionalCosts.map((row, i) => {
            const total = (Number(row.cost_per_pax) || 0) * (Number(row.number_of_pax) || 0) * ((Number(noOfNights) + 1) || 0)
            return (
              <div key={`addcost-${i}`} className={styles.addCostRow}>
                <InputText label="" hideLabel={true} noMargin={true} value={toTitleCase(row.description || "")}
                  onChange={(e) => updateAdditionalCostRow(i, "description", e.target.value)} />
                <InputText label="" hideLabel={true} noMargin={true} type="number" value={String(row.cost_per_pax ?? 0)}
                  onChange={(e) => updateAdditionalCostRow(i, "cost_per_pax", Number(e.target.value) || 0)} />
                <InputText label="" hideLabel={true} noMargin={true} type="number" value={String(row.number_of_pax ?? 0)}
                  onChange={(e) => updateAdditionalCostRow(i, "number_of_pax", Number(e.target.value) || 0)} />
                <InputText label="" disabled={true} hideLabel={true} noMargin={true} type="number" value={String(total)} onChange={() => undefined} />
                <button type="button" className={styles.iconButton} onClick={() => removeAdditionalCostRow(i)} aria-label="Remove row">
                  <IconsColor iconName="deleteIcon" width="20px" height="20px" fillColor="none" strokeColor="#e11d48" />
                </button>
              </div>
            )
          })}
          <div className={styles.addCostAddRow}>
            <Button type="outlined" label="+ Add Row" onClick={addAdditionalCostRow} />
          </div>
        </div>
      </Grid>

      {/* Package service charge */}
      <Grid size={{ xs: 12, md: 12 }}>
        <div className={styles.contentCard}>
          <h2 className={styles.sectionTitle}>Total Package Service Charge (VND)</h2>
          <InputText
            label=""
            hideLabel={true}
            type="number"
            value={packageServiceCharge}
            onChange={() => undefined}
            disabled={true}
            sx={{ width: "auto" }}
          />
          <p className={styles.mutedHint}>
            Auto-Calculated: {autoPackageCharge} (Based On {packageChargeTierMeta.nightCount} Nights, {packageChargeTierMeta.pax} Pax And {packageChargeTierMeta.pricePerPax} Per Pax)
          </p>
        </div>
      </Grid>

      {/* {(() => {
        const dayIndex = activeHotelPicker
        if (dayIndex == null) return null
        const day = journeyDays[dayIndex]
        if (!day) return null
        const key = buildCityDateKey(day.city_id, day.date)
        const hotels = hotelOptionsByCityDate[key] || []
        const roomsNeeded = Math.max(1, Number(day.hotel?.rooms || rooms || 1))
        return (
          <HotelRoomSelectorV2
            open={true}
            onClose={() => setActiveHotelPicker(null)}
            cityName={day.city_name || ""}
            hotels={hotels}
            roomsNeeded={roomsNeeded}
            selectedHotelId={String(day.hotel?.hotel_id || "")}
            selectedRoomTypeId={String(day.hotel?.hotel_room_type_id || "")}
            selectedMealPlanTypeId={Number((day.hotel as any)?.meal_plan_type_id || 0) || null}
            selectedMealPlanTypeName={String((day.hotel as any)?.hotel_meal_plan_type_name || "")}
            onSelect={(hotel, roomType, pricingOption, resolvedPrice) =>
              handleSelectHotelFromModal(dayIndex, hotel, roomType, pricingOption, resolvedPrice)
            }
          />
        )
      })()} */}
      {selectedHotelPickerOptions.dayIndex != null && (
        (() => {
          const resolved = resolveSelectedHotelSelection(
            (selectedHotelPickerOptions.hotels || []) as QuotationHotelOptionsByDateHotel[],
            selectedHotelPickerOptions.day.hotel as any,
          )
          return (
            <HotelRoomSelectorV2
              open={true}
              onClose={() => setActiveHotelPicker(null)}
              cityName={selectedHotelPickerOptions.day.city_name || ""}
              hotels={selectedHotelPickerOptions.hotels}
              roomsNeeded={selectedHotelPickerOptions.roomsNeeded}
              selectedHotelId={resolved.selectedHotelId}
              selectedRoomTypeId={resolved.selectedRoomTypeId}
              selectedMealPlanTypeId={resolved.selectedMealPlanTypeId}
              selectedMealPlanTypeName={resolved.selectedMealPlanTypeName}
              onSelect={(hotel, roomType, pricingOption, resolvedPrice) =>
                handleSelectHotelFromModal(selectedHotelPickerOptions.dayIndex, hotel, roomType, pricingOption, resolvedPrice)
              }
            />
          )
        })()
      )}
    </Grid>
  )
}

export default JourneyDetails
